import { assembleLpSmbHomeContent, isLpSmbHomeContentUsable, LP_SMB_HOME_BLOCK_KEYS } from "@shared/lp-express-smb-home";
import React, { useEffect, useRef, useState } from "react";
import { LpStatusDotsContext, useLpStatusDots } from "./lp-status-dots";
import { LpEditorProvider, LpElementStateProvider, LpElementStyleProvider, LpBrandFontsProvider, LpThemeProvider, LpComponentOrderProvider, LpMediaOverrideProvider, LpReviewOverrideProvider, LpPageSectionsProvider, LpTemplateVersionProvider, LpReorderGroup, LpStylePopover, EditableText, EditableElement, EditableItem, RegenerateButton, LpSectionRegenerate, LpImageActions, LpReviewActions, useLpMediaOverride, useLpMediaOverridesMap, useLpReviewOverride, lpSectionStyle, useLpElementStyle, useLpEditor, useLpElementState, useLpV2 } from "./lp-editor";
import type { LpEditorControls, LpLinkTarget } from "./lp-editor";
import { LP_THEME_FONTS, collectLpElementFontLoads, resolveLpElementFontStack, type LpTheme, type LpElementStyle } from "@shared/lp-express-sections";
import {
  selectBrandFontRoles,
  brandFontCssStack,
  brandFontFamilyName,
  collectBrandFontLoads,
  brandFontProxyUrl,
  brandFontFaceRule,
} from "@shared/brand-fonts";
import { autoCtaTextColor } from "@shared/brand-cta-style";
import { googleFontsCssUrl } from "@shared/google-fonts";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Truck, Leaf, Flower2, ShieldCheck, Sparkles, Star, ChevronLeft, ChevronRight,
  Check, CheckCircle2, Award, AlertTriangle, ArrowRight, ArrowDown, ChevronDown, ChevronUp, X as XIcon, Quote,
  Loader2, Scissors, RotateCw, ShoppingBag, Eye, EyeOff, Trash2,
} from "lucide-react";
import type { Brand, Product, Persona, NarrativeAngle, SalesDiscount, ProductReview, BrandFont } from "@shared/schema";
import { computeReceipt, computeSalePrice, computeReviewDisplay, parsePrice, formatMoney, type LpReviewDisplay } from "@/lib/lp-express-pricing";
import { BeliefSection } from "@/components/BeliefSection";
import { FarmstandSection } from "@/components/FarmstandSection";
import { RemainingPageSections } from "@/components/RemainingPageSections";
import { getLpSections, LP_SECTION_REGISTRY, lpSectionAnchorId, LP_SECTION_SCROLL_MARGIN, type LpSectionType } from "@shared/lp-express-sections";
import LpExpressQuiz from "@/components/lp-express-quiz";
import { isLpQuizContentUsable } from "@shared/lp-express-quiz";
import LpExpressListicle, { type ListicleProductCard } from "@/components/lp-express-listicle";
import { isLpListicleContentUsable } from "@shared/lp-express-listicle";
import LpExpressAdvertorial from "@/components/lp-express-advertorial";
import LpExpressColdDrip from "@/components/lp-express-cold-drip";
import { isLpColdDripContentUsable, normalizeLpColdDripContent } from "@shared/lp-express-cold-drip";
import LpExpressCoffeeShop from "@/components/lp-express-coffee-shop";
import { isLpCoffeeShopContentUsable, normalizeLpCoffeeShopContent } from "@shared/lp-express-coffee-shop";
import LpExpressTemeculaPassport from "@/components/lp-express-temecula-passport";
import { isLpTemeculaPassportContentUsable, normalizeLpTemeculaPassportContent } from "@shared/lp-express-temecula-passport";
import LpExpressPapabearLabel from "@/components/lp-express-papabear-label";
import {
  isLpPapabearLabelContentUsable,
  normalizeLpPapabearLabelContent,
  resolvePapabearLabelProductImages,
} from "@shared/lp-express-papabear-label";
import { isLpAdvertorialContentUsable } from "@shared/lp-express-advertorial";
import LpExpressProductHero from "@/components/lp-express-product-hero";
import {
  hasUsableLpProductHeroSections,
  type LpProductHeroProductDetails,
} from "@shared/lp-express-product-hero";
import LpExpressProblemSolution from "@/components/lp-express-problem-solution";
import { hasUsableLpProblemSolutionSections } from "@shared/lp-express-problem-solution";
import LpExpressOfferStack from "@/components/lp-express-offer-stack";
import { hasUsableLpOfferStackSections } from "@shared/lp-express-offer-stack";
import LpExpressStudioTemplate from "@/components/lp-express-studio-template";
import {
  isLpStudioDesignSnapshot,
  type LpStudioDesignSnapshot,
} from "@shared/lp-express-studio";

export type AdvisoryContent = {
  advisory_label?: string;
  advisory_headline?: string;
  advisory_subheadline?: string;
  advisory_headline_muted?: string;
  advisory_headline_bold?: string;
  advisory_headline_accent?: string;
};

export type StationsContent = {
  section_label?: string;
  section_headline?: string;
  stations?: Array<{ num?: number | string; label?: string; sub?: string }>;
  loop_quote?: string;
};

type GeneratedContent = {
  // Persisted, ordered, typed section list. When present, getLpSections honors
  // this order + membership (re-deriving each section's fields from the live
  // content); when absent it falls back to the canonical derived order.
  sections?: Array<{ id: string; type: LpSectionType }>;
  // Set when the hero copy workflow hard-failed or returned no usable copy. The
  // hero renders an error/retry state instead of the template placeholders.
  heroError?: string;
  // Template variant selector. "long-form-v2" routes the renderer down the
  // brand-adaptive v2 path (isV2); absent/other keeps the original v1 behavior.
  templateType?: string;
  /** Frozen static document for LP Express 2 Template Studio publications. */
  studioDesign?: LpStudioDesignSnapshot;
  /** UI-only status projection for a page-local Studio fork. It deliberately
   *  carries no provider identifiers or provider error details. */
  studioBrandDesignStatus?: "pending" | "ready" | "failed";
  hero?: {
    label?: string;
    headline?: string;
    subheadline?: string;
    cta_text?: string;
    // Per-pill overrides for the navy benefit bar under the hero carousel. When
    // absent each slot falls back to the derived product benefit (placeholder).
    benefitPills?: string[];
  };
  leadin?: string;
  advisory?: AdvisoryContent;
  stations?: StationsContent;
  cta?: {
    headline?: string;
    cta_text?: string;
  };
  // Hero-image carousel — three R2 URLs (front-facing studio still,
  // in-use lifestyle, editorial close-up) generated asynchronously by
  // the LP Express hero-image worker. Array entries may be null while
  // their slot is still rendering — the carousel pads those with a
  // loading skeleton. When the array is missing/empty the carousel
  // falls back to the product's own photos.
  heroImages?: Array<string | null>;
  heroImagesLoading?: boolean;
  // Per-slot hero-image status aligned 1:1 with heroImages by index
  // ("ready" | "failed" | "pending"). Lets the carousel show a retry
  // affordance on a permanently-failed slot instead of an endless spinner.
  heroImageStatuses?: Array<string>;
  showcase?: {
    headline?: string;
    subheadlines?: string[];
  };
  // One product-context block per belief card (image + card_header +
  // card_subtext), each driven by its own workflow call. Rendered inside the
  // "Real ingredients" BeliefSection, mapped 1:1 to the ingredient cards.
  productContext?: Array<{
    cardHeader?: string;
    cardSubtext?: string;
    imageUrl?: string;
    altText?: string;
    imageStatus?: string;
    imageLoading?: boolean;
  }>;
  villain?: {
    checklist_headline?: string;
    checklist_items?: string[];
    checklist_footer?: string;
    report_eyebrow?: string;
    report_headline?: string;
    report_rows?: Array<{ name?: string; note?: string; grade?: string }>;
    company_type_label?: string;
    grade_label?: string;
    verdict_label?: string;
    verdict?: string;
    belief_eyebrow?: string;
    belief_headline?: string;
  };
  // Per-section "is the AI workflow still generating?" flags. When a section's
  // flag is true and it has no live content yet, the renderer shows a loading
  // spinner instead of flashing the static template/placeholder copy.
  loading?: {
    // True while the initial hero copy Sim workflow is in flight (and no live
    // headline exists yet) — the hero renders its loading skeleton.
    hero?: boolean;
    advisory?: boolean;
    stations?: boolean;
    showcase?: boolean;
    villain?: boolean;
    productContext?: boolean;
    // True while the lp-origin-copy / lp-quote-copy workflows are still in
    // flight — sections show a spinner instead of the static fallback copy.
    originCopy?: boolean;
    quotes?: boolean;
    // True for the entire origin-image pipeline (origin copy → image gen →
    // poll). While true, every Farmstand image slot without a durable URL shows
    // a spinner instead of the hardcoded static placeholder.
    originImages?: boolean;
    // Per-section long-form copy workflow flags.
    timeline?: boolean;
    membership?: boolean;
    alternatives?: boolean;
    philosophy?: boolean;
    differentiator?: boolean;
    faq?: boolean;
    reviews?: boolean;
    finalCta?: boolean;
    proof?: boolean;
    // Section-image pipeline flags (copy → image gen → poll), same semantics as
    // originImages: while true, slots without a durable URL show a spinner.
    alternativesImages?: boolean;
    philosophyImages?: boolean;
    proofImages?: boolean;
  };
  // AI farmstand/origin copy (lp-origin-copy workflow). Every field falls back
  // to the template's hardcoded string when absent.
  originCopy?: {
    section_eyebrow?: string;
    section_headline?: string;
    section_subheadline?: string;
    fresh_badge?: string;
    block_1_headline?: string;
    block_1_body?: string;
    block_2_headline?: string;
    block_2_body?: string;
    block_3_headline?: string;
    block_3_body?: string;
    location_carousel_captions?: string[];
    origin_carousel_captions?: string[];
  };
  // AI-generated Farmstand still images (lp-origin-image workflow), one entry
  // per slot keyed by stable slot id. Missing/failed slots fall back to the
  // hardcoded MediaPlaceholder; imageLoading drives the per-slot spinner.
  originImages?: Array<{
    key?: string;
    imageUrl?: string;
    altText?: string;
    imageStatus?: string;
    imageLoading?: boolean;
  }>;
  // The 7 short pull-quotes (lp-quote-copy workflow), keyed by variant:
  // verdict, declaration, reframe, craft_statement, closing_challenge,
  // value_reframe, cta_headline. Missing variants fall back to hardcoded copy.
  quotes?: Record<string, { quote?: string; attribution?: string }>;
  // ---- Long-form section copy (8 workflows). Every field falls back to the
  // template's hardcoded copy when absent/empty. Shapes mirror the backend
  // extractors in server/services/lp-express-worker.ts. ----
  timeline?: {
    section_eyebrow?: string;
    section_headline?: string;
    section_subheadline?: string;
    steps?: Array<{ label?: string; headline?: string; bullets?: string[] }>;
    disclaimer?: string;
    sticky_note_headline?: string;
  };
  membership?: {
    section_eyebrow?: string;
    section_headline?: string;
    body_1?: string;
    body_2?: string;
    body_closing?: string;
    membership_label?: string;
    membership_tagline?: string;
    benefits?: Array<{ icon_hint?: string; label?: string; note?: string }>;
    objection_note_1?: string;
    objection_note_2?: string;
    objection_note_3?: string;
    location_label?: string;
    member_name?: string;
    member_since?: string;
    member_next_ship?: string;
    member_id?: string;
  };
  alternatives?: {
    section_eyebrow?: string;
    hero_headline?: string;
    hero_subheadline?: string;
    hero_differentiators?: string[];
    competitor_intro?: string;
    competitor_cards?: Array<{ title?: string; body?: string }>;
    comparison_them_label?: string;
    comparison_us_label?: string;
    comparison_them?: string[];
    comparison_us?: string[];
    closing_line_1?: string;
    closing_line_2?: string;
  };
  philosophy?: {
    section_eyebrow?: string;
    section_headline?: string;
    section_body_1?: string;
    section_body_2?: string;
    pillar_card_label?: string;
    pillars?: Array<{ num?: string; title?: string; body?: string }>;
    pillar_footer_words?: string[];
  };
  differentiator?: {
    section_eyebrow?: string;
    section_headline?: string;
    features?: Array<{ icon_hint?: string; title?: string; body?: string }>;
    trust_badge?: string;
  };
  faq?: {
    faqs?: Array<{ question?: string; answer?: string }>;
  };
  reviews?: {
    reviews?: Array<{ name?: string; variant?: string | null; quote?: string; verified?: boolean }>;
  };
  finalCta?: {
    section_eyebrow?: string;
    section_headline?: string;
    section_subheadline?: string;
    bundle_label?: string;
    savings_badge?: string;
    cta_button?: string;
    cta_subtext?: string;
    trust_badges?: Array<{ icon_hint?: string; label?: string }>;
    social_proof_line?: string;
  };
  // Science / proof section (lp-proof-copy workflow). Every field is silent-
  // fallback: when absent/empty the corresponding UI renders nothing (no
  // hardcoded template copy). Shape mirrors ProofResult in the backend.
  proof?: {
    section_eyebrow?: string;
    section_headline?: string;
    section_subheadline?: string;
    proof_folder_headline?: string;
    proof_folder_body?: string;
    proof_folder_bullets?: string[];
    proof_cards?: Array<{
      name?: string;
      what_it_is?: string;
      what_it_does?: string;
      why_it_matters?: string;
      research_links?: Array<{ label?: string; url?: string }>;
    }>;
    why_it_matters_headline?: string;
    why_it_matters_body?: string;
    why_it_matters_links?: Array<{ label?: string; url?: string }>;
    comparison_them?: string[];
    comparison_us?: string[];
    disclaimer?: string;
  };
  // ---- Section images (3 workflows), one entry per slot keyed by stable slot
  // id. Missing/failed slots fall back to the hardcoded MediaPlaceholder;
  // imageLoading drives the per-slot spinner. Slot keys:
  //   alternativesImages: brand-hero, competitor-1..5
  //   philosophyImages:   brand-lifestyle
  //   proofImages:        material-texture
  alternativesImages?: Array<LpSectionImageSlot>;
  philosophyImages?: Array<LpSectionImageSlot>;
  proofImages?: Array<LpSectionImageSlot>;
};

export type LpSectionImageSlot = {
  key?: string;
  imageUrl?: string;
  altText?: string;
  imageStatus?: string;
  imageLoading?: boolean;
};

export interface LpExpressRendererProps {
  brand: Brand | null | undefined;
  /** The exact product selected for this page before bundle expansion. Bundle
   * renderProducts may contain only member SKUs, so identity/options must come
   * from this root record rather than products[0]. */
  rootProduct?: Product | null;
  /** Products in the kit. NEVER hardcode the length — always render
   *  products.length items. A single-product LP is just a 1-item array. */
  products: Product[];
  persona?: Persona | null;
  angle?: NarrativeAngle | null;
  sale?: SalesDiscount | null;
  generatedContent?: GeneratedContent;
  /** Reviews grouped by productId. The product showcase pulls the first
   *  entry for each product's verified-review card; missing/empty entries
   *  hide the review card entirely (no "Jamie R." placeholder). */
  reviewsByProduct?: Record<string, ProductReview[]>;
  /** Optional buy-button override (used by the public, published page).
   *  - undefined  → keep the in-app default (brand.url).
   *  - non-empty  → every CTA links here (e.g. products.pdpUrl).
   *  - "" / null  → CTAs render inert (no href). */
  ctaUrl?: string | null;
  /** True while the brand's Instagram videos are being fetched/mirrored. While
   *  true, a Farmstand video slot with no durable URL yet shows a loading
   *  spinner instead of the dashed TODO placeholder. */
  videosLoading?: boolean;
  /** Retry a single failed page-image slot. Only the in-app builder passes
   *  this; the public/published page leaves it undefined so failed slots stay
   *  empty (no retry affordance). */
  onRetrySlot?: (
    mergeKey: "originImages" | "alternativesImages" | "philosophyImages" | "proofImages" | "listicleImages" | "advertorialImages" | "coldDripImages" | "coffeeShopImages" | "temeculaPassportImages" | "papabearLabelImages" | "productHeroImages" | "problemSolutionImages" | "offerStackImages",
    slotKey: string,
  ) => void;
  /** Published public page ONLY: route params the Coffee Shop template's lead
   *  forms need to POST to the public lead-capture endpoint. Leave undefined
   *  in the builder/preview so the forms render inert. */
  publicLeadContext?: { brandSlug: string; slug: string };
  /** Retry a single hero-carousel slot by index. In-app builder only — the
   *  public/published page leaves it undefined so failed slots stay empty. */
  onRetryHeroSlot?: (slotIndex: number) => void;
  /** Re-run the whole hero copy workflow. In-app builder only — wired to the
   *  hero loading/error state's Retry button so a failed/empty hero generation
   *  can be retried in place. */
  onRetryHero?: () => void;
  /** Recover a failed, page-local Studio Product Hero fork using its already
   *  admitted remote result. This is intentionally separate from retrying
   *  generation so a known remote failure can never submit a second request. */
  onRecoverStudioDesign?: () => void;
  /** True only while the page-local Studio fork is failed with a confirmed
   *  remote result that can be recovered read-only. */
  studioDesignRecoveryAvailable?: boolean;
  /** True while the explicit read-only Studio recovery is polling. */
  studioDesignRecoveryPending?: boolean;
  /** Retry a single "Real ingredients" belief-card image by index. In-app
   *  builder only; undefined on the public page. */
  onRetryProductContext?: (cardIndex: number) => void;
  /** Show the in-app debug/QA status overlays (Live/Static source pills, content
   *  SourceDot, and the green "AI-generated image" dot). Defaults to true for the
   *  in-app preview/builder surfaces; the published public page passes false so
   *  visitors never see the authoring indicators. */
  showStatusDots?: boolean;
  /** When provided, the renderer becomes an in-canvas editor: every section gets
   *  a hover toolbar (move up/down, hide/show, delete) and hidden sections are
   *  shown dimmed with a badge instead of being skipped. When absent (the
   *  published page / read-only preview) hidden sections are simply not
   *  rendered. */
  editor?: LpEditorControls;
}

// LpEditorControls now lives in lp-editor.tsx (alongside the in-canvas editing
// primitives that consume it) and is re-exported here for existing importers.
export type { LpEditorControls };

// =============================================================================
// OFFER — single consolidated commerce object used by ALL buy boxes & receipts.
// All sale-dependent fields are EMPTY when no SalesDiscount is attached. The
// renderer hides the corresponding rows so we never show fake savings or
// fake daily-price copy. Real product price still comes from product.priceText.
// =============================================================================
// Prefix "$" when a price string is numeric-only (e.g., "29" → "$29"). Leaves
// already-formatted strings ("$29", "29 USD", "—") alone and passes through
// empty values unchanged so the renderer can branch on truthiness.
function ensureDollar(v: string | null | undefined): string {
  if (!v) return "";
  const t = String(v).trim();
  if (!t) return "";
  if (/^[$€£¥]/.test(t)) return t;
  if (/^[\d.,]+$/.test(t)) return `$${t}`;
  return t;
}

export type Offer = {
  priceToday: string;
  retailTotal: string;
  discountAmount: string;
  discountPercent: string;
  shipping: string;
  guaranteeDays: number | null;
  refillPrice: string;
  refillDays: number | null;
  perDay: string;
  checkoutUrl: string;
  guaranteeChips: string[];
};

function buildOffer(
  brand: Brand | null | undefined,
  products: Product[] | null | undefined,
  sale: SalesDiscount | null | undefined,
  ctaOverride?: string | null,
): Offer {
  const s = sale as any;
  const b = brand as any;
  const list = Array.isArray(products) ? products.filter(Boolean) : [];
  const primaryProduct = list[0] ?? null;
  const guaranteeDays: number | null = s?.guaranteeDays ?? null;
  const refillPrice: string = s?.refillPrice ?? "";
  const refillDays: number | null = s?.refillDays ?? null;
  const chips: string[] = [];
  if (guaranteeDays) chips.push(`${guaranteeDays}-Day Money-Back`);
  if (s?.customerFacingCopy) chips.push(s.customerFacingCopy);
  if (refillPrice && refillDays) chips.push(`${refillPrice} Refill in ${refillDays} days`);

  // Retail = summed real product price(s). Sale price + savings come from the
  // attached sale's actual discount (saleType + discountValue), NOT from
  // non-existent sale.retailTotal/discountAmount/discountPercent fields. The
  // savings fields stay empty when no real discount applies so the hero hides
  // its discount UI and the showcase falls back to its hardcoded literals.
  const prices = list.map((p) => parsePrice((p as any).priceText));
  const allKnown = prices.length > 0 && prices.every((x) => x !== null);
  const retailNum = allKnown ? (prices as number[]).reduce((a, c) => a + c, 0) : null;
  const saleNum = retailNum !== null ? computeSalePrice(retailNum, sale as any) : null;
  const discountNum =
    retailNum !== null && saleNum !== null && retailNum > saleNum + 0.005
      ? retailNum - saleNum
      : null;
  const pctNum =
    discountNum !== null && retailNum ? Math.round((discountNum / retailNum) * 100) : null;
  const priceTodayNum = saleNum ?? retailNum;

  return {
    priceToday: priceTodayNum !== null ? formatMoney(priceTodayNum) : ((primaryProduct as any)?.priceText ?? ""),
    retailTotal: discountNum !== null && retailNum !== null ? formatMoney(retailNum) : "",
    discountAmount: discountNum !== null ? formatMoney(discountNum) : "",
    discountPercent: pctNum !== null ? String(pctNum) : "",
    shipping: s?.customerFacingCopy ?? "",
    guaranteeDays,
    refillPrice,
    refillDays,
    perDay: s?.perDay ?? "",
    // When a ctaOverride is supplied (public/published page) use it verbatim
    // — an empty string means "inert" (the buy buttons render with no href).
    // Otherwise keep the in-app builder default of the brand URL.
    checkoutUrl: ctaOverride !== undefined ? (ctaOverride ?? "") : (b?.url ?? "#"),
    guaranteeChips: chips,
  };
}

export function groundedStudioProductDetails(
  product: Product | null | undefined,
  offer: Pick<Offer, "priceToday">,
): LpProductHeroProductDetails | null {
  if (!product) return null;
  const text = (value: unknown): string =>
    typeof value === "string" ? value.trim() : "";
  const specs: LpProductHeroProductDetails["specs"] = [];
  const addSpec = (label: string, value: unknown) => {
    const groundedValue = text(value);
    if (groundedValue && specs.length < 4) {
      specs.push({ label, value: groundedValue });
    }
  };
  addSpec("Price", offer.priceToday);
  addSpec("Category", product.category);
  addSpec("SKU", product.sku);
  if (product.subscriptionAvailable === true) {
    addSpec("Subscription", "Available");
  }
  const heading = text(product.name);
  const description = text(product.description) || text(product.summary);
  if (!heading && !description && !specs.length) return null;
  return {
    label: "Product details",
    heading,
    description,
    specs,
  };
}

type ReceiptItem = { name: string; retail: string; label: string };
function buildReceiptItems(products: Product[]): ReceiptItem[] {
  return products.map((p) => ({
    name: p.name,
    retail: (p as any).priceText ?? "",
    label: "Included in kit",
  }));
}

// Per-token fallbacks for the LP Express look. The six "Group A" tokens
// (navy / coral / cream / text / deepnavy / card) are brand-driven: when the
// brand's labeled colorPalette supplies a matching color it overrides the
// fallback below, so any brand's long-form page themes itself from its own
// settings. The remaining tokens (muted / lightborder / green) are static
// page chrome and intentionally NOT brand-driven.
const LP_EXPRESS_TOKEN_FALLBACKS: Record<string, string> = {
  "--color-brand-navy": "#3F5094",
  "--color-brand-coral": "#F26967",
  "--color-brand-cream": "#F4EDDD",
  "--color-brand-text": "#231F20",
  "--color-brand-deepnavy": "#1B2837",
  "--color-brand-card": "#FFFFFF",
  "--color-brand-muted": "#6B6560",
  "--color-brand-lightborder": "#D8CFC3",
  "--color-brand-green": "#3A7D5C",
  // v2-accent aliases: v1 keeps these distinct Papabear hexes so the long-form
  // page stays pixel-identical; brandCssVarsV2 remaps them onto the brand palette.
  "--color-brand-greenaccent": "#2D7A4F",
  "--color-brand-navyaccent": "#1B3A6B",
  "--color-brand-gold": "#C4A265",
  "--color-brand-goldaccent": "#E8A33D",
  "--color-brand-indigo": "#3730A3",
};

// Match each Group A token to a brand colorPalette entry by label keyword.
// Order matters: more specific roles (deep navy, card) are matched before the
// broader navy/cream roles so e.g. "Deep Navy (Footer)" and "Card Background
// — White" aren't swallowed by the looser "navy"/"background" checks. Each
// palette entry is consumed at most once.
const LP_EXPRESS_BRAND_MATCHERS: Array<{ varName: string; match: (label: string) => boolean }> = [
  { varName: "--color-brand-deepnavy", match: (l) => l.includes("deep navy") || l.includes("footer") },
  { varName: "--color-brand-card", match: (l) => l.includes("card") },
  { varName: "--color-brand-coral", match: (l) => l.includes("coral") },
  { varName: "--color-brand-navy", match: (l) => l.includes("navy") || l.includes("primary") },
  { varName: "--color-brand-text", match: (l) => l.includes("text") || l.includes("near-black") || l.includes("near black") },
  { varName: "--color-brand-cream", match: (l) => l.includes("cream") || l.includes("background") },
];

const isHexColor = (h?: string): boolean =>
  typeof h === "string" && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(h.trim());

// Build the --color-brand-* CSS vars for a brand. Starts from the Papabear
// fallbacks, then overrides the six Group A tokens from the brand's labeled
// colorPalette ({ label, hex }[]) by keyword match. Any token without a
// matching palette entry keeps its fallback so nothing renders unstyled.
function brandCssVars(brand: Brand | null | undefined): React.CSSProperties {
  const vars: Record<string, string> = { ...LP_EXPRESS_TOKEN_FALLBACKS };
  const rawPalette = (brand as any)?.colorPalette;
  const entries = Array.isArray(rawPalette)
    ? (rawPalette as Array<{ label?: string; hex?: string }>)
        .map((e) => ({ label: (e?.label ?? "").toLowerCase().trim(), hex: (e?.hex ?? "").trim() }))
        .filter((e) => isHexColor(e.hex))
    : [];
  const used = new Set<number>();
  for (const m of LP_EXPRESS_BRAND_MATCHERS) {
    for (let i = 0; i < entries.length; i++) {
      if (used.has(i)) continue;
      if (m.match(entries[i].label)) {
        vars[m.varName] = entries[i].hex;
        used.add(i);
        break;
      }
    }
  }
  return vars as React.CSSProperties;
}

// ── Long Form v2 brand color derivation ──────────────────────────────────────
// v2 pages theme their ENTIRE palette from the brand instead of falling back to
// Papabear's fixed hexes. Semantic roles are resolved from the brand's labeled
// colorPalette and mapped onto the template's color tokens; any role the brand
// doesn't supply degrades to a NEUTRAL (non-Papabear) default, and text/section
// roles are luminance-checked so copy stays legible on every surface. v1 is
// untouched (still uses brandCssVars) — this only affects long-form-v2.
const LP_V2_NEUTRAL_FALLBACKS: Record<string, string> = {
  "--color-brand-navy": "#2E3A59",
  "--color-brand-coral": "#D7553F",
  "--color-brand-cream": "#F6F4EF",
  "--color-brand-text": "#231F20",
  "--color-brand-deepnavy": "#1F2733",
  "--color-brand-card": "#FFFFFF",
  "--color-brand-muted": "#6B6560",
  "--color-brand-lightborder": "#D8CFC3",
  "--color-brand-green": "#2E7D52",
  "--color-brand-gold": "#B0883D",
};

function lpHexToRgb(hex: string): [number, number, number] | null {
  let h = (hex || "").trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
// WCAG relative luminance (0 = black, 1 = white).
function lpRelLuminance(hex: string): number {
  const rgb = lpHexToRgb(hex);
  if (!rgb) return 0.5;
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
// Green-ish hue test for the price/positive token.
function lpIsGreenish(hex: string): boolean {
  const rgb = lpHexToRgb(hex);
  if (!rgb) return false;
  const [r, g, b] = rgb;
  return g > r + 12 && g > b + 12;
}

function brandCssVarsV2(
  brand: Brand | null | undefined,
  _theme: LpTheme | null | undefined,
): React.CSSProperties {
  const vars: Record<string, string> = { ...LP_V2_NEUTRAL_FALLBACKS };
  const rawPalette = (brand as any)?.colorPalette;
  const entries = Array.isArray(rawPalette)
    ? (rawPalette as Array<{ label?: string; hex?: string }>)
        .map((e) => ({ label: (e?.label ?? "").toLowerCase().trim(), hex: (e?.hex ?? "").trim() }))
        .filter((e) => isHexColor(e.hex))
    : [];
  const isText = (l: string) =>
    l.includes("text") || l.includes("near-black") || l.includes("near black") || l.includes("ink") || l.includes("foreground");
  const isBg = (l: string) =>
    l.includes("background") || l.includes("cream") || l.includes("canvas") || l.includes("surface") || /\bbg\b/.test(l);
  const find = (kw: string[], pred?: (e: { label: string; hex: string }) => boolean) =>
    entries.find((e) => kw.some((k) => e.label.includes(k)) && (!pred || pred(e)))?.hex;
  const byDark = entries.slice().sort((a, b) => lpRelLuminance(a.hex) - lpRelLuminance(b.hex));
  const darkest = byDark[0]?.hex;

  // Primary brand color — labeled "primary"/"main"/"brand" (not the text/bg role);
  // else the first non-text, non-bg swatch.
  const primary =
    find(["primary", "main", "brand"], (e) => !isText(e.label) && !isBg(e.label)) ||
    entries.find((e) => !isText(e.label) && !isBg(e.label))?.hex;
  const accent = find(["accent", "cta", "highlight"]) || find(["secondary"]) || primary;
  const secondary = find(["secondary", "tertiary"]);
  const textCand = find(["text", "near-black", "near black", "ink", "foreground"]);
  const bgCand = find(["background", "cream", "canvas", "surface"]);
  const deepCand = find(["footer", "deep", "dark"]) || primary || darkest;

  // --color-brand-navy (primary). theme.primaryColor still wins via lpThemeCssVars.
  if (primary) vars["--color-brand-navy"] = primary;

  // --color-brand-deepnavy: big dark section backgrounds carry WHITE text, so the
  // chosen color must be dark enough (lum <= 0.18 ⇒ >= ~4.5:1 on white).
  for (const cand of [deepCand, darkest]) {
    if (cand && lpRelLuminance(cand) <= 0.18) {
      vars["--color-brand-deepnavy"] = cand;
      break;
    }
  }

  // --color-brand-coral (accent): also used as text on light → keep readable.
  if (accent && lpRelLuminance(accent) <= 0.55) vars["--color-brand-coral"] = accent;

  // --color-brand-cream: a genuinely light, low-key background. Vivid swatches
  // (e.g. a bright yellow "secondary") make terrible section backgrounds, so only
  // an explicitly-labeled background that is actually light is accepted.
  if (bgCand && lpRelLuminance(bgCand) >= 0.85) vars["--color-brand-cream"] = bgCand;

  // --color-brand-text: must be dark for body copy on light.
  if (textCand && lpRelLuminance(textCand) <= 0.4) vars["--color-brand-text"] = textCand;

  // --color-brand-green (price/positive + primary CTA button): prefer a real
  // brand green; else DERIVE from a dark brand color (primary/accent/deep) so the
  // CTA and positive signals read as the brand instead of a foreign emerald. Must
  // be dark enough to work both as a button fill (white text) and as text on light.
  const greenCand = entries.find((e) => lpIsGreenish(e.hex) && lpRelLuminance(e.hex) <= 0.4)?.hex;
  const greenDerived = [primary, accent, deepCand, darkest].find(
    (c) => c && lpRelLuminance(c) <= 0.4,
  );
  if (greenCand) vars["--color-brand-green"] = greenCand;
  else if (greenDerived) vars["--color-brand-green"] = greenDerived;

  // --color-brand-gold (premium/ratings): the FIRST of secondary → accent →
  // primary that reads on light. A vivid secondary (e.g. a bright yellow) that
  // fails the contrast gate must fall through to a usable accent instead of
  // dropping to the foreign neutral gold.
  const goldDerived = [secondary, accent, primary, darkest].find(
    (c) => c && lpRelLuminance(c) <= 0.55,
  );
  if (goldDerived) vars["--color-brand-gold"] = goldDerived;

  // Accent aliases mirror their base role so v1's distinct Papabear accent hexes
  // (#2D7A4F / #1B3A6B / #E8A33D / #3730A3) all resolve to the brand palette in v2.
  vars["--color-brand-greenaccent"] = vars["--color-brand-green"];
  vars["--color-brand-navyaccent"] = vars["--color-brand-deepnavy"];
  vars["--color-brand-goldaccent"] = vars["--color-brand-gold"];
  vars["--color-brand-indigo"] = vars["--color-brand-navy"];

  return vars as React.CSSProperties;
}

// Typography classes driven by brand.brandDesignSystem.typographyDirection.
// Headlines default to a serif, body to sans, eyebrows to uppercase widely-
// tracked micro-caps — matching the Papabear warm-farmstand feel. When the
// brand's typographyDirection.*Style hints at "sans" or "serif" we honor it.
function brandTypography(_brand: Brand | null | undefined) {
  // LP Express uses a single typographic system: Inter everywhere, font-black
  // for headings/eyebrows/CTAs, no serif or display faces. Brand
  // typographyDirection is intentionally ignored on this surface.
  return {
    headline: "font-sans font-black tracking-tight",
    body: "font-sans",
    eyebrow: "font-sans uppercase font-black tracking-[0.2em] text-[10px]",
  };
}

// ── WYSIWYG global theme ──────────────────────────────────────────────────────
// The page's `theme` is turned into CSS custom properties on the root wrapper.
// Colors override the existing brand tokens (so every `var(--color-brand-*)` AND
// the bridged `text-brand-*` utility — see LP_WYSIWYG_STYLE — follow the new
// value). Fonts/radius are new vars consumed only by the scoped bridge. When a
// field is unset the var is simply omitted, so the brand/Tailwind default wins
// and the page looks exactly as before.
const LP_BTN_RADIUS: Record<string, string> = {
  rounded: "0.5rem",
  pill: "9999px",
  square: "0px",
};
function lpThemeCssVars(
  theme: LpTheme | null | undefined,
  brandFonts?: BrandFont[] | null,
): React.CSSProperties {
  const vars: Record<string, string> = {};
  if (!theme) return vars as React.CSSProperties;
  if (theme.primaryColor) vars["--color-brand-navy"] = theme.primaryColor;
  if (theme.accentColor) vars["--color-brand-coral"] = theme.accentColor;
  if (theme.headingFont && LP_THEME_FONTS[theme.headingFont]) {
    vars["--lp-heading-font"] = LP_THEME_FONTS[theme.headingFont];
  }
  if (theme.bodyFont && LP_THEME_FONTS[theme.bodyFont]) {
    vars["--lp-body-font"] = LP_THEME_FONTS[theme.bodyFont];
  }
  if (theme.buttonShape && LP_BTN_RADIUS[theme.buttonShape]) {
    vars["--lp-btn-radius"] = LP_BTN_RADIUS[theme.buttonShape];
  }
  // Task #1233 — explicit CTA button fill/text (seeded from the brand CTA style
  // at generation, editable like any other theme field). Text color falls back
  // to the shared luminance auto-contrast when the theme doesn't pick one —
  // except for outline buttons, whose label sits on the page background, so the
  // auto text IS the fill color.
  if (theme.ctaColor) {
    vars["--lp-cta-fill"] = theme.ctaColor;
    vars["--lp-cta-text"] =
      theme.ctaTextColor ||
      (theme.buttonFill === "outline"
        ? theme.ctaColor
        : autoCtaTextColor(theme.ctaColor));
  }
  // CTA label font — same grammar/resolution as per-block overrides.
  const ctaFontStack = resolveLpElementFontStack(theme.ctaFontFamily, brandFonts);
  if (ctaFontStack) vars["--lp-cta-font"] = ctaFontStack;
  return vars as React.CSSProperties;
}

// The brand's SAVED fonts (Edit Brand → Design System step) become the page's
// heading/body faces. These vars sit BENEATH the page `theme` fonts: the theme
// still wins when it sets headingFont/bodyFont (lpThemeCssVars is spread AFTER
// this), so an explicit page theme override is preserved. No saved fonts → no
// vars, so the page renders exactly as before (Inter default).
function brandFontCssVars(brand: Brand | null | undefined): React.CSSProperties {
  const roles = selectBrandFontRoles((brand as any)?.brandDesignSystem?.fonts);
  const vars: Record<string, string> = {};
  if (roles.heading) vars["--lp-heading-font"] = brandFontCssStack(roles.heading);
  if (roles.body) vars["--lp-body-font"] = brandFontCssStack(roles.body);
  return vars as React.CSSProperties;
}

// The <style> content that actually LOADS the fonts this page needs: the
// brand's page-wide saved role fonts (Google @import + proxied @font-face for
// uploads) PLUS any fonts referenced by per-block fontFamily overrides in
// `stylesMap` (curated keys are globally preloaded so they're skipped). Empty
// string when nothing needs loading (no font markup added, output unchanged).
// Rendered as a real JSX <style> so it works inside the mobile-preview iframe's
// nested React root AND on the published page (no silent fallback).
function brandFontFaceCss(
  brand: Brand | null | undefined,
  stylesMap?: Record<string, LpElementStyle> | null,
  brandFonts?: BrandFont[] | null,
  theme?: LpTheme | null,
): string {
  const roles = selectBrandFontRoles((brand as any)?.brandDesignSystem?.fonts);
  const roleLoads = collectBrandFontLoads(roles);
  const blockLoads = collectLpElementFontLoads(stylesMap ?? null, brandFonts, theme);
  // Merge + dedupe role (page-wide) fonts with per-block referenced fonts.
  const googleFamilies = Array.from(
    new Set([...roleLoads.googleFamilies, ...blockLoads.googleFamilies]),
  );
  const uploadedById = new Map<string, BrandFont>();
  for (const f of [...roleLoads.uploadedFonts, ...blockLoads.uploadedFonts]) {
    uploadedById.set(f.id, f);
  }
  const parts: string[] = [];
  if (googleFamilies.length > 0) {
    parts.push(`@import url("${googleFontsCssUrl(googleFamilies)}");`);
  }
  for (const f of uploadedById.values()) {
    if (f.url) parts.push(brandFontFaceRule(f, brandFontProxyUrl(f.url)));
  }
  return parts.join("\n");
}

// The root <div> always carries `lp-wysiwyg-root`; spacing/fill add modifier
// classes. Theme is opt-in per field — absent fields add no class/var.
// Task #1233 — the CTA style is PAGE-THEME-DRIVEN: the brand's saved CTA style
// is seeded into `generatedContent.theme` (ctaColor/ctaTextColor/buttonShape)
// once at page generation on the server, so published pages never restyle
// retroactively and per-page theme edits win by construction. A theme with a
// ctaColor gets the lp-cta-branded bridge; unset pages add no class/var →
// byte-identical markup.
function lpThemeRootClass(theme: LpTheme | null | undefined): string {
  const cls = ["lp-wysiwyg-root"];
  if (theme?.spacing === "compact") cls.push("lp-space-compact");
  if (theme?.spacing === "spacious") cls.push("lp-space-spacious");
  if (theme?.buttonShape) cls.push("lp-btn-shaped");
  if (theme?.buttonFill === "outline") cls.push("lp-btn-outline");
  if (theme?.ctaColor) cls.push("lp-cta-branded");
  if (theme?.ctaAllCaps) cls.push("lp-cta-caps");
  if (theme?.ctaFontFamily) cls.push("lp-cta-font");
  return cls.join(" ");
}

// Scoped stylesheet injected once inside every LP root. Each rule references a
// theme CSS var with a sensible fallback, so with NO theme set every rule is a
// visual no-op (resolves to the same Inter / brand hex / default radius). This
// is what bridges the renderer's hardcoded Tailwind `*-brand-navy/coral` utility
// classes to the themeable vars without touching 4000 lines of markup. NEVER
// raw user CSS — only fixed rules driven by allow-listed vars.
const LP_WYSIWYG_STYLE = `
.lp-wysiwyg-root .font-sans { font-family: var(--lp-body-font, var(--font-sans)); }
.lp-wysiwyg-root .font-black { font-family: var(--lp-heading-font, var(--lp-body-font, var(--font-sans))); }
.lp-wysiwyg-root .text-brand-navy { color: var(--color-brand-navy); }
.lp-wysiwyg-root .bg-brand-navy { background-color: var(--color-brand-navy); }
.lp-wysiwyg-root .border-brand-navy { border-color: var(--color-brand-navy); }
.lp-wysiwyg-root .text-brand-coral { color: var(--color-brand-coral); }
.lp-wysiwyg-root .bg-brand-coral { background-color: var(--color-brand-coral); }
.lp-wysiwyg-root .border-brand-coral { border-color: var(--color-brand-coral); }
.lp-wysiwyg-root.lp-btn-shaped [data-lp-cta] { border-radius: var(--lp-btn-radius) !important; }
.lp-wysiwyg-root.lp-cta-branded [data-lp-cta] {
  background-color: var(--lp-cta-fill) !important;
  color: var(--lp-cta-text) !important;
}
.lp-wysiwyg-root.lp-btn-outline [data-lp-cta] {
  background-color: transparent !important;
  color: var(--color-brand-coral) !important;
  border: 2px solid var(--color-brand-coral) !important;
}
.lp-wysiwyg-root.lp-cta-branded.lp-btn-outline [data-lp-cta] {
  background-color: transparent !important;
  color: var(--lp-cta-text) !important;
  border: 2px solid var(--lp-cta-fill) !important;
}
.lp-wysiwyg-root.lp-cta-caps [data-lp-cta] { text-transform: uppercase !important; }
.lp-wysiwyg-root.lp-cta-font [data-lp-cta] { font-family: var(--lp-cta-font) !important; }
.lp-wysiwyg-root.lp-space-compact > div > section,
.lp-wysiwyg-root.lp-space-compact > section { padding-top: 1.75rem; padding-bottom: 1.75rem; }
.lp-wysiwyg-root.lp-space-spacious > div > section,
.lp-wysiwyg-root.lp-space-spacious > section { padding-top: 5.5rem; padding-bottom: 5.5rem; }
`;

function StaticBadge({ label = "STATIC" }: { label?: string }) {
  if (!useLpStatusDots()) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-sm bg-yellow-300 text-black text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-yellow-500 shadow-sm align-middle"
      data-testid="badge-static"
      title="Placeholder — wire to real data"
    >
      ● STATIC {label !== "STATIC" ? `· ${label}` : ""}
    </span>
  );
}

function LiveBadge({ floating = false }: { floating?: boolean }) {
  if (!useLpStatusDots()) return null;
  // `floating` renders the dot as an out-of-flow marker pinned to the top-right
  // of a relatively-positioned parent. Used after block-level / wrapping
  // headlines & eyebrows so the green indicator never claims its own line or
  // shifts surrounding text (the in-app preview should match the published page).
  if (floating) {
    return (
      <span
        aria-hidden
        title="Live data"
        className="pointer-events-none absolute -top-1 right-0 h-1.5 w-1.5 rounded-full bg-green-500 ring-1 ring-green-700/50"
      />
    );
  }
  return (
    <span
      aria-label="Live data"
      title="Live data"
      className="inline-block relative -top-2 h-1.5 w-1.5 rounded-full bg-green-500 ring-1 ring-green-700/50"
    />
  );
}

// Stable media-override slot keys for the product-photo surfaces (closing CTA
// card, bundle/order-summary card, sticky bottom bar). Each lets the user pick a
// different gallery photo / upload / revert on that specific slot, persisted via
// generatedContent.mediaOverrides (displaySrc = override ?? default product photo).
const LP_PRODUCT_SLOT_CLOSING_CTA = "product:closing-cta";
const LP_PRODUCT_SLOT_BUNDLE = "product:bundle";
const LP_PRODUCT_SLOT_STICKY_BAR = "product:sticky-bar";

function getProductImages(product: Product | null | undefined): string[] {
  if (!product) return [];
  const media = (product as any).mediaJson;
  if (Array.isArray(media) && media.length > 0) {
    return media.filter((x) => typeof x === "string");
  }
  const refs = (product as any).adgenImageRefs;
  if (Array.isArray(refs) && refs.length > 0) {
    return refs.filter((x) => typeof x === "string");
  }
  return [];
}

// Product media galleries can mix tiny thumbnails with full-resolution shots
// (and the first entry is often the smallest). Loading every candidate and
// keeping the one with the largest pixel area lets the page always render the
// crispest available *product* image — no AI generation, just the best photo
// the product already has. Resolves to the first URL if none load.
function loadBestImageUrl(urls: string[]): Promise<string | null> {
  const valid = urls.filter((u): u is string => typeof u === "string" && !!u);
  if (valid.length === 0) return Promise.resolve(null);
  if (valid.length === 1) return Promise.resolve(valid[0]);
  return new Promise((resolve) => {
    let remaining = valid.length;
    let bestUrl = valid[0];
    let bestArea = -1;
    const settle = (url: string, area: number) => {
      if (area > bestArea) {
        bestArea = area;
        bestUrl = url;
      }
      remaining -= 1;
      if (remaining === 0) resolve(bestUrl);
    };
    valid.forEach((url) => {
      const probe = new Image();
      probe.onload = () => settle(url, probe.naturalWidth * probe.naturalHeight);
      probe.onerror = () => settle(url, -1);
      probe.src = url;
    });
  });
}

// Hook: highest-resolution image for a single product. Renders the first URL
// immediately, then upgrades to the largest once dimensions are known.
function useBestResolutionImage(urls: string[]): string | null {
  const key = urls.join("|");
  const [best, setBest] = useState<string | null>(urls[0] ?? null);
  useEffect(() => {
    let cancelled = false;
    setBest(urls[0] ?? null);
    loadBestImageUrl(urls).then((u) => {
      if (!cancelled) setBest(u);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return best;
}

// Hook: highest-resolution image keyed by product id, for lists where a hook
// can't be called per row. Falls back to each product's first image until the
// async probe resolves.
function useBestProductImages(products: Product[]): Record<string, string | null> {
  const key = products.map((p) => p.id).join("|");
  const [map, setMap] = useState<Record<string, string | null>>({});
  useEffect(() => {
    let cancelled = false;
    const result: Record<string, string | null> = {};
    Promise.all(
      products.map((p) =>
        loadBestImageUrl(getProductImages(p)).then((u) => {
          result[p.id] = u;
        }),
      ),
    ).then(() => {
      if (!cancelled) setMap(result);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return map;
}

function getIngredients(product: Product | null | undefined): string[] {
  return getIngredientRows(product).map((r) => r.name);
}

/** Returns ingredient rows from `products.ingredientsOrFeatures`. Accepts
 *  three shapes: plain string array, comma-delimited string, or array of
 *  `{ ingredient|name|title, benefit|description|note }` objects. Rows with
 *  no usable name are dropped. */
function getIngredientRows(
  product: Product | null | undefined,
): { name: string; benefit?: string }[] {
  const raw = (product as any)?.ingredientsOrFeatures;
  if (!raw) return [];
  if (typeof raw === "string") {
    return raw.split(",").map((s) => s.trim()).filter(Boolean).map((name) => ({ name }));
  }
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry): { name: string; benefit?: string } | null => {
      if (typeof entry === "string") {
        const t = entry.trim();
        return t ? { name: t } : null;
      }
      if (entry && typeof entry === "object") {
        const o = entry as Record<string, unknown>;
        const name =
          (typeof o.ingredient === "string" && o.ingredient.trim()) ||
          (typeof o.name === "string" && o.name.trim()) ||
          (typeof o.title === "string" && o.title.trim()) ||
          (typeof o.label === "string" && o.label.trim()) ||
          "";
        if (!name) return null;
        const benefit =
          (typeof o.benefit === "string" && o.benefit.trim()) ||
          (typeof o.description === "string" && o.description.trim()) ||
          (typeof o.note === "string" && o.note.trim()) ||
          (typeof o.detail === "string" && o.detail.trim()) ||
          undefined;
        return benefit ? { name, benefit } : { name };
      }
      return null;
    })
    .filter((x): x is { name: string; benefit?: string } => x !== null);
}

// =============================================================================
// 1. ANNOUNCEMENT BAR
// =============================================================================
function AnnouncementBar({ brand, sale }: { brand: Brand | null | undefined; sale?: SalesDiscount | null }) {
  const v2 = useLpV2();
  // Build the rotating label set. Prefer live sale copy + brand
  // approvedPhrases; pad with the template's evergreen six so the strip
  // always reads as a populated dual-copy ticker.
  const liveItems: string[] = [];
  if (sale?.customerFacingCopy) liveItems.push(sale.customerFacingCopy.trim());
  else if (sale?.name) {
    const v = sale.discountValue ? ` — ${sale.discountValue}` : "";
    liveItems.push(`${sale.name}${v}`.trim());
  }
  const approved = Array.isArray((brand as any)?.approvedPhrases)
    ? ((brand as any).approvedPhrases as unknown[]).filter(
        (x): x is string => typeof x === "string" && x.trim().length > 0,
      )
    : [];
  for (const a of approved) liveItems.push(a);
  const evergreen = v2
    ? [
        "FREE U.S. SHIPPING",
        "MADE FRESH IN SMALL BATCHES",
        "SKIP, PAUSE OR CANCEL ANYTIME",
        "PREMIUM QUALITY GUARANTEED",
        "CRAFTED WITH CARE",
        "SATISFACTION GUARANTEED",
      ]
    : [
        "FARM-TO-SKIN FUNCTIONAL SKINCARE",
        "MADE FRESH IN SMALL BATCHES",
        "FREE U.S. SHIPPING",
        "SKIP, PAUSE OR CANCEL ANYTIME",
        "RANCH CRAFTED IN CALIFORNIA",
        "ZERO SYNTHETICS · ALL NATURAL",
      ];
  while (liveItems.length < 6) liveItems.push(evergreen[liveItems.length]);
  const items = liveItems.slice(0, 6);
  const hasLive = !!(sale?.customerFacingCopy || sale?.name || approved.length > 0);

  // Sigil separators alternate between two lucide glyphs (no emojis per
  // brand guidelines).
  const renderRun = (keyPrefix: string) => (
    <div className="flex items-center whitespace-nowrap shrink-0" data-testid={`announcement-run-${keyPrefix}`}>
      {items.map((t, i) => {
        const Sigil = i % 2 === 0 ? Flower2 : Leaf;
        return (
          <span key={`${keyPrefix}-${i}`} className="flex items-center">
            <span className="mx-5 text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] font-sans">
              {t}
            </span>
            <Sigil className={`${v2 ? "text-[var(--color-brand-coral)]" : "text-brand-coral"} w-3.5 h-3.5`} aria-hidden />
          </span>
        );
      })}
    </div>
  );

  return (
    <div
      className={v2
        ? "relative bg-[var(--color-brand-deepnavy)] text-[var(--color-brand-cream)] overflow-hidden border-b border-[var(--color-brand-coral)]/30"
        : "relative bg-brand-deepnavy text-brand-cream overflow-hidden border-b border-brand-coral/30"}
      data-testid="bar-announcement"
    >
      <div className="absolute top-1 left-2 z-10">
        {hasLive ? <LiveBadge /> : <StaticBadge label="sale + approvedPhrases empty" />}
      </div>
      <div className="py-2.5 flex whitespace-nowrap animate-[announcementMarquee_40s_linear_infinite]">
        {renderRun("a")}
        {renderRun("b")}
      </div>
      <style>{`@keyframes announcementMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

// =============================================================================
// 1b. BRAND TAGLINE STRIP — thin cream strip above the sticky nav
// =============================================================================
function BrandTaglineStrip({ brand, typography }: { brand: Brand | null | undefined; typography: ReturnType<typeof brandTypography> }) {
  const tagline: string | null =
    (brand as any)?.promise
    ?? (brand as any)?.tagline
    ?? (brand as any)?.brandPromise
    ?? null;
  const logoUrl: string | null = (brand as any)?.logoUrl ?? (brand as any)?.adgenLogoUrl ?? null;
  return (
    <div className="bg-[var(--color-brand-cream)] border-b border-[var(--color-brand-lightborder)]">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-center gap-3">
        {logoUrl && (
          <img src={logoUrl} alt={brand?.name ?? "Brand"} className="h-5 w-5 rounded-full object-contain shrink-0" />
        )}
        {tagline ? (
          <span className={`${typography.body} text-xs sm:text-sm text-[var(--color-brand-muted)] text-center`} data-testid="text-brand-tagline-strip">
            {tagline}
          </span>
        ) : (
          <span className={`${typography.body} text-xs italic text-[var(--color-brand-muted)]`}>
            <StaticBadge label="brand.promise tagline missing" />
          </span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// 2. HERO
// =============================================================================
// Centered loading spinner used in place of an AI-driven section's static
// placeholder copy while its Sim workflow is still generating.
function SectionLoading({
  label,
  tone = "light",
}: {
  label?: string;
  tone?: "light" | "dark";
}) {
  const isDark = tone === "dark";
  return (
    <div
      className={`flex flex-col items-center justify-center gap-3 py-12 ${
        isDark ? "text-[var(--color-brand-cream)]/70" : "text-[var(--color-brand-deepnavy)]/50"
      }`}
      data-testid="loading-section"
      aria-label={label || "Loading"}
    >
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  );
}

// Full-bleed retry affordance for the main hero image card, shown when the
// current carousel slot's generation failed AND a retry handler is available
// (in-app builder only). Clicking regenerates just this one slot.
function HeroImageRetry({
  slotIndex,
  onRetry,
}: {
  slotIndex: number;
  onRetry: (slotIndex: number) => void;
}) {
  const [clicked, setClicked] = useState(false);
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 bg-[var(--color-brand-lightborder)]/30 px-4 text-center"
      data-testid={`retry-hero-${slotIndex}`}
    >
      <span className="text-xs font-bold text-[var(--color-brand-muted)]">
        Image failed to generate
      </span>
      <button
        type="button"
        disabled={clicked}
        onClick={() => {
          setClicked(true);
          onRetry(slotIndex);
        }}
        data-testid={`button-retry-hero-${slotIndex}`}
        className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-brand-navy)] bg-white px-3 py-1.5 text-xs font-bold text-[var(--color-brand-navy)] disabled:opacity-60"
      >
        <RotateCw className={`h-3.5 w-3.5 ${clicked ? "animate-spin" : ""}`} />
        {clicked ? "Retrying…" : "Retry"}
      </button>
    </div>
  );
}

// Compact retry button for a failed hero thumbnail slot (in-app builder only).
function HeroThumbRetry({
  slotIndex,
  onRetry,
}: {
  slotIndex: number;
  onRetry: (slotIndex: number) => void;
}) {
  const [clicked, setClicked] = useState(false);
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        if (clicked) return;
        setClicked(true);
        onRetry(slotIndex);
      }}
      className="w-full h-full flex items-center justify-center bg-[var(--color-brand-lightborder)]/40 text-[var(--color-brand-navy)]"
      data-testid={`button-retry-hero-thumb-${slotIndex}`}
      aria-label={`Retry image ${slotIndex + 1}`}
    >
      <RotateCw className={`h-4 w-4 ${clicked ? "animate-spin" : ""}`} />
    </span>
  );
}

function HeroSection({
  brand, products, sale, offer, generatedContent, typography, reviewDisplay, onRetryHeroSlot, onRetryHero,
}: {
  brand: Brand | null | undefined;
  products: Product[];
  sale?: SalesDiscount | null;
  offer: Offer;
  generatedContent?: GeneratedContent;
  typography: ReturnType<typeof brandTypography>;
  reviewDisplay?: LpReviewDisplay;
  onRetryHeroSlot?: (slotIndex: number) => void;
  onRetryHero?: () => void;
}) {
  const product = products[0] ?? null;
  const hasDiscount = !!offer.retailTotal && !!offer.discountPercent;
  // The blue discount banner and the cream pricing card inside the buy-box are
  // independent hide/delete units. Track each one's state so the connecting
  // tear-line and the corner rounding stay correct in every combination. In the
  // editor a hidden element stays visible (dimmed) and still occupies layout; on
  // the published page (no editor) it is omitted entirely.
  const lpEditor = useLpEditor();
  const buyboxDiscountState = useLpElementState("hero:buybox-discount");
  const buyboxPricingState = useLpElementState("hero:buybox-card");
  const discountInLayout =
    !!offer.discountPercent &&
    !buyboxDiscountState.deleted &&
    !(!lpEditor && buyboxDiscountState.hidden);
  const pricingInLayout =
    !buyboxPricingState.deleted && !(!lpEditor && buyboxPricingState.hidden);
  const showBuyboxTearLine = discountInLayout && pricingInLayout;
  // The inline hero review prefers a real pulled-in product review (read-only);
  // it only falls back to the editable placeholder copy when none exists. The
  // user can override the default (first) pick via the hero review picker; that
  // choice is snapshotted on generatedContent.reviewOverrides under "hero".
  const heroReviewOptions = reviewDisplay?.allReviews ?? [];
  const heroReviewOverride = useLpReviewOverride("hero");
  const heroReview = heroReviewOverride ?? reviewDisplay?.topReview ?? null;
  // Hero carousel is intentionally AI-generated (no fallback to raw product
  // photos). Each slot may be null while still rendering so the carousel can
  // show a skeleton in that slot; prev/next skip the nulls.
  const generatedHero: Array<string | null> = Array.isArray(generatedContent?.heroImages)
    ? generatedContent!.heroImages!.map((x) => (typeof x === "string" && x ? x : null))
    : [];
  const generatedHeroLoading = !!generatedContent?.heroImagesLoading;
  // User-uploaded image overrides shadow the AI hero slots by carousel index
  // (key `hero:<i>`). The underlying FAL URL is preserved so regeneration
  // context survives a revert. When no AI hero exists yet we still expose a
  // single slot (index 0) so a hero upload has somewhere to land.
  const heroOverridesMap = useLpMediaOverridesMap();
  const heroOverrideAt = (i: number): string | undefined => {
    const v = heroOverridesMap[`hero:${i}`]?.url;
    return typeof v === "string" && v.trim() ? v : undefined;
  };
  const baseHero: Array<string | null> = generatedHero.length > 0 ? generatedHero : [null];
  const carousel: Array<string | null> = baseHero.map((falUrl, i) => heroOverrideAt(i) ?? falUrl);
  const images: string[] = carousel.filter((x): x is string => !!x);
  const [idx, setIdx] = useState(0);
  const [openFaqIdx, setOpenFaqIdx] = useState<number | null>(null);
  const safeIdx = carousel.length > 0 ? Math.min(idx, carousel.length - 1) : 0;
  const img = carousel[safeIdx] ?? null;
  const overrideForSafe = heroOverrideAt(safeIdx);
  const falAtSafe = generatedHero[safeIdx] ?? null;
  // Per-slot status, aligned 1:1 with the carousel by index. A "failed" slot
  // (in-app only) shows a retry affordance instead of an endless spinner.
  const heroStatuses: Array<string> = Array.isArray(generatedContent?.heroImageStatuses)
    ? generatedContent!.heroImageStatuses!
    : [];
  const slotFailed = (i: number) =>
    !!onRetryHeroSlot && carousel[i] == null && heroStatuses[i] === "failed";
  const imgIsLoading = generatedHero.length > 0 && img === null && !slotFailed(safeIdx);
  const hero = generatedContent?.hero;
  // ── Hero copy generation states ───────────────────────────────────────────
  // The hero copy is produced by a Sim workflow. While that initial generation
  // is in flight (or has failed / returned empty), surface an explicit loading
  // or error/retry state instead of silently rendering the hardcoded template
  // placeholders. `heroLive` (a real headline exists) short-circuits both, so
  // published pages and any in-flight image rendering are unaffected.
  const v2 = useLpV2();
  const heroCopyLive = !!(hero as any)?.headline;
  const heroCopyLoading = !!generatedContent?.loading?.hero && !heroCopyLive;
  const heroCopyError = (generatedContent as any)?.heroError as string | undefined;
  // A present-but-empty hero object (workflow succeeded but returned no copy)
  // is treated as a failure so it doesn't fall through to the placeholders.
  const heroCopyEmpty =
    !heroCopyLive &&
    !!hero &&
    !(hero as any)?.subheadline &&
    !(hero as any)?.cta_text;
  const heroCopyFailed = !heroCopyLive && !heroCopyLoading && (!!heroCopyError || heroCopyEmpty);
  if (heroCopyLoading) {
    return (
      <section
        className="bg-[var(--color-brand-cream)]/40 py-10 md:py-16 px-4 sm:px-6"
        data-testid="status-hero-loading"
      >
        <SectionLoading label="Generating hero copy…" />
      </section>
    );
  }
  if (heroCopyFailed) {
    return (
      <section
        className="bg-[var(--color-brand-cream)]/40 py-16 px-4 sm:px-6"
        data-testid="status-hero-error"
      >
        <div className="max-w-xl mx-auto flex flex-col items-center text-center gap-3">
          <AlertTriangle className="h-8 w-8 text-destructive" />
          <h2 className={`${typography.headline} text-xl font-bold text-[var(--color-brand-navy)]`}>
            We couldn’t generate your hero
          </h2>
          <p className="text-sm text-[var(--color-brand-muted)]">
            {heroCopyError || "The hero workflow returned no copy. Please try generating again."}
          </p>
          {onRetryHero && (
            <button
              type="button"
              onClick={onRetryHero}
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-[var(--color-brand-navy)] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
              data-testid="button-retry-hero"
            >
              <RotateCw className="h-4 w-4" />
              Retry hero generation
            </button>
          )}
        </div>
      </section>
    );
  }
  // 4-row buy-box FAQ. Pull from generatedContent.hero.faqs (or .buy_box_faq)
  // when the workflow returns them; otherwise fall back to the Papabear
  // template's four canonical rows so the panel never reads thin.
  const heroAny = (hero ?? {}) as any;
  const liveFaqs: Array<{ q: string; a: string }> = (() => {
    const raw =
      (Array.isArray(heroAny?.faqs) && heroAny.faqs)
      || (Array.isArray(heroAny?.buy_box_faq) && heroAny.buy_box_faq)
      || (Array.isArray(heroAny?.faq) && heroAny.faq)
      || [];
    return raw
      .map((r: any) => ({
        q: (r?.q ?? r?.question ?? r?.title ?? "").toString().trim(),
        a: (r?.a ?? r?.answer ?? r?.content ?? r?.body ?? "").toString().trim(),
      }))
      .filter((r: { q: string; a: string }) => r.q && r.a);
  })();
  const fallbackFaqs: Array<{ q: string; a: string }> = v2
    ? [
        { q: "WHY US?", a: "We use high-quality, thoughtfully sourced ingredients and make every product in small batches for real results — not shortcuts." },
        { q: "WHAT'S INSIDE", a: "Carefully selected, effective ingredients and nothing unnecessary. Every component earns its place and delivers on what we promise." },
        { q: "QUALITY & SOURCING", a: "Responsibly sourced and crafted in small batches to ensure the highest quality and freshness in every order." },
        { q: "HOW TO USE", a: "Follow the simple steps included with your order. Use consistently, morning and night, for the best results." },
      ]
    : [
        { q: "WHY PAPABEAR?", a: "We use 100% grass-fed beef tallow sourced locally in California. It mirrors your skin's natural oils, allowing for deeper penetration and real healing, unlike water-based lotions full of synthetics." },
        { q: "INGREDIENTS & BENEFITS", a: "Grass-fed tallow, organic cold-pressed jojoba oil, and natural botanicals. Rich in vitamins A, D, E, and K to restore your skin barrier, reduce fine lines, and provide all-day hydration." },
        { q: "SOURCING & QUALITY", a: "Sourced exclusively from regenerative ranches. Hand-whipped in small batches in Murrieta, CA to ensure the highest quality and freshness." },
        { q: "HOW TO USE YOUR KIT", a: "Step 1: Apply Eye Cream gently. Step 2: Use Restorative Balm on face and neck. Step 3: Scrub lips if needed. Step 4: Apply Lip Balm. Use morning and night for best results." },
      ];
  const faqItems: Array<{ q: string; a: string }> = (
    liveFaqs.length >= 4 ? liveFaqs.slice(0, 4) : [...liveFaqs, ...fallbackFaqs].slice(0, 4)
  );
  const faqLive = liveFaqs.length > 0;
  const heroLive = !!hero?.headline;
  const ingredients = getIngredients(product);
  const benefits = ingredients.slice(0, 4);
  const benefitsLive = benefits.length > 0;
  const benefitPills = benefitsLive
    ? benefits
    : (v2
        ? ["Premium Quality", "Thoughtfully Made", "Clean Formula", "Crafted with Care"]
        : ["All-Day Glow", "Deep Moisture", "Zero Synthetics", "American-Made"]);
  const benefitIcons = [Sparkles, Leaf, ShieldCheck, Award];

  // 8 thumbnail slots — fill from images, leave the rest empty placeholders.
  const THUMB_COUNT = 8;
  const thumbSlots = Array.from({ length: THUMB_COUNT }, (_, i) => images[i] ?? null);

  // Subhead — short marketing tagline for the buy box.
  const subhead = hero?.subheadline
    || (product as any)?.shortDescription
    || (v2 ? "Crafted with care · Made to last · Shipped fast" : "Always handmade · Always small-batch · Always shipped fresh");
  const subheadLive = !!hero?.subheadline;

  return (
    <section className="bg-brand-cream/30 text-[var(--color-brand-text)] py-8 md:py-16 px-4 sm:px-6">
      {/* Mobile-only header block — product name + stars + review link + tagline */}
      <div className="lg:hidden mb-5 max-w-6xl mx-auto" data-testid="block-hero-mobile-header">
        <h1
          className="relative text-[28px] font-black text-brand-navy leading-tight tracking-tight mb-2"
          data-testid="text-hero-mobile-headline"
        >
          <EditableText
            as="span"
            regenBlockKey="hero"
            elementKey="hero:headline"
            path={["hero", "headline"]}
            value={hero?.headline}
            placeholder={product?.name || (v2 ? "Your Product" : "Farm to Skin Essentials")}
          />
          {heroLive && <LiveBadge floating />}
        </h1>
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center gap-1">
            <div className="flex text-yellow-400">
              {[1, 2, 3, 4].map((s) => (
                <Star key={s} className="fill-current w-4 h-4" />
              ))}
              <Star className="fill-current w-4 h-4" style={{ clipPath: "inset(0 20% 0 0)" }} />
            </div>
            <span className="text-xs font-bold text-brand-navy" data-testid="text-hero-mobile-rating">{reviewDisplay?.ratingText ?? "4.8"}</span>
          </div>
          <a
            href="#reviews"
            className="text-sm font-semibold text-brand-navy underline underline-offset-2 hover:text-brand-coral transition-colors"
            data-testid="link-hero-mobile-reviews"
          >
            {reviewDisplay?.reviewCountText ?? "4,000+"}{" "}
            <EditableText
              as="span"
              noHideDelete
              path={["hero", "reviewsLinkLabel"]}
              value={(hero as any)?.reviewsLinkLabel}
              placeholder="Reviews"
            />
          </a>
        </div>
        <p
          className="text-center text-xs font-semibold text-brand-muted leading-relaxed"
          data-testid="text-hero-mobile-tagline"
        >
          <EditableText
            as="span"
            multiline
            regenBlockKey="hero"
            regenAnchor
            elementKey="hero:subheadline"
            path={["hero", "subheadline"]}
            value={hero?.subheadline}
            placeholder={subhead}
          />
        </p>
      </div>
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start w-full">
        {/* =========================================================
            LEFT — image card (no padding) + benefit overlay + thumbs
            ========================================================= */}
        <div className="w-full space-y-3">
          <EditableElement elementKey="hero:image" toolbarSide="left" testId="element-hero-image">
          <div
            className="group/lpimg relative aspect-square rounded-2xl overflow-hidden bg-[var(--color-brand-lightborder)]/50"
            style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.08)" }}
          >
            {img ? (
              <AnimatePresence mode="wait">
                <motion.img
                  key={safeIdx}
                  src={img}
                  alt={product?.name ?? ""}
                  className="absolute inset-0 w-full h-full object-cover"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.25 }}
                />
              </AnimatePresence>
            ) : slotFailed(safeIdx) && onRetryHeroSlot ? (
              <HeroImageRetry slotIndex={safeIdx} onRetry={onRetryHeroSlot} />
            ) : imgIsLoading || generatedHeroLoading ? (
              <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-brand-lightborder)]/60 to-[var(--color-brand-lightborder)]/30 animate-pulse flex items-center justify-center text-[var(--color-brand-muted)]" aria-label="Generating hero image">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-[var(--color-brand-muted)] text-sm gap-2 font-sans">
                No product images
                <StaticBadge label="product.images empty" />
              </div>
            )}
            {img && !overrideForSafe && (
              <div className="absolute top-3 left-3 z-20" data-testid="badge-hero-image-live"><LiveBadge /></div>
            )}
            <LpImageActions
              slotKey={`hero:${safeIdx}`}
              canRegenerate={!!falAtSafe && !!onRetryHeroSlot}
              onRegenerate={onRetryHeroSlot ? () => onRetryHeroSlot(safeIdx) : undefined}
              testIdPrefix={`hero-${safeIdx}`}
            />
            {carousel.length > 1 && (
              <>
                <button
                  onClick={() => setIdx((i) => (i - 1 + carousel.length) % carousel.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 border border-[var(--color-brand-lightborder)] shadow flex items-center justify-center text-[var(--color-brand-text)] z-20"
                  aria-label="Previous image"
                  data-testid="button-hero-prev-image"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setIdx((i) => (i + 1) % carousel.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/90 border border-[var(--color-brand-lightborder)] shadow flex items-center justify-center text-[var(--color-brand-text)] z-20"
                  aria-label="Next image"
                  data-testid="button-hero-next-image"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </>
            )}

          </div>
          </EditableElement>

          {/* Full-width tab strip under the image — navy bar with coral bullets.
              Whole bar is one hide/delete unit (EditableElement); each pill's
              label is individually editable, falling back to the derived product
              benefit as placeholder. */}
          <EditableElement elementKey="hero:benefit-bar" testId="element-hero-benefit-bar">
            <div className={
              v2
                ? `w-full rounded-md bg-[var(--color-brand-deepnavy)] text-[var(--color-brand-cream)] px-5 py-4 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 items-center ${typography.body}`
                : `w-full rounded-md bg-[var(--color-brand-deepnavy)] text-[var(--color-brand-cream)] px-5 py-5 flex items-center justify-between gap-1 ${typography.body}`
            }>
              {benefitPills.map((p, i) => (
                <span
                  key={`tab-${i}`}
                  className={
                    v2
                      ? "flex items-center gap-1.5 text-[9px] sm:text-[10px] uppercase tracking-wider min-w-0"
                      : "flex items-center gap-1 text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-wider whitespace-nowrap min-w-0"
                  }
                >
                  <span className="h-1 w-1 rounded-full bg-[var(--color-brand-coral)] shrink-0" />
                  <EditableText
                    as="span"
                    path={["hero", "benefitPills", i]}
                    value={hero?.benefitPills?.[i]}
                    placeholder={p}
                    testId={`text-hero-benefit-${i}`}
                  />
                </span>
              ))}
            </div>
          </EditableElement>

          {/* Thumbnail strip — show every carousel slot (real images and
              still-rendering skeletons) so the user can see how many
              variants are on the way. Capped at 8. */}
          {carousel.length > 0 && (
            <div className="flex gap-2 overflow-x-auto">
              {carousel.slice(0, 8).map((src, i) => {
                const active = i === safeIdx;
                return (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    className={`h-16 w-16 sm:h-20 sm:w-20 shrink-0 rounded border-2 overflow-hidden bg-white ${
                      active
                        ? "border-[var(--color-brand-navy)] ring-1 ring-[var(--color-brand-navy)]"
                        : "border-[var(--color-brand-lightborder)]"
                    }`}
                    data-testid={`button-hero-thumb-${i}`}
                    aria-label={`View image ${i + 1}`}
                  >
                    {src ? (
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    ) : slotFailed(i) && onRetryHeroSlot ? (
                      <HeroThumbRetry slotIndex={i} onRetry={onRetryHeroSlot} />
                    ) : (
                      <div className="w-full h-full bg-[var(--color-brand-lightborder)]/40 animate-pulse flex items-center justify-center">
                        <Loader2 className="h-3 w-3 animate-spin text-[var(--color-brand-muted)]" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* =========================================================
              RIGHT — buy box (template parity)
              ========================================================= */}
          <div className="bg-white rounded-xl p-6 md:p-8 shadow-xl border border-brand-lightborder/50 min-w-0 w-full">

            {/* Desktop title */}
            <h1
              className="relative hidden lg:block text-[32px] md:text-[38px] font-black text-brand-navy leading-tight tracking-tight mb-5"
              data-testid="text-hero-headline"
            >
              <EditableText
                as="span"
                regenBlockKey="hero"
                regenAnchor
                elementKey="hero:headline"
                path={["hero", "headline"]}
                value={hero?.headline}
                placeholder={product?.name || (v2 ? "Premium Care, Crafted for You." : "Full Functional Skin Care for Your Face.")}
              />
              {heroLive && <LiveBadge floating />}
            </h1>
            <p
              className="relative hidden lg:block mb-5 text-[13px] font-semibold text-brand-muted"
              data-testid="text-hero-subheadline"
            >
              <EditableText
                as="span"
                multiline
                regenBlockKey="hero"
                regenAnchor
                elementKey="hero:subheadline"
                path={["hero", "subheadline"]}
                value={hero?.subheadline}
                placeholder={subhead}
              />
              {subheadLive && <LiveBadge floating />}
            </p>

            {/* Mobile title */}
            <h1
              className="relative lg:hidden font-black leading-tight tracking-tight mb-3 text-brand-navy"
              style={{ fontSize: "clamp(22px, 6vw, 28px)" }}
            >
              <EditableText
                as="span"
                regenBlockKey="hero"
                regenAnchor
                elementKey="hero:headline"
                path={["hero", "headline"]}
                value={hero?.headline}
                placeholder={product?.name || (v2 ? "Premium Care, Crafted for You." : "Full Functional Skin Care for Your Face.")}
              />
              {heroLive && <LiveBadge floating />}
            </h1>

            {/* What's in the kit */}
            <LpReorderGroup
              groupKey="hero:buybox"
              items={[
                { key: "hero:kit-card", node: (
            <EditableElement elementKey="hero:kit-card" testId="element-hero-kit-card">
            <div className="rounded-xl mb-4 overflow-hidden border border-[#D8CFC3]">
              <div className="px-3 py-2 flex items-center justify-between bg-[var(--color-brand-cream)] border-b border-[#D8CFC3]">
                <EditableText
                  as="span"
                  className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-navy"
                  path={["hero", "buyBox", "kitLabel"]}
                  value={(hero as any)?.buyBox?.kitLabel}
                  placeholder={products.length > 1 ? "What's in the kit" : "What you get"}
                />
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-coral">
                  {products.length} {products.length === 1 ? "piece" : "pieces"}
                </span>
              </div>
              {products.length > 0 && (
                <ul data-testid="list-hero-kit-items">
                  {products.map((p, i, arr) => {
                    const desc = ((p as any).shortDescription as string | undefined)
                      || ((p as any).description as string | undefined)?.slice(0, 60)
                      || (v2 ? "Premium quality, thoughtfully crafted" : "Premium ingredients, small-batch crafted");
                    const price = (p as any).priceText as string | undefined;
                    return (
                      <li
                        key={p.id}
                        className="flex items-center gap-2.5 px-3 py-2.5 bg-white"
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #EDE4D0" : "none" }}
                        data-testid={`text-hero-kit-item-${p.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 shrink-0 text-brand-green" />
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-black leading-tight text-brand-navy">{p.name}</p>
                          <p className="text-[11px] font-semibold leading-snug text-brand-muted">{desc}</p>
                        </div>
                        {price && (
                          <span
                            className={`text-[11px] font-semibold shrink-0 ${hasDiscount ? "text-[#C0392B] line-through" : "text-brand-muted"}`}
                          >
                            {ensureDollar(price)}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            </EditableElement>
                ) },
                { key: "hero:leadin", node: (
            <EditableText
              as="p"
              multiline
              elementKey="hero:leadin"
              regenBlockKey="leadin"
              regenAnchor
              className="text-sm font-semibold leading-snug mb-5 text-brand-muted"
              path={["leadin"]}
              value={generatedContent?.leadin}
              placeholder={((product as any)?.description as string | undefined)
                || (v2
                  ? "Small-batch, zero shortcuts. Exactly what you deserve — nothing less."
                  : "Ranch-crafted, small-batch, zero shortcuts. Your skin gets exactly what nature intended.")}
              testId="text-hero-buybox-leadin"
            />
                ) },
                { key: "hero:buybox-card", node: (
            /* Coupon / price block — the blue discount banner and the cream
               pricing card are independent hide/delete units. */
            <div
              className="mb-6 relative"
              style={{ filter: "drop-shadow(0 6px 24px rgba(63,80,148,0.18))" }}
              data-testid="card-hero-buybox"
            >
              <div className="rounded-2xl" style={{ border: "2px dashed #C8BFB3" }}>

                {/* Header strip — blue discount section (independently hideable) */}
                {offer.discountPercent && (
                  <EditableElement elementKey="hero:buybox-discount" testId="element-hero-buybox-discount">
                  <div className={`bg-brand-navy ${pricingInLayout ? "rounded-t-xl" : "rounded-xl"} px-5 py-5 text-center relative overflow-hidden`}>
                    <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-white/10" />
                    <div className="absolute -bottom-6 -left-6 w-[72px] h-[72px] rounded-full bg-white/10" />
                    <Scissors className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <EditableText
                      as="p"
                      className="text-white/70 text-[10px] font-black uppercase tracking-[0.25em] mb-0.5 relative z-10"
                      path={["hero", "buyBox", "saleName"]}
                      value={sale?.name}
                      placeholder={v2 ? "Special Offer" : "Ranch Special"}
                    />
                    <p className="text-white font-black leading-none relative z-10" style={{ fontSize: 64, lineHeight: 1 }}>
                      {offer.discountPercent.replace(/%/g, "")}
                      <span style={{ fontSize: 40 }}>%</span>
                    </p>
                    <EditableText
                      as="p"
                      className="text-white font-black text-base tracking-widest relative z-10 -mt-1 uppercase"
                      path={["hero", "buyBox", "offTodayLabel"]}
                      value={(hero as any)?.buyBox?.offTodayLabel}
                      placeholder="Off Today"
                    />
                  </div>
                  </EditableElement>
                )}

                {/* Tear line — only when both sections share the ticket */}
                {showBuyboxTearLine && (
                  <div className="relative h-6 bg-[#FAF6EE] flex items-center overflow-visible">
                    <div
                      className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white z-10"
                      style={{ border: "2px dashed #C8BFB3" }}
                    />
                    <div
                      className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white z-10"
                      style={{ border: "2px dashed #C8BFB3" }}
                    />
                    <div className="mx-6 w-full border-t-2 border-dashed border-[#C8BFB3]" />
                  </div>
                )}

                {/* Body — pricing section (independently hideable) */}
                <EditableElement elementKey="hero:buybox-card" testId="element-hero-buybox-card">
                <div className={`bg-[#FAF6EE] ${discountInLayout ? "rounded-b-xl" : "rounded-xl"} px-5 pt-3 pb-5`} style={{ containerType: "inline-size" }}>
                  <div className="flex items-center justify-between gap-3 mb-4" style={{ padding: "4px 8px" }}>
                    <div className="min-w-0 flex-1">
                      <EditableText
                        as="p"
                        className="text-[9px] text-brand-muted uppercase tracking-widest font-bold mb-1"
                        path={["hero", "buyBox", "priceTodayLabel"]}
                        value={(hero as any)?.buyBox?.priceTodayLabel}
                        placeholder="Your price today"
                      />
                      <div className="flex items-end gap-2 min-w-0">
                        <span
                          className="font-black text-brand-navy leading-none whitespace-nowrap"
                          style={{ fontSize: "clamp(1.5rem, 12cqw, 4.5rem)" }}
                          data-testid="text-hero-price-today"
                        >
                          {ensureDollar(offer.priceToday)}
                        </span>
                        {offer.retailTotal && (
                          <div className="flex flex-col items-start mb-1 min-w-0">
                            <EditableText
                              as="span"
                              className="text-[8px] text-brand-muted uppercase tracking-wide font-bold mb-0.5"
                              path={["hero", "buyBox", "regPriceLabel"]}
                              value={(hero as any)?.buyBox?.regPriceLabel}
                              placeholder="Reg. price"
                            />
                            <span
                              className="text-brand-muted line-through font-bold whitespace-nowrap"
                              style={{ fontSize: "clamp(0.75rem, 4cqw, 1.5rem)" }}
                              data-testid="text-hero-price-retail"
                            >
                              {ensureDollar(offer.retailTotal)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    {offer.discountAmount && (
                      <div
                        className="rounded-full bg-[#E8F3ED] border-4 border-brand-green flex flex-col items-center justify-center shrink-0"
                        style={{ width: "clamp(52px, 22cqw, 90px)", height: "clamp(52px, 22cqw, 90px)" }}
                        data-testid="badge-hero-save-circle"
                      >
                        <EditableText
                          as="span"
                          className="text-[9px] text-brand-green font-black uppercase tracking-widest leading-none mb-1"
                          path={["hero", "buyBox", "saveLabel"]}
                          value={(hero as any)?.buyBox?.saveLabel}
                          placeholder="SAVE"
                        />
                        <span className="text-brand-green font-black leading-none whitespace-nowrap" style={{ fontSize: "clamp(12px, 5.2cqw, 22px)" }}>
                          {ensureDollar(offer.discountAmount)}
                        </span>
                        <EditableText
                          as="span"
                          className="text-[9px] text-brand-green font-bold leading-none mt-0.5 uppercase tracking-wide"
                          path={["hero", "buyBox", "saveTodayLabel"]}
                          value={(hero as any)?.buyBox?.saveTodayLabel}
                          placeholder="today"
                        />
                      </div>
                    )}
                  </div>

                  <div className="-mx-5 bg-[#E8F3ED] border-y border-[#C5E1D4] flex items-center gap-2 px-5 py-3" data-testid="row-hero-feature-0">
                    <Leaf className="w-4 h-4 text-brand-green shrink-0" />
                    <EditableText
                      as="span"
                      className="text-brand-green font-bold text-sm"
                      path={["hero", "buyBox", "shippingPromo"]}
                      value={offer.shipping}
                      placeholder="FREE U.S. shipping on every order"
                    />
                  </div>
                  <div className="-mx-5 bg-[#FEF6E4] border-b border-[#F5DFA8] flex items-center gap-2 px-5 py-3" data-testid="row-hero-feature-1">
                    <Leaf className="w-4 h-4 shrink-0 text-[#92600A]" />
                    <EditableText
                      as="span"
                      className="font-bold text-sm text-[#92600A]"
                      path={["hero", "buyBox", "feature1"]}
                      value={(hero as any)?.buyBox?.feature1}
                      placeholder="Made fresh daily in small batches"
                    />
                  </div>
                  <div className="-mx-5 bg-[#EEF2FF] border-b border-[#C7D2F8] flex items-center gap-2 px-5 py-3" data-testid="row-hero-feature-2">
                    <Sparkles className="w-4 h-4 shrink-0 text-brand-navy" />
                    <EditableText
                      as="span"
                      className="font-bold text-sm text-brand-navy"
                      path={["hero", "buyBox", "feature2"]}
                      value={(hero as any)?.buyBox?.feature2}
                      placeholder="Crafted with all-natural ingredients"
                    />
                  </div>

                  <div className="text-center pt-4">
                    <EditableText
                      as="p"
                      className="text-brand-navy font-black text-base tracking-tight"
                      path={["hero", "buyBox", "perDay"]}
                      value={offer.perDay}
                      placeholder="Less than 65¢ a day"
                      testId="text-hero-per-day"
                    />
                    <EditableText
                      as="p"
                      className="text-brand-muted text-[10px] font-medium tracking-wide mt-0.5"
                      path={["hero", "buyBox", "supplyLabel"]}
                      value={(hero as any)?.buyBox?.supplyLabel}
                      placeholder={`${offer.refillDays ? `${offer.refillDays}-day supply` : "45-day supply"}${(brand as any)?.locationLabel ? ` · ${(brand as any).locationLabel}` : (v2 ? "" : " · Temecula, CA")}`}
                    />
                  </div>
                </div>
                </EditableElement>
              </div>
            </div>
                ) },
              ]}
            />

            {/* CTA */}
            {!!hero?.cta_text && (
              <div className="flex justify-end mb-1" data-testid="badge-hero-cta-live"><LiveBadge /></div>
            )}
            <a
              href={offer.checkoutUrl || undefined}
              data-lp-cta
              className={v2
                ? "block w-full bg-[var(--color-brand-greenaccent)] hover:brightness-90 text-white text-center font-black rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.28)] hover:shadow-[0_12px_30px_rgba(0,0,0,0.38)] transition-all hover:-translate-y-0.5 mb-6 py-4"
                : "block w-full bg-[var(--color-brand-greenaccent)] hover:bg-[#1F5E3C] text-white text-center font-black rounded-2xl shadow-[0_8px_24px_rgba(45,122,79,0.45)] hover:shadow-[0_12px_30px_rgba(45,122,79,0.55)] transition-all hover:-translate-y-0.5 mb-6 py-4"}
              data-testid="button-hero-cta"
            >
              <div className="text-xl uppercase tracking-wider leading-tight">
                <EditableText
                  as="span"
                  noHideDelete
                  path={["hero", "cta_text"]}
                  value={hero?.cta_text}
                  placeholder={offer.priceToday ? `Get My Kit for ${ensureDollar(offer.priceToday)} Today` : "Add to Cart"}
                />
              </div>
              <EditableText
                as="div"
                className="text-xs font-semibold text-white/75 tracking-wide mt-0.5 normal-case"
                path={["hero", "buyBox", "guaranteeSubtext"]}
                value={(hero as any)?.buyBox?.guaranteeSubtext}
                placeholder={`Free shipping · ${offer.guaranteeDays ? `${offer.guaranteeDays}-Day` : "45-Day"} guarantee`}
              />
            </a>

            {/* Inline verified review — the whole row is one hide/delete unit
                (EditableElement); the review picker lives inside it. */}
            <EditableElement
              elementKey="hero:buybox-review"
              toolbarSide="left"
              className="mb-2.5"
              testId="element-hero-buybox-review"
            >
            <div
              className="group/lpreview relative flex gap-2.5 items-center"
              style={{ background: "#fff", border: "1px solid #e5e5e5", borderRadius: 8, padding: "10px 12px" }}
              data-testid="row-hero-review"
            >
              <LpReviewActions slotKey="hero" reviews={heroReviewOptions} testIdPrefix="hero" />
              <div
                className="shrink-0 rounded-full bg-brand-lightborder flex items-center justify-center overflow-hidden"
                style={{ width: 28, height: 28 }}
              >
                <Star className="w-3.5 h-3.5 text-brand-coral fill-brand-coral" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="text-[#F5A623] text-[10px] tracking-wide">
                    {heroReview ? "★".repeat(heroReview.rating) : "★★★★★"}
                  </span>
                  <EditableText
                    as="span"
                    className="text-[9px] font-semibold text-[var(--color-brand-greenaccent)]"
                    path={["hero", "buyBox", "verifiedLabel"]}
                    value={(hero as any)?.buyBox?.verifiedLabel}
                    placeholder="Verified"
                  />
                </div>
                <p className="text-[11px] leading-snug text-brand-navy m-0">
                  {heroReview ? (
                    // A real product review takes precedence and is read-only —
                    // it's pulled-in social proof, not editable marketing copy.
                    <>
                      <span data-testid="text-hero-review-quote">{`"${heroReview.reviewText}"`}</span>
                      {heroReview.reviewer && (
                        <span className="text-[10px] text-[#999]" data-testid="text-hero-review-name">
                          {` — ${heroReview.reviewer}`}
                        </span>
                      )}
                    </>
                  ) : (
                    // No real review exists yet — fall back to editable placeholder copy.
                    <>
                      <EditableText
                        as="span"
                        multiline
                        path={["hero", "buyBox", "reviewQuote"]}
                        value={(hero as any)?.buyBox?.reviewQuote}
                        placeholder={'"Threw out my $80 cream. Under-eyes have never looked better."'}
                      />
                      <EditableText
                        as="span"
                        className="text-[10px] text-[#999]"
                        path={["hero", "buyBox", "reviewName"]}
                        value={(hero as any)?.buyBox?.reviewName}
                        placeholder=" — Mariah P."
                      />
                    </>
                  )}
                </p>
              </div>
            </div>
            </EditableElement>

            {/* Guarantee bullets */}
            <div className="flex flex-wrap mb-1.5" style={{ gap: "4px 14px" }} data-testid="row-hero-subscription-terms">
              {[
                `Refill ships in ${offer.refillDays || 45} days`,
                "Skip, pause or cancel anytime",
                `${offer.guaranteeDays || 45}-Day Money-Back Guarantee`,
                "FREE avg 3-day shipping today",
              ].map((text, i) => (
                <div key={text} className="flex items-center gap-1">
                  <CheckCircle2 className="text-brand-green shrink-0" style={{ width: 10, height: 10 }} />
                  <EditableText
                    as="span"
                    className="text-[9px] font-semibold text-[#5A554F]"
                    path={["hero", "buyBox", "subscriptionTerms", i]}
                    value={(hero as any)?.buyBox?.subscriptionTerms?.[i]}
                    placeholder={text}
                  />
                </div>
              ))}
            </div>

            <EditableText
              as="p"
              multiline
              className="text-[8px] text-[#9A8E80] m-0 uppercase tracking-[0.1em]"
              path={["hero", "buyBox", "finePrint"]}
              value={(hero as any)?.buyBox?.finePrint}
              placeholder="*By subscribing you agree to our terms. Cancel anytime."
              testId="text-hero-fine-print"
            />

            {/* In-box FAQ */}
            <div className="mt-4">
              {faqLive && (
                <div className="flex justify-end mb-1" data-testid="badge-hero-faq-live"><LiveBadge /></div>
              )}
              <LpReorderGroup
                groupKey="hero:faq-list"
                items={faqItems.map((f, i) => {
                const isOpen = openFaqIdx === i;
                return { key: `hero:faq-${i}`, node: (
                  <EditableElement key={i} elementKey={`hero:faq-${i}`} testId={`element-faq-${i}`} className="border-t border-brand-lightborder">
                  <div data-testid={`faq-item-${i}`}>
                    <button
                      type="button"
                      onClick={() => setOpenFaqIdx(isOpen ? null : i)}
                      className="w-full flex items-center justify-between py-3.5 text-left focus:outline-none"
                      data-testid={`button-faq-toggle-${i}`}
                    >
                      <EditableText
                        as="span"
                        noHideDelete
                        className="text-brand-navy font-black text-xs tracking-widest uppercase"
                        path={["hero", "faqs", i, "q"]}
                        value={(heroAny?.faqs?.[i]?.q ?? heroAny?.faqs?.[i]?.question)}
                        placeholder={f.q}
                      />
                      <span className={`w-6 h-6 rounded-full border-2 border-brand-navy flex items-center justify-center flex-shrink-0 ml-3 transition-transform ${isOpen ? "rotate-180" : ""}`}>
                        <ChevronRight className="w-3.5 h-3.5 text-brand-navy rotate-90" />
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.22 }}
                          className="overflow-hidden"
                        >
                          <EditableText
                            as="p"
                            multiline
                            noHideDelete
                            className="text-brand-muted text-xs leading-relaxed pb-3.5"
                            path={["hero", "faqs", i, "a"]}
                            value={(heroAny?.faqs?.[i]?.a ?? heroAny?.faqs?.[i]?.answer)}
                            placeholder={f.a}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  </EditableElement>
                ) };
              })}
              />
            </div>
          </div>
      </div>
    </section>
  );
}

// =============================================================================
// 2b. PRODUCT SHOWCASE — "THE RECIPE" header card + per-product detail cards
// =============================================================================
function ProductShowcaseSection({
  brand, products, offer, generatedContent, typography, reviewsByProduct, sale,
}: {
  brand: Brand | null | undefined;
  products: Product[];
  offer: Offer;
  generatedContent?: GeneratedContent;
  typography: ReturnType<typeof brandTypography>;
  reviewsByProduct?: Record<string, ProductReview[]>;
  sale?: SalesDiscount | null;
}) {
  // Recipe-card headline comes from the lp-showcase Sim workflow
  // (generatedContent.showcase.headline). Fall back to the template's two
  // canonical lines until the workflow returns.
  const v2 = useLpV2();
  const showcaseHeadline = generatedContent?.showcase?.headline?.trim() || "";
  const showcaseLive = !!showcaseHeadline;
  const showcaseLines = showcaseHeadline
    ? showcaseHeadline.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
    : (v2 ? ["Thoughtfully crafted.", "Made for you."] : ["Four handmade products.", "One complete routine."]);
  // Trust lines under the recipe headline also come from the showcase Sim
  // workflow (generatedContent.showcase.subheadlines). Fall back to the
  // template's four canonical lines until the workflow returns.
  const showcaseSubs = (generatedContent?.showcase?.subheadlines ?? [])
    .map((s) => s.trim())
    .filter(Boolean);
  const trustLinesLive = showcaseSubs.length > 0;
  const trustLines = trustLinesLive
    ? showcaseSubs
    : (v2
        ? [
            "Certified handmade in small batches",
            "Packaged and shipped fresh daily",
            "Premium quality ingredients",
            "Crafted with care",
          ]
        : [
            "Certified handmade in small batches",
            "Packaged and shipped fresh daily",
            "100% whole-food ingredients",
            "Ranch-crafted in California",
          ]);
  // Product Context now lives in the BeliefSection "Real ingredients" block
  // (one block per ingredient card), NOT at the top of this showcase section.
  // Only show struck-through retail + discount UI when the attached sale yields
  // a real, lower price. buildOffer leaves retailTotal/discountPercent empty
  // otherwise, so there's no discount to display.
  const hasDiscount = !!offer.retailTotal && !!offer.discountPercent;
  // Highest-resolution product photo per product (avoids tiny thumbnails that
  // happen to sit first in the media gallery). Pulls only from the product's
  // own images — never AI generation.
  const bestImages = useBestProductImages(products);
  // Per-product showcase-card image overrides. Read the whole map ONCE here (not
  // a hook per product, which would break rules-of-hooks inside products.map).
  const cardOverridesMap = useLpMediaOverridesMap();
  return (
    <section className="bg-[#FAFAF7] py-12 md:py-20 px-4 sm:px-6" data-testid="section-product-showcase">
      <div className="space-y-10">
        {/* ============ PART A — "THE RECIPE" header card ============
            Designed to mirror the supplied template: white card with a soft
            border, navy "THE RECIPE" pill straddling the top edge, serif
            headline, dotted divider, a stack of trust lines where the first
            is green and the rest are dark-navy semibold (no check icons),
            and an inline price row (strike retail + big price + green OFF pill). */}
        {/* Exact styling spec supplied by user — colors, sizes, weights, and
            spacing values come straight from the template prompt. */}
        <LpReorderGroup
          groupKey="showcase:blocks"
          items={[
            { key: "showcase:recipe-header-card", node: (
        <EditableElement elementKey="showcase:recipe-header-card" testId="element-showcase-recipe-header">
        <div className="mx-auto max-w-[520px] pt-4">
          <div
            className="relative bg-[#FAF6EE] rounded-[16px] p-8 text-center"
            style={{ border: "2px solid var(--color-brand-deepnavy)", boxShadow: "3px 3px 0px var(--color-brand-deepnavy)" }}
            data-testid="card-showcase-header"
          >
            <EditableText
              as="span"
              className="absolute left-1/2 -translate-x-1/2 inline-block bg-[var(--color-brand-deepnavy)] text-white uppercase rounded-full"
              style={{
                top: "-16px",
                fontSize: "10px",
                fontWeight: 800,
                letterSpacing: "0.2em",
                padding: "6px 16px",
              }}
              elementKey="showcase:eyebrow"
              path={["showcase", "eyebrow"]}
              value={(generatedContent as any)?.showcase?.eyebrow}
              placeholder="THE RECIPE"
              testId="badge-showcase-eyebrow"
            />
            {generatedContent?.loading?.showcase && !showcaseLive && !trustLinesLive ? (
              <SectionLoading label="Generating recipe…" />
            ) : (
              <>
                <h2
                  className="relative text-center text-[var(--color-brand-navy)]"
                  style={{ fontSize: "30px", fontWeight: 900, lineHeight: 1.2, marginBottom: "20px" }}
                  data-testid="text-showcase-headline"
                >
                  <EditableText
                    as="span"
                    className="block"
                    style={{ whiteSpace: "pre-line" }}
                    multiline
                    regenAnchor
                    elementKey="showcase:headline"
                    path={["showcase", "headline"]}
                    value={generatedContent?.showcase?.headline}
                    placeholder={showcaseLines.join("\n")}
                  />
                  {showcaseLive && <LiveBadge floating />}
                </h2>
                <div
                  aria-hidden
                  style={{ borderTop: "1px dashed #C8BFB3", marginBottom: "16px" }}
                />
                <ul>
                  {trustLines.map((text, i) => (
                    <li
                      key={i}
                      className="relative text-center"
                      style={{
                        fontSize: "13px",
                        lineHeight: 1.8,
                        color: i === 0 ? "var(--color-brand-green)" : "var(--color-brand-navy)",
                        fontWeight: i === 0 ? 600 : 800,
                      }}
                      data-testid={`text-showcase-trust-line-${i}`}
                    >
                      <EditableText
                        as="span"
                        path={["showcase", "subheadlines", i]}
                        value={generatedContent?.showcase?.subheadlines?.[i]}
                        placeholder={text}
                      />
                      {trustLinesLive && i === trustLines.length - 1 && (
                        <LiveBadge floating />
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="mt-6 inline-flex items-center justify-center gap-2 flex-wrap">
              {hasDiscount && (
                <span
                  className="line-through"
                  style={{ fontSize: "13px", color: "#6B6560" }}
                  data-testid="text-showcase-price-retail"
                >
                  {ensureDollar(offer.retailTotal)}
                </span>
              )}
              <span
                className="text-[var(--color-brand-deepnavy)]"
                style={{ fontSize: "36px", fontWeight: 900, lineHeight: 1 }}
                data-testid="text-showcase-price-today"
              >
                {ensureDollar(offer.priceToday)}
              </span>
              {hasDiscount && (
                <span
                  className="inline-flex items-center bg-[var(--color-brand-green)] text-white uppercase rounded-full"
                  style={{
                    fontSize: "11px",
                    fontWeight: 800,
                    letterSpacing: "0.1em",
                    padding: "6px 12px",
                  }}
                  data-testid="badge-showcase-discount"
                >
                  {`${offer.discountPercent.replace(/%/g, "")}% OFF`}
                </span>
              )}
            </div>
          </div>
        </div>
        </EditableElement>
            ) },
            /* ============ PART B — per-product detail cards ============
               Flattened into the showcase:blocks group (no separate nested
               product-cards group) so a SINGLE product card can still reorder
               relative to the recipe header / bundle card. A lone card in its own
               nested group had nothing to swap with → permanently dead arrows. */
            ...(products.length === 0
              ? [{ key: "showcase:empty", node: (
          <EditableText
            as="div"
            className="text-center text-sm text-[var(--color-brand-muted)]"
            path={["showcase", "emptyState"]}
            value={(generatedContent as any)?.showcase?.emptyState}
            placeholder="No products in this brand yet."
          />
                ) }]
              : products.map((product) => {
              const images = getProductImages(product);
              const cardSlotKey = `product-card:${product.id}`;
              const heroImgDefault = bestImages[product.id] ?? images[0] ?? null;
              const heroImg = cardOverridesMap[cardSlotKey]?.url ?? heroImgDefault;
              const ingredientRows = getIngredientRows(product);
              const benefitsRaw = (product as any).benefits;
              const benefits: string[] = Array.isArray(benefitsRaw)
                ? benefitsRaw.filter((x: unknown): x is string => typeof x === "string" && x.trim().length > 0).slice(0, 4)
                : [];
              const ingredientNames = ingredientRows.map((r) => r.name);
              const benefitsList: string[] = benefits.length > 0 ? benefits : ingredientNames.slice(0, 4);
              const chipBenefits: string[] = benefits.length > 0 ? benefits : ingredientNames.slice(0, 3);
              const valueRaw = (product as any).comparisonCallout as string | undefined;
              const valueLine = valueRaw && valueRaw.trim().length > 0 ? valueRaw : null;
              const priceText = (product as any).priceText as string | undefined;
              const descriptor = ((product as any).shortDescription as string | undefined)
                || ((product as any).summary as string | undefined)
                || null;

              const productReviews = reviewsByProduct?.[product.id] ?? [];
              const reviewCount = productReviews.length;
              const ratings = productReviews
                .map((r) => (typeof r.rating === "number" ? r.rating : null))
                .filter((x): x is number => x !== null && x > 0);
              const avgRating = ratings.length > 0
                ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10
                : null;
              const firstReview = productReviews[0] ?? null;
              const reviewInitial = firstReview?.reviewer
                ? firstReview.reviewer.trim().charAt(0).toUpperCase()
                : "?";
              const firstReviewRating = typeof firstReview?.rating === "number" && firstReview.rating > 0
                ? Math.min(5, Math.max(1, Math.round(firstReview.rating)))
                : 5;

              return { key: `showcase:product-card-${product.id}`, node: (
                <EditableElement
                  key={product.id}
                  elementKey={`showcase:product-card-${product.id}`}
                  testId={`element-showcase-product-${product.id}`}
                >
                <div
                  className="w-full max-w-[600px] mx-auto rounded-[12px] border border-[#D8CFC3] bg-[#FAF6EE] overflow-hidden flex flex-col"
                  style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
                  data-testid={`card-showcase-product-${product.id}`}
                >
                  {/* Navy header — name + stars/count + retail value */}
                  <div className="bg-[var(--color-brand-navyaccent)] text-white px-5 py-4 space-y-1.5">
                    <div className="flex items-start justify-between gap-3">
                      <h3
                        className={`${typography.headline} uppercase text-base md:text-lg tracking-wide truncate flex-1 min-w-0`}
                        data-testid={`text-showcase-product-name-${product.id}`}
                      >
                        {product.name}
                      </h3>
                      {priceText && (
                        <div className="text-right shrink-0" data-testid={`text-showcase-product-retail-${product.id}`}>
                          <div className={`${typography.headline} text-base md:text-lg leading-none`}>
                            {priceText}
                          </div>
                          <EditableText
                            as="div"
                            className="text-[9px] uppercase tracking-[0.18em] text-white/70 mt-1"
                            path={["showcase", "retailValueLabel"]}
                            value={(generatedContent as any)?.showcase?.retailValueLabel}
                            placeholder="Retail Value"
                          />
                        </div>
                      )}
                    </div>
                    {reviewCount > 0 && (
                      <div className="flex items-center gap-2" data-testid={`row-showcase-product-rating-${product.id}`}>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={`h-3.5 w-3.5 ${
                                avgRating !== null && i < Math.round(avgRating)
                                  ? "fill-[var(--color-brand-coral)] text-[var(--color-brand-coral)]"
                                  : "text-white/30"
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-[11px] text-white/80 font-semibold">
                          {avgRating !== null ? `${avgRating.toFixed(1)} · ` : ""}
                          {reviewCount.toLocaleString()} {reviewCount === 1 ? "review" : "reviews"}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-5 space-y-5">
                    {/* Product image */}
                    <div className="group/lpimg relative w-full aspect-square">
                      <div className="absolute inset-0 rounded-xl overflow-hidden bg-[var(--color-brand-lightborder)]/40">
                        {heroImg ? (
                          <img
                            src={heroImg}
                            alt={product.name}
                            className="absolute inset-0 w-full h-full object-cover"
                            data-testid={`img-showcase-product-${product.id}`}
                          />
                        ) : null}
                      </div>
                      <LpImageActions
                        slotKey={cardSlotKey}
                        productPhotos={images}
                        revertLabel="Use default"
                        revertTitle="Use the default product photo"
                      />
                    </div>

                    {/* Benefit bullets */}
                    {benefitsList.length > 0 && (
                      <ul className="space-y-2" data-testid={`list-showcase-product-benefits-${product.id}`}>
                        {benefitsList.map((b, i) => (
                          <li key={i} className={`${typography.body} flex items-start gap-2 text-sm text-[var(--color-brand-text)]`}>
                            <Check className="h-4 w-4 text-[var(--color-brand-greenaccent)] mt-0.5 shrink-0" />
                            <span className="flex-1 min-w-0">{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Value-pricing line */}
                    {valueLine && (
                      <p
                        className={`${typography.body} italic text-sm text-[#6B6560] leading-relaxed`}
                        data-testid={`text-showcase-product-value-${product.id}`}
                      >
                        {valueLine}
                      </p>
                    )}

                    {/* "What you get" row with struck-through retail price.
                        Label adapts to product count — "Included in kit" only
                        reads right when there's actually a multi-product kit. */}
                    <div className="flex items-center justify-between gap-3 flex-wrap pt-1 border-t border-[#EDE6DA]">
                      <span className="inline-flex items-center gap-1.5 rounded-md bg-[var(--color-brand-greenaccent)] text-white text-[11px] font-black uppercase tracking-wider px-2.5 py-1.5">
                        <Check className="h-3 w-3" />
                        <EditableText
                          as="span"
                          path={["showcase", "kitInclusionLabel"]}
                          value={(generatedContent as any)?.showcase?.kitInclusionLabel}
                          placeholder={products.length > 1 ? "Included in kit" : "What you get"}
                        />
                      </span>
                      {priceText && (
                        hasDiscount && products.length === 1 ? (
                          // Single product (not a bundle): the offer-level discount
                          // IS this item's discount, so surface the discounted price
                          // here too — struck-through retail + the "today" price.
                          <span
                            className="inline-flex items-baseline gap-2"
                            data-testid={`text-showcase-product-price-${product.id}`}
                          >
                            <span className="text-base font-semibold text-[var(--color-brand-coral)] line-through">
                              {priceText}
                            </span>
                            <span
                              className="text-lg font-black text-[var(--color-brand-deepnavy)]"
                              data-testid={`text-showcase-product-price-today-${product.id}`}
                            >
                              {ensureDollar(offer.priceToday)}
                            </span>
                          </span>
                        ) : (
                          // Bundle (multi-product): keep the discount at the bundle
                          // total — show only the struck-through retail per item.
                          <span
                            className={`text-lg font-semibold ${hasDiscount ? "text-[var(--color-brand-coral)] line-through" : "text-[var(--color-brand-text)]"}`}
                            data-testid={`text-showcase-product-price-${product.id}`}
                          >
                            {priceText}
                          </span>
                        )
                      )}
                    </div>

                    {/* Verified review — only if at least one real review exists */}
                    {firstReview && (
                      <div
                        className="rounded-xl border border-[#D8CFC3] bg-white p-4"
                        data-testid={`block-showcase-product-review-${product.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-full bg-[var(--color-brand-navyaccent)] text-white flex items-center justify-center text-sm font-black shrink-0">
                            {reviewInitial}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-bold text-[var(--color-brand-text)]">
                                {firstReview.reviewer || "Verified buyer"}
                              </span>
                              <div className="flex items-center gap-0.5">
                                {Array.from({ length: 5 }).map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`h-3 w-3 ${
                                      i < firstReviewRating
                                        ? "fill-[var(--color-brand-coral)] text-[var(--color-brand-coral)]"
                                        : "text-[#D8CFC3]"
                                    }`}
                                  />
                                ))}
                              </div>
                              <span className="inline-flex items-center gap-1 rounded bg-[var(--color-brand-greenaccent)] text-white text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5">
                                <Check className="h-2.5 w-2.5" />
                                <EditableText
                                  as="span"
                                  path={["showcase", "verifiedLabel"]}
                                  value={(generatedContent as any)?.showcase?.verifiedLabel}
                                  placeholder="Verified"
                                />
                              </span>
                            </div>
                            {firstReview.reviewText && (
                              <p className="text-sm italic text-[var(--color-brand-text)] mt-1.5 leading-relaxed">
                                &ldquo;{firstReview.reviewText}&rdquo;
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Ingredient Facts — only if ingredientsOrFeatures non-empty.
                        Right-column copy is the per-row benefit from the source
                        data; if a row has no benefit we render an em-dash so
                        the table stays visually aligned without inventing text. */}
                    {ingredientRows.length > 0 && (
                      <div
                        className="rounded-xl border-2 border-[var(--color-brand-text)] bg-white overflow-hidden"
                        data-testid={`block-showcase-product-facts-${product.id}`}
                      >
                        <div className="bg-[var(--color-brand-text)] text-white px-4 py-2">
                          <EditableText
                            as="div"
                            className={`${typography.headline} text-base uppercase tracking-wide`}
                            path={["showcase", "ingredientFactsLabel"]}
                            value={(generatedContent as any)?.showcase?.ingredientFactsLabel}
                            placeholder="Ingredient Facts"
                          />
                          {descriptor && (
                            <div className="text-[11px] font-semibold text-white/80 mt-0.5">
                              {descriptor}
                            </div>
                          )}
                        </div>
                        <ul className="divide-y divide-[#D8CFC3]">
                          {ingredientRows.map((row, i) => (
                            <li
                              key={i}
                              className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
                              data-testid={`row-showcase-product-fact-${product.id}-${i}`}
                            >
                              <span className="flex items-center gap-2 min-w-0">
                                <Leaf className="h-3.5 w-3.5 text-[var(--color-brand-greenaccent)] shrink-0" />
                                <span className="font-semibold text-[var(--color-brand-text)] truncate">{row.name}</span>
                              </span>
                              <span className="text-xs text-[#6B6560] text-right shrink-0 max-w-[55%] truncate">
                                {row.benefit ?? "—"}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Bottom chip strip */}
                  {chipBenefits.length > 0 && (
                    <div className="mt-auto bg-[var(--color-brand-deepnavy)] text-[var(--color-brand-cream)] px-5 py-3 flex items-center justify-between gap-2 flex-wrap">
                      {chipBenefits.slice(0, 3).map((c, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1.5 text-[10px] md:text-[11px] uppercase tracking-wider font-semibold min-w-0"
                          data-testid={`chip-showcase-product-${product.id}-${i}`}
                        >
                          <Sparkles className="h-3 w-3 text-[var(--color-brand-coral)] shrink-0" />
                          <span className="truncate">{c}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                </EditableElement>
              ) };
            })),
            /* ============ PART C — bundle / order-summary CTA card ============
               Wrapped in EditableElement so the user can hide/delete/reorder the
               CTA box independently of the product cards. */
            { key: "showcase:bundle", node: (
            <EditableElement elementKey="showcase:bundle-card" testId="element-showcase-bundle">
            <BundleOrderSummaryCard
              brand={brand}
              products={products}
              offer={offer}
              sale={sale ?? null}
              generatedContent={generatedContent}
              typography={typography}
            />
            </EditableElement>
            ) },
          ]}
        />
      </div>
    </section>
  );
}

// =============================================================================
// 2c. BUNDLE / ORDER-SUMMARY CTA CARD — renders after the per-product cards
// =============================================================================
function BundleOrderSummaryCard({
  brand, products, offer, sale, generatedContent, typography,
}: {
  brand: Brand | null | undefined;
  products: Product[];
  offer: Offer;
  sale: SalesDiscount | null;
  generatedContent?: GeneratedContent;
  typography: ReturnType<typeof brandTypography>;
}) {
  // CTA card hero uses the real product photo (not an AI-generated image),
  // falling back to a gradient placeholder when no product photo exists.
  // Always the FIRST image of the FIRST product, so every CTA across the page
  // shows the same product shot.
  const bundlePhotos = getProductImages(products[0] ?? null);
  const heroImgDefault = bundlePhotos[0] ?? null;
  const heroImgOverride = useLpMediaOverride(LP_PRODUCT_SLOT_BUNDLE);
  const heroImg = heroImgOverride ?? heroImgDefault;

  // Parse numeric prices from priceText strings ("$29", "29 USD" → 29).
  const parsePrice = (raw: string | null | undefined): number | null => {
    if (!raw) return null;
    const m = String(raw).replace(/[^\d.]/g, "");
    if (!m) return null;
    const n = parseFloat(m);
    return Number.isFinite(n) ? n : null;
  };
  const productPrices = products.map((p) => parsePrice((p as any).priceText as string | undefined));
  const allPricesKnown = productPrices.every((p) => p !== null);
  const computedRetailTotal = allPricesKnown
    ? (productPrices as number[]).reduce((a, b) => a + b, 0)
    : null;

  const explicitRetail = parsePrice(offer.retailTotal);
  const retailTotal = explicitRetail ?? computedRetailTotal;
  const priceToday = parsePrice(offer.priceToday);
  const discountAmount = retailTotal !== null && priceToday !== null && retailTotal > priceToday
    ? retailTotal - priceToday
    : null;
  const discountPercent = retailTotal !== null && priceToday !== null && retailTotal > 0
    ? Math.round(((retailTotal - priceToday) / retailTotal) * 100)
    : null;
  // Only treat this as a discount when there's a real reduction. Without a sale,
  // retailTotal === priceToday so discountAmount is null — show prices plainly,
  // no struck-through retail.
  const hasDiscount = discountAmount !== null;

  const fmt = (n: number) => `$${n.toFixed(2).replace(/\.00$/, "")}`;

  // Feature pills — pull from brand approved phrases, fall back to sensible
  // DTC defaults so the card always renders four pills.
  const approved = Array.isArray((brand as any)?.approvedPhrases)
    ? ((brand as any).approvedPhrases as unknown[]).filter(
        (x): x is string => typeof x === "string" && x.trim().length > 0,
      )
    : [];
  const FALLBACK_PILLS = ["Free shipping", "Money-back guarantee", "Made fresh", "Cancel anytime"];
  const pillLabels = approved.length >= 4 ? approved.slice(0, 4) : FALLBACK_PILLS;
  const pillIcons = [Truck, ShieldCheck, Leaf, Award];

  const isMulti = products.length > 1;
  const ctaText = generatedContent?.cta?.cta_text
    || generatedContent?.hero?.cta_text
    || (isMulti ? "Get my kit" : "Get yours");
  const checkoutUrl = offer.checkoutUrl;
  const guaranteeDays = offer.guaranteeDays ?? 30;

  return (
    <div
      className="w-full max-w-[600px] mx-auto rounded-[12px] border border-[#D8CFC3] bg-[#FAF6EE] overflow-hidden shadow-sm"
      data-testid="card-bundle-order-summary"
    >
      {/* Hero image — image only, no overlaid headline (intentional). Rendered
          at its natural height so the full graphic (title + product) is shown
          without cropping. */}
      <div className="group/lpimg relative bg-[var(--color-brand-lightborder)]/40">
        {heroImg ? (
          <img
            src={heroImg}
            alt="Bundle"
            className="block w-full h-auto"
            data-testid="img-bundle-hero"
          />
        ) : (
          <div className="aspect-[16/9] bg-gradient-to-br from-[var(--color-brand-green)]/30 to-[var(--color-brand-navyaccent)]/40" />
        )}
        <LpImageActions
          slotKey={LP_PRODUCT_SLOT_BUNDLE}
          productPhotos={bundlePhotos}
          revertLabel="Use default"
          revertTitle="Use the default product photo"
        />
      </div>

      {/* Feature pills row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-5 py-4 bg-[#FAF6EE] border-b border-[#D8CFC3]">
        {pillLabels.map((label, i) => {
          const Icon = pillIcons[i] ?? Sparkles;
          return (
            <div
              key={i}
              className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[var(--color-brand-navyaccent)] min-w-0"
              data-testid={`pill-bundle-${i}`}
            >
              <Icon className="h-3.5 w-3.5 text-[var(--color-brand-green)] shrink-0" />
              <span className="truncate">{label}</span>
            </div>
          );
        })}
      </div>

      {/* Order summary header */}
      <EditableText
        as="div"
        className={`${typography.eyebrow} bg-[var(--color-brand-navyaccent)] text-white px-5 py-2.5 text-center`}
        path={["showcase", "orderSummaryLabel"]}
        value={(generatedContent as any)?.showcase?.orderSummaryLabel}
        placeholder="Order Summary"
      />

      {/* Line items — one per product */}
      <ul className="divide-y divide-[#EDE6DA]" data-testid="list-bundle-line-items">
        {products.map((p) => {
          const price = (p as any).priceText as string | undefined;
          return (
            <li
              key={p.id}
              className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm"
              data-testid={`row-bundle-line-${p.id}`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <Check className="h-3.5 w-3.5 text-[var(--color-brand-greenaccent)] shrink-0" />
                <span className="text-[var(--color-brand-text)] truncate">{p.name}</span>
              </span>
              {price && (
                <span className={`text-sm shrink-0 ${hasDiscount ? "text-[var(--color-brand-coral)] line-through" : "text-[#6B6560]"}`}>{price}</span>
              )}
            </li>
          );
        })}
        <li className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm">
          <span className="flex items-center gap-2 min-w-0">
            <Truck className="h-3.5 w-3.5 text-[var(--color-brand-greenaccent)] shrink-0" />
            <EditableText
              as="span"
              className="text-[var(--color-brand-text)]"
              path={["showcase", "freeShippingLabel"]}
              value={(generatedContent as any)?.showcase?.freeShippingLabel}
              placeholder="Free U.S. shipping"
            />
          </span>
          <EditableText
            as="span"
            className="text-xs font-bold uppercase tracking-wider text-[var(--color-brand-greenaccent)] shrink-0"
            path={["showcase", "freeShippingValue"]}
            value={(generatedContent as any)?.showcase?.freeShippingValue}
            placeholder="Free"
          />
        </li>
      </ul>

      {/* Totals block */}
      <div className="px-5 py-4 space-y-2 bg-[#FAF6EE] border-t border-[#EDE6DA]">
        {hasDiscount && retailTotal !== null && (
          <div className="flex items-center justify-between text-sm">
            <EditableText
              as="span"
              className="text-[#6B6560]"
              path={["showcase", "retailTotalLabel"]}
              value={(generatedContent as any)?.showcase?.retailTotalLabel}
              placeholder="Retail total"
            />
            <span className="text-[#6B6560] line-through" data-testid="text-bundle-retail-total">
              {fmt(retailTotal)}
            </span>
          </div>
        )}
        {discountAmount !== null && discountPercent !== null && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-[var(--color-brand-greenaccent)] font-semibold">
              {sale?.name ? `${sale.name}` : (isMulti ? "Bundle savings" : "Your discount")}
              {discountPercent > 0 ? ` (${discountPercent}% OFF)` : ""}
            </span>
            <span className="text-[var(--color-brand-greenaccent)] font-semibold" data-testid="text-bundle-discount">
              −{fmt(discountAmount)}
            </span>
          </div>
        )}
        {priceToday !== null && (
          <div className="flex items-end justify-between pt-2 mt-1 border-t border-[#D8CFC3]">
            <div>
              <EditableText
                as="div"
                className={`${typography.eyebrow} text-[var(--color-brand-navyaccent)]`}
                path={["showcase", "youPayTodayLabel"]}
                value={(generatedContent as any)?.showcase?.youPayTodayLabel}
                placeholder="You Pay Today"
              />
              <EditableText
                as="div"
                multiline
                className="text-[11px] text-[#6B6560] mt-0.5"
                path={["showcase", "supplyLine"]}
                value={(generatedContent as any)?.showcase?.supplyLine}
                placeholder="45-day supply · Less than 65¢/day"
              />
            </div>
            <span
              className={`${typography.headline} text-3xl text-[var(--color-brand-navyaccent)] leading-none`}
              data-testid="text-bundle-price-today"
            >
              {fmt(priceToday)}
            </span>
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="px-5 pb-5 pt-1 bg-[#FAF6EE]">
        <a
          href={checkoutUrl || undefined}
          data-lp-cta
          className="block w-full text-center bg-[var(--color-brand-greenaccent)] hover:bg-[#236340] text-white rounded-xl px-5 py-4 transition-colors"
          data-testid="link-bundle-cta"
        >
          <EditableText
            as="div"
            noHideDelete
            className="uppercase"
            style={{ fontSize: "18px", fontWeight: 800, letterSpacing: "0.04em", lineHeight: 1.1 }}
            path={["showcase", "ctaLabel"]}
            value={(generatedContent as any)?.showcase?.ctaLabel}
            placeholder={priceToday !== null
              ? `GET MY KIT FOR ${fmt(priceToday)} TODAY`
              : ctaText}
          />
          <EditableText
            as="div"
            multiline
            className="mt-1.5 text-white/90"
            style={{ fontSize: "12px", fontWeight: 400 }}
            path={["showcase", "ctaSubtext"]}
            value={(generatedContent as any)?.showcase?.ctaSubtext}
            placeholder={`Free shipping · ${guaranteeDays}-Day money-back guarantee`}
          />
        </a>

        {/* Bottom checklist row */}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-[var(--color-brand-deepnavy)]">
          {[
            "$39 Refill ships in 45 days",
            "Skip, pause or cancel anytime",
            `${guaranteeDays}-Day Money-Back Guarantee`,
            "FREE avg 3-day shipping today",
          ].map((item, i) => (
            <span key={i} className="inline-flex items-center gap-1.5">
              <Check className="h-3 w-3 text-[var(--color-brand-greenaccent)] shrink-0" />
              <EditableText
                as="span"
                path={["showcase", "bundleTerms", i]}
                value={(generatedContent as any)?.showcase?.bundleTerms?.[i]}
                placeholder={item}
              />
            </span>
          ))}
        </div>
      </div>

    </div>
  );
}

// =============================================================================
// 3. LEAD-IN
// =============================================================================
function LeadinSection({ generatedContent, typography }: { generatedContent?: GeneratedContent; typography: ReturnType<typeof brandTypography> }) {
  const text = generatedContent?.leadin;
  return (
    <section className="bg-[var(--color-brand-cream)] py-12 md:py-20 px-4 sm:px-6 border-y border-[#D8CFC3]">
      <div className="max-w-4xl mx-auto">
        <div
          className="bg-white border border-[#D8CFC3] rounded-2xl shadow-sm p-8 sm:p-12 text-center"
          style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
        >
          {text ? (
            <h2 className={`relative ${typography.headline} text-[26px] md:text-[32px] leading-[1.15] text-[var(--color-brand-text)]`}>
              {text}
              <LiveBadge floating />
            </h2>
          ) : (
            <div
              className="flex items-center justify-center py-6 text-[var(--color-brand-text)]/50"
              data-testid="loading-leadin"
              aria-label="Generating lead-in"
            >
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// 5. VILLAIN STATIONS
// =============================================================================
function StationsSection({ generatedContent, typography }: { generatedContent?: GeneratedContent; typography: ReturnType<typeof brandTypography> }) {
  const data = generatedContent?.stations;
  const stations = data?.stations && data.stations.length > 0 ? data.stations : null;
  const sectionLabel = data?.section_label;
  const sectionHeadline = data?.section_headline;
  const loopQuote = data?.loop_quote;

  // No fallback content — render nothing until the Sim workflow returns
  // real stations data for this brand.
  if (!stations && !sectionHeadline && !loopQuote) {
    return null;
  }

  return (
    <section className="bg-[var(--color-brand-deepnavy)] text-[var(--color-brand-cream)] py-12 md:py-20 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        {(sectionLabel || sectionHeadline) && (
          <div className="text-center mb-14">
            {sectionLabel && (
              <div className={`${typography.eyebrow} text-[var(--color-brand-coral)] mb-3`}>
                {sectionLabel}
              </div>
            )}
            {sectionHeadline && (
              <h2 className={`${typography.headline} text-[26px] md:text-[32px] uppercase text-[var(--color-brand-cream)]`} data-testid="text-stations-headline">
                {sectionHeadline}
              </h2>
            )}
          </div>
        )}
        {stations && (
          <div className="relative grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-3">
            {stations.length > 1 && (
              <div aria-hidden className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-[var(--color-brand-coral)]/40" />
            )}
            {stations.map((s, i) => (
              <div
                key={i}
                className="relative bg-[var(--color-brand-text)] rounded-lg p-6 border border-[var(--color-brand-lightborder)]/30"
                data-testid={`card-station-${i}`}
              >
                <div className={`${typography.headline} relative h-16 w-16 rounded-full bg-[var(--color-brand-coral)] text-[var(--color-brand-deepnavy)] text-2xl flex items-center justify-center mx-auto -mt-14 mb-4 border-4 border-[var(--color-brand-deepnavy)] shadow-lg`}>
                  {s.num ?? i + 1}
                </div>
                <h3 className={`${typography.headline} text-lg uppercase tracking-wider mb-2 text-center text-[var(--color-brand-cream)]`}>
                  {s.label}
                </h3>
                <p className={`${typography.body} text-sm text-[var(--color-brand-cream)]/80 leading-relaxed text-center`}>
                  {s.sub}
                </p>
              </div>
            ))}
          </div>
        )}
        {loopQuote && (
          <div className="mt-14 text-center">
            <blockquote className={`${typography.body} text-xl sm:text-2xl italic font-semibold text-[var(--color-brand-coral)] max-w-3xl mx-auto leading-relaxed`}>
              {loopQuote}
            </blockquote>
          </div>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// 6. INGREDIENT CARDS
// =============================================================================
function IngredientsSection({ products, typography }: { products: Product[]; typography: ReturnType<typeof brandTypography> }) {
  const product = products[0] ?? null;
  const ingredients = getIngredients(product);
  const isLive = ingredients.length > 0;

  if (!isLive) {
    return (
      <section className="bg-[var(--color-brand-cream)] py-12 md:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <StaticBadge label="ingredients — needs product.ingredientsOrFeatures" />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[var(--color-brand-cream)] py-12 md:py-20 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className={`${typography.eyebrow} text-[var(--color-brand-coral)] mb-2`}>The recipe card</div>
          <h2 className={`relative ${typography.headline} text-[26px] md:text-[32px] text-[var(--color-brand-text)]`}>
            What's inside the kit
            <LiveBadge floating />
          </h2>
          <p className={`${typography.body} mt-3 text-[13px] md:text-[14px] text-[#6B6560] leading-relaxed`}>Every ingredient sourced with intention. Nothing synthetic.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ingredients.map((ing) => (
            <div
              key={ing}
              className="bg-white rounded-xl border border-[#D8CFC3] overflow-hidden"
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
            >
              <div className="relative aspect-square bg-[var(--color-brand-cream)]/60 flex items-center justify-center">
                <Leaf className="h-12 w-12 text-[var(--color-brand-green)]/50" />
                <div className="absolute top-2 right-2"><StaticBadge label="IMG" /></div>
              </div>
              <div className="p-4">
                <h3 className={`${typography.headline} text-[var(--color-brand-text)] mb-1`}>{ing}</h3>
                <p className={`${typography.body} text-xs text-[var(--color-brand-muted)] leading-relaxed`}>
                  [Ingredient description]
                  <StaticBadge label="COPY" />
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// 7. CLOSING CTA + ORDER SUMMARY
// =============================================================================
function ClosingCtaSection({
  products, sale, offer, generatedContent, typography,
}: {
  products: Product[];
  sale?: SalesDiscount | null;
  offer: Offer;
  generatedContent?: GeneratedContent;
  typography: ReturnType<typeof brandTypography>;
}) {
  const product = products[0] ?? null;
  const images = getProductImages(product);
  const img = images[0] ?? null;
  const overrideImg = useLpMediaOverride(LP_PRODUCT_SLOT_CLOSING_CTA);
  const displayImg = overrideImg ?? img;
  const receiptItems = buildReceiptItems(products);
  const cta = generatedContent?.cta;
  const ctaText = cta?.cta_text || "[CTA button text]";

  return (
    <section className="bg-[var(--color-brand-cream)] py-12 md:py-20 px-4 sm:px-6 border-t border-[#D8CFC3]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h2 className={`relative ${typography.headline} text-[26px] md:text-[32px] text-[var(--color-brand-text)]`}>
            {cta?.headline || "[Closing headline]"}
            {cta?.headline ? <LiveBadge floating /> : <span className="ml-2 align-middle inline-block"><StaticBadge /></span>}
          </h2>
        </div>

        <div
          className="rounded-2xl border border-[#D8CFC3] overflow-hidden bg-white"
          style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
        >
          <div className="grid md:grid-cols-2">
            {/* product */}
            <div className="group/lpimg bg-[var(--color-brand-cream)]/60 aspect-square md:aspect-auto relative">
              {displayImg ? (
                <img src={displayImg} alt={product?.name ?? ""} className="w-full h-full object-cover" />
              ) : (
                <div className={`absolute inset-0 flex items-center justify-center text-[var(--color-brand-muted)] ${typography.body}`}>No image</div>
              )}
              <div className="absolute top-3 left-3">{displayImg ? <LiveBadge /> : <StaticBadge />}</div>
              <LpImageActions
                slotKey={LP_PRODUCT_SLOT_CLOSING_CTA}
                productPhotos={images}
                revertLabel="Use default"
                revertTitle="Use the default product photo"
              />
            </div>

            {/* receipt-style summary */}
            <div className="p-6 sm:p-8 space-y-5">
              {offer.guaranteeChips.length > 0 ? (
                <div className={`grid grid-cols-3 gap-2 text-[11px] uppercase tracking-wider text-[var(--color-brand-muted)] ${typography.eyebrow}`}>
                  {offer.guaranteeChips.slice(0, 3).map((t, i) => {
                    const icons = [
                      <Truck key="t" className="h-4 w-4" />,
                      <Leaf key="l" className="h-4 w-4" />,
                      <Award key="a" className="h-4 w-4" />,
                    ];
                    return (
                      <div key={`${i}-${t}`} className="flex flex-col items-center gap-1 rounded-md border border-[var(--color-brand-lightborder)] bg-white py-2 px-1 text-center">
                        <span className="text-[var(--color-brand-text)]">{icons[i % icons.length]}</span>
                        <span className="leading-tight">{t}</span>
                        <LiveBadge />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex justify-center"><StaticBadge label="feature chips — needs sale guarantee/refill" /></div>
              )}

              <div className="rounded-lg border border-dashed border-[var(--color-brand-lightborder)] bg-[var(--color-brand-cream)]/40 p-4 space-y-2" data-testid="card-order-summary">
                <div className={`${typography.eyebrow} text-[var(--color-brand-muted)] flex items-center gap-2`}>
                  Order Summary
                </div>
                <div className={`space-y-1.5 text-sm ${typography.body}`}>
                  {receiptItems.length === 0 ? (
                    <div className="text-[var(--color-brand-muted)] italic">No items in kit</div>
                  ) : (
                    receiptItems.map((it, i) => (
                      <div
                        key={i}
                        className="flex justify-between text-[var(--color-brand-text)]"
                        data-testid={`row-receipt-${i}`}
                      >
                        <span>{it.name}</span>
                        <span className="font-semibold">{it.retail || it.label}</span>
                      </div>
                    ))
                  )}
                </div>
                <div className={`pt-2 border-t border-dashed border-[var(--color-brand-lightborder)] space-y-1.5 text-sm ${typography.body}`}>
                  {offer.retailTotal && (
                    <div className="flex justify-between text-[var(--color-brand-muted)]">
                      <span>Retail</span>
                      <span className="line-through">{ensureDollar(offer.retailTotal)}</span>
                    </div>
                  )}
                  {offer.discountAmount && offer.discountPercent && (
                    <div className="flex justify-between text-[var(--color-brand-green)] font-semibold">
                      <span>You save</span>
                      <span>{ensureDollar(offer.discountAmount)} ({offer.discountPercent})</span>
                    </div>
                  )}
                  <div className={`${typography.headline} flex justify-between text-[var(--color-brand-text)] text-lg pt-1`}>
                    <span>Total</span>
                    <span>{ensureDollar(offer.priceToday)}</span>
                  </div>
                  {offer.shipping && <div className="text-xs text-[var(--color-brand-muted)]">{offer.shipping}</div>}
                </div>
                {sale?.promoCode && (
                  <div className="text-xs text-[var(--color-brand-green)] font-semibold">
                    Promo applied: {sale.promoCode} <LiveBadge />
                  </div>
                )}
              </div>

              <Button
                asChild
                size="lg"
                data-lp-cta
                className={`w-full bg-[var(--color-brand-navy)] hover:bg-[var(--color-brand-deepnavy)] text-white hover:text-white font-sans font-black uppercase tracking-widest text-base rounded-xl py-3.5 md:py-4 px-6 md:px-8 h-auto`}
                data-testid="button-closing-cta"
              >
                <a href={offer.checkoutUrl || undefined}>
                  {ctaText}
                  {cta?.cta_text ? <span className="ml-2"><LiveBadge /></span> : <span className="ml-2"><StaticBadge /></span>}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// 8. GUARANTEE STRIP — driven by offer.guaranteeChips
// =============================================================================
function GuaranteeStrip({ offer, typography }: { offer: Offer; typography: ReturnType<typeof brandTypography> }) {
  const icons = [
    <Truck key="t" className="h-6 w-6" />,
    <ShieldCheck key="s" className="h-6 w-6" />,
    <Leaf key="l" className="h-6 w-6" />,
    <Award key="a" className="h-6 w-6" />,
  ];
  return (
    <section className="bg-[var(--color-brand-cream)]/70 py-12 md:py-20 px-4 sm:px-6 border-t border-[#D8CFC3]">
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
        {offer.guaranteeChips.map((chip, i) => (
          <div
            key={i}
            className="flex flex-col items-center text-center gap-1"
            data-testid={`chip-guarantee-${i}`}
          >
            <div className="text-[var(--color-brand-coral)]">{icons[i % icons.length]}</div>
            <div className={`${typography.body} text-[13px] md:text-[14px] font-semibold text-[var(--color-brand-text)] leading-relaxed`}>{chip}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// =============================================================================
// 9. STICKY NAV — brand.logoUrl + brand.promise tagline
// =============================================================================
function StickyNav({ brand, typography: _typography }: { brand: Brand | null | undefined; typography: ReturnType<typeof brandTypography> }) {
  const logoUrl: string | null = (brand as any)?.logoUrl ?? (brand as any)?.adgenLogoUrl ?? null;
  // Prefer a real brand homepage so Sign-In and cart go somewhere real.
  // Fall back to an in-page anchor when no brand URL is configured.
  const brandSite: string =
    (brand as any)?.websiteUrl
    ?? (brand as any)?.url
    ?? (brand as any)?.brandUrl
    ?? "#";
  const cartHref = brandSite === "#" ? "#cart" : brandSite;

  return (
    <nav
      className="sticky top-0 z-40 bg-brand-navy px-4 sm:px-6 shadow-md transition-all"
      style={{ paddingTop: 18, paddingBottom: 18 }}
      data-testid="nav-sticky"
    >
      <div className="max-w-6xl mx-auto relative flex items-center justify-between">
        {/* Spacer to balance the right cluster */}
        <div className="w-24 sm:w-32" />
        {/* Centered logo + brand wordmark */}
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 sm:gap-3">
          {logoUrl && (
            <img
              src={logoUrl}
              alt={brand?.name ?? "Brand"}
              className="h-9 sm:h-11 w-auto object-contain"
              data-testid="img-nav-logo"
            />
          )}
          <span
            className="text-white uppercase tracking-[0.18em] text-sm sm:text-base font-black whitespace-nowrap"
            data-testid="text-nav-brand-name"
          >
            {brand?.name ?? "Brand"}
          </span>
        </div>
        {/* Right cluster — cart link with coral count badge */}
        <div className="flex gap-3 sm:gap-6 items-center text-white text-xs sm:text-sm font-bold uppercase tracking-widest">
          <a
            href={cartHref}
            aria-label="Cart"
            className="relative hover:text-brand-coral transition-colors"
            data-testid="link-nav-cart"
          >
            <ShoppingBag className="w-6 h-6" strokeWidth={1.75} />
            <span
              className="absolute -top-1.5 -right-1.5 bg-brand-coral text-white text-[10px] font-black w-4 h-4 rounded-full flex items-center justify-center leading-none"
              data-testid="badge-nav-cart-count"
            >
              1
            </span>
          </a>
        </div>
      </div>
    </nav>
  );
}

// =============================================================================
// 10. FOOTER — brand.promise + brand.name
// =============================================================================
function BrandFooter({ brand, typography }: { brand: Brand | null | undefined; typography: ReturnType<typeof brandTypography> }) {
  const tagline: string | null = (brand as any)?.promise ?? null;
  return (
    <footer className="bg-[var(--color-brand-deepnavy)] text-[var(--color-brand-cream)]/80 py-12 border-t border-[var(--color-brand-lightborder)]/30">
      <div className="max-w-6xl mx-auto px-6 text-center space-y-3">
        <div className={`${typography.headline} text-lg text-[var(--color-brand-cream)]`} data-testid="text-footer-brand">
          {brand?.name ?? "Brand"}
        </div>
        {tagline ? (
          <div className={`${typography.body} text-sm text-[var(--color-brand-cream)]/70 max-w-xl mx-auto`} data-testid="text-footer-tagline">
            {tagline}
          </div>
        ) : (
          <div className={`${typography.body} text-sm text-[var(--color-brand-cream)]/50 italic`}>
            <StaticBadge label="brand.promise missing" />
          </div>
        )}
        <div className={`${typography.body} text-xs text-[var(--color-brand-cream)]/40 pt-4`}>
          © {new Date().getFullYear()} {brand?.name ?? "Brand"}. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

// =============================================================================
// 11. SOCIAL PROOF MARQUEE — scrolling strip between announcement & sticky nav
// =============================================================================
function SocialProofMarquee({ brand, products }: { brand: Brand | null | undefined; products: Product[] }) {
  const items: string[] = [];
  const creds = (brand as any)?.credentials;
  if (Array.isArray(creds)) {
    for (const c of creds) if (typeof c === "string" && c.trim()) items.push(c.trim());
  } else if (typeof creds === "string") {
    creds.split(/[,•·]/).map((s) => s.trim()).filter(Boolean).forEach((s) => items.push(s));
  }
  const reviewCount = (products[0] as any)?.reviewCount;
  if (typeof reviewCount === "number" && reviewCount > 0) {
    items.push(`${reviewCount.toLocaleString()}+ 5-star reviews`);
  }
  if (items.length === 0) return null;
  const repeated = Array.from({ length: 6 }, () => items).flat();
  return (
    <div className="relative bg-[var(--color-brand-deepnavy)] text-white overflow-hidden">
      <div className="absolute top-1 left-2 z-10">
        <LiveBadge />
      </div>
      <div className="py-2.5 flex whitespace-nowrap animate-[marqueeSP_40s_linear_infinite]">
        {repeated.map((t, i) => (
          <span key={i} className="mx-6 text-xs font-black uppercase tracking-widest font-sans flex items-center gap-6">
            {t}
            <span className="text-[var(--color-brand-coral)]">·</span>
          </span>
        ))}
      </div>
      <style>{`@keyframes marqueeSP { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

// =============================================================================
// 12. PSA WARNING — single yellow advisory card on deep-navy bg (static)
// =============================================================================
// ProblemLoopSection — direct adoption of the FactoryStationsSection
// template (framer-motion based). Live Sim payload
// (generatedContent.stations) hydrates section_label, section_headline,
// the 4 station rows (num/label/sub), and loop_quote. The 4-step palette
// (green → yellow → orange → deep red), factory SVG, sludge pipes,
// auto-cycling active-station pulse+shake, ↻ repeat row, and yellow
// hazard-tape warning sign all come straight from the template.

// Animated smoke puff rising from a chimney
function FactorySmoke({ cx, delay }: { cx: number; delay: number }) {
  return (
    <motion.g
      animate={{ y: [0, -36], opacity: [0.7, 0] }}
      transition={{ duration: 2.2, delay, repeat: Infinity, ease: "easeOut", repeatDelay: 0 }}
    >
      <circle cx={cx} cy={-4} r={8} fill="rgba(120,120,100,0.65)" />
    </motion.g>
  );
}

// SVG factory building with windows, door, 3 chimneys + animated smoke
function FactoryHeader() {
  return (
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
      <svg
        viewBox="0 0 220 68"
        style={{ width: "100%", maxWidth: 300, overflow: "visible" }}
        fill="none"
        data-testid="img-problem-loop-factory"
      >
        <rect x="8" y="36" width="204" height="30" fill="#181818" rx="2" />
        <rect x="18"  y="44" width="11" height="14" fill="#d4660a" opacity="0.75" rx="1" />
        <rect x="43"  y="44" width="11" height="14" fill="#a3c463" opacity="0.65" rx="1" />
        <rect x="78"  y="44" width="11" height="14" fill="#c8b400" opacity="0.6"  rx="1" />
        <rect x="113" y="44" width="11" height="14" fill="#d4660a" opacity="0.7"  rx="1" />
        <rect x="148" y="44" width="11" height="14" fill="#a3c463" opacity="0.55" rx="1" />
        <rect x="183" y="44" width="11" height="14" fill="#8B3A0A" opacity="0.65" rx="1" />
        <rect x="101" y="50" width="18" height="16" fill="#0f0f0f" rx="1" />
        <rect x="32" y="10" width="18" height="28" fill="#1e1e1e" rx="1" />
        <rect x="28" y="8"  width="26" height="5"  fill="#2d2d2d" rx="1" />
        <rect x="95" y="16" width="15" height="22" fill="#1e1e1e" rx="1" />
        <rect x="91" y="14" width="23" height="5"  fill="#2d2d2d" rx="1" />
        <rect x="158" y="6"  width="19" height="32" fill="#1e1e1e" rx="1" />
        <rect x="154" y="4"  width="27" height="5"  fill="#2d2d2d" rx="1" />
        <rect x="66"  y="44" width="5" height="22" fill="#2a2a2a" />
        <rect x="138" y="44" width="5" height="22" fill="#2a2a2a" />
        <FactorySmoke cx={41}  delay={0}   />
        <FactorySmoke cx={41}  delay={0.8} />
        <FactorySmoke cx={41}  delay={1.6} />
        <FactorySmoke cx={102} delay={0.35} />
        <FactorySmoke cx={102} delay={1.15} />
        <FactorySmoke cx={102} delay={1.9}  />
        <FactorySmoke cx={167} delay={0.18} />
        <FactorySmoke cx={167} delay={1.0}  />
        <FactorySmoke cx={167} delay={1.8}  />
      </svg>
    </div>
  );
}

// Sludge pipe connecting two stations — flowing colored blobs cascade
// from the current station's accent into the next's.
function SludgePipe({ fromColor, toColor, testId }: { fromColor: string; toColor: string; testId?: string }) {
  const PIPE_H = 60;
  const PIPE_W = 34;
  const WALL_W = 5;
  return (
    <div
      style={{ display: "flex", justifyContent: "center", height: PIPE_H, position: "relative" }}
      data-testid={testId}
    >
      <div style={{ position: "relative", width: PIPE_W, height: PIPE_H }}>
        <div style={{ position: "absolute", top: 0, left: -7, right: -7, height: 7, background: "linear-gradient(to bottom, #5a5a5a, #333)", borderRadius: "3px 3px 0 0", zIndex: 3 }} />
        <div style={{ position: "absolute", bottom: 0, left: -7, right: -7, height: 7, background: "linear-gradient(to top, #5a5a5a, #333)", borderRadius: "0 0 3px 3px", zIndex: 3 }} />
        <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: WALL_W, background: "linear-gradient(to right, #525252, #2c2c2c)", zIndex: 2 }} />
        <div style={{ position: "absolute", top: 0, bottom: 0, right: 0, width: WALL_W, background: "linear-gradient(to left, #525252, #2c2c2c)", zIndex: 2 }} />
        <div style={{ position: "absolute", top: 0, bottom: 0, left: WALL_W, right: WALL_W, background: "#0c0900", overflow: "hidden", zIndex: 1 }}>
          {[0, 0.45, 0.9].map((delay, i) => (
            <motion.div
              key={i}
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                height: 22,
                background: `linear-gradient(to bottom, ${fromColor}ee, ${toColor}88)`,
                borderRadius: "0 0 45% 45%",
                filter: "blur(0.5px)",
              }}
              animate={{ top: [-22, PIPE_H + 6] }}
              transition={{ duration: 1.1, delay, repeat: Infinity, ease: "easeIn", repeatDelay: 0 }}
            />
          ))}
        </div>
        <div style={{ position: "absolute", top: 12,    left:  -11, width: 6, height: 6, borderRadius: "50%", background: "radial-gradient(circle at 35% 35%, #666, #222)", zIndex: 5 }} />
        <div style={{ position: "absolute", bottom: 12, left:  -11, width: 6, height: 6, borderRadius: "50%", background: "radial-gradient(circle at 35% 35%, #666, #222)", zIndex: 5 }} />
        <div style={{ position: "absolute", top: 12,    right: -11, width: 6, height: 6, borderRadius: "50%", background: "radial-gradient(circle at 35% 35%, #666, #222)", zIndex: 5 }} />
        <div style={{ position: "absolute", bottom: 12, right: -11, width: 6, height: 6, borderRadius: "50%", background: "radial-gradient(circle at 35% 35%, #666, #222)", zIndex: 5 }} />
      </div>
    </div>
  );
}

function ProblemLoopSection({
  generatedContent,
  typography,
}: {
  generatedContent?: GeneratedContent;
  typography: ReturnType<typeof brandTypography>;
}) {
  // Live data from the lp-stations-copy Sim workflow.
  const stationsPayload = generatedContent?.stations;
  const stationsArr = stationsPayload?.stations;
  const hasStations =
    !!stationsPayload &&
    Array.isArray(stationsArr) &&
    stationsArr.length > 0;
  const sectionLabel =
    (stationsPayload?.section_label && stationsPayload.section_label.trim()) || "";
  const loopQuote =
    (stationsPayload?.loop_quote && stationsPayload.loop_quote.trim()) || "";
  // The "verdict" pull-quote takes precedence in the yellow warning sign;
  // falls back to the stations loop_quote, then to nothing (sign hidden).
  const verdictQuote =
    (generatedContent?.quotes?.verdict?.quote && generatedContent.quotes.verdict.quote.trim()) || "";
  const warningQuote = verdictQuote || loopQuote;
  const quotesLoading = !!generatedContent?.loading?.quotes;
  const stations: Array<{ num: string; label: string; sub: string }> = (stationsArr ?? []).map(
    (s, i) => ({
      num:
        typeof s?.num === "number"
          ? String(s.num).padStart(2, "0")
          : (typeof s?.num === "string" && s.num.trim())
            ? s.num
            : String(i + 1).padStart(2, "0"),
      label: typeof s?.label === "string" ? s.label : "",
      sub: typeof s?.sub === "string" ? s.sub : "",
    }),
  );

  // Template's 4-step palette (green → yellow → orange → deep red).
  // Pulse/permanent glow values bake the cascade ramp.
  const LOOP_PALETTE = [
    {
      accentColor: "#a3c463",
      baseBg: "#0b1408",
      borderColor: "rgba(130,180,50,0.3)",
      permanentGlow: "0 0 12px rgba(130,180,50,0.3), 0 0 28px rgba(130,180,50,0.12)",
      pulseGlow: "0 0 24px rgba(130,180,50,0.7), 0 0 52px rgba(130,180,50,0.3)",
    },
    {
      accentColor: "#c8b400",
      baseBg: "#181400",
      borderColor: "rgba(200,180,0,0.35)",
      permanentGlow: "0 0 18px rgba(200,180,0,0.4), 0 0 40px rgba(200,180,0,0.18)",
      pulseGlow: "0 0 34px rgba(200,180,0,0.8), 0 0 70px rgba(200,180,0,0.35)",
    },
    {
      accentColor: "#d4660a",
      baseBg: "#1a0d00",
      borderColor: "rgba(212,100,10,0.4)",
      permanentGlow: "0 0 26px rgba(212,100,10,0.5), 0 0 56px rgba(212,100,10,0.22)",
      pulseGlow: "0 0 44px rgba(212,100,10,0.9), 0 0 90px rgba(212,100,10,0.4)",
    },
    {
      accentColor: "#8B3A0A",
      baseBg: "#1a0800",
      borderColor: "rgba(139,58,10,0.55)",
      permanentGlow: "0 0 36px rgba(139,58,10,0.65), 0 0 72px rgba(139,58,10,0.32)",
      pulseGlow: "0 0 56px rgba(180,80,20,0.95), 0 0 110px rgba(139,58,10,0.5)",
    },
  ] as const;

  // Auto-cycle active station while the stations container is in view.
  const loopRef = useRef<HTMLDivElement>(null);
  const loopInView = useInView(loopRef, { once: false, margin: "-100px" });
  const [activeLoopStep, setActiveLoopStep] = useState(0);
  useEffect(() => {
    if (!loopInView || stations.length === 0) return;
    const stepDurations = [2200, 1400, 1400, 1400];
    let timer: ReturnType<typeof setTimeout>;
    const cycle = (current: number) => {
      const next = (current + 1) % stations.length;
      timer = setTimeout(() => {
        setActiveLoopStep(next);
        cycle(next);
      }, stepDurations[next % stepDurations.length]);
    };
    timer = setTimeout(() => {
      const first = stations.length > 1 ? 1 : 0;
      setActiveLoopStep(first);
      cycle(first);
    }, stepDurations[0]);
    return () => clearTimeout(timer);
  }, [loopInView, stations.length]);

  if (!hasStations) {
    // Section keeps its dark backdrop slot while the stations Sim workflow
    // is in flight, but without the "STATIC · problem loop — needs
    // generatedContent.stations" debug badge that used to leak into the
    // live page during generation.
    return (
      <section
        className="relative bg-[var(--color-brand-deepnavy)] py-12 md:py-16 px-4 sm:px-6"
        data-testid="section-problem-loop"
      >
        <div
          className="max-w-2xl mx-auto flex items-center justify-center py-16 text-[var(--color-brand-cream)]/70"
          data-testid="loading-problem-loop"
          aria-label="Generating the problem loop"
        >
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
        <div className="mt-4">
          <VillainSection generatedContent={generatedContent} typography={typography} />
        </div>
      </section>
    );
  }

  return (
    <section
      className="relative bg-[var(--color-brand-deepnavy)] py-12 md:py-16 px-4 sm:px-6 overflow-hidden"
      data-testid="section-problem-loop"
    >
      <motion.div
        className="max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.6 }}
      >
        {/* Section header */}
        <div className="mb-2 text-center">
          <div className="flex justify-center mb-2" data-testid="badge-problem-loop-live"><LiveBadge /></div>
          {sectionLabel && (
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1 mb-3 rounded font-black text-[10px] uppercase tracking-[0.25em]"
              style={{ backgroundColor: "rgba(130,180,50,0.15)", border: "1px solid rgba(130,180,50,0.35)", color: "#a3c463" }}
              data-testid="text-problem-loop-label"
            >
              <AlertTriangle className="h-3 w-3" />
              <EditableText as="span" elementKey="stations:eyebrow" path={["stations", "section_label"]} value={sectionLabel} />
            </div>
          )}
          <EditableText
            as="h2"
            multiline
            elementKey="stations:headline"
            className={`${typography.headline} text-white text-2xl sm:text-3xl md:text-4xl uppercase tracking-wider leading-snug mb-3`}
            testId="text-problem-loop-headline"
            path={["stations", "section_headline"]}
            value={stationsPayload?.section_headline?.trim() || ""}
            placeholder={"BIG PHARMA & BIG SKIN'S\nPLAN FOR YOUR FACE"}
          />
          <FactoryHeader />
        </div>

        {/* Station cards + sludge pipes */}
        <div ref={loopRef} className="relative flex flex-col gap-0 mb-6">
          {stations.map((station, i) => {
            const pal = LOOP_PALETTE[i] ?? LOOP_PALETTE[LOOP_PALETTE.length - 1];
            const nextPal = LOOP_PALETTE[i + 1] ?? pal;
            const isActive = activeLoopStep === i;
            return (
              <div key={i} className="contents">
                <EditableItem
                  path={["stations", "stations"]}
                  index={i}
                  count={stations.length}
                  hidden={(stationsArr as any)?.[i]?.hidden}
                  testId={`station-${i}`}
                >
                <motion.div
                  data-testid={`card-problem-loop-station-${i}`}
                  style={{
                    position: "relative",
                    backgroundColor: pal.baseBg,
                    border: `1px solid ${pal.borderColor}`,
                    borderRadius: 12,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    gap: 16,
                    padding: "14px 18px 14px 22px",
                  }}
                  initial={{ boxShadow: pal.permanentGlow, x: 0 }}
                  animate={{
                    boxShadow: isActive
                      ? [pal.permanentGlow, pal.pulseGlow, pal.permanentGlow]
                      : pal.permanentGlow,
                    x: isActive ? [0, -3, 3, -2, 2, -1, 1, 0] : 0,
                  }}
                  transition={
                    isActive
                      ? {
                          boxShadow: { duration: 0.75, repeat: Infinity, repeatType: "loop", ease: "easeInOut" },
                          x: { duration: 0.45, repeat: Infinity, repeatDelay: 1.0, ease: "easeInOut" },
                        }
                      : { duration: 0.4, ease: "easeOut" }
                  }
                >
                  {/* Hazard-stripe left accent */}
                  <div style={{
                    position: "absolute", left: 0, top: 0, bottom: 0, width: 8,
                    background: `repeating-linear-gradient(-45deg, ${pal.accentColor} 0px, ${pal.accentColor} 5px, #000 5px, #000 10px)`,
                  }} />
                  {/* Corner bolts */}
                  <div style={{ position: "absolute", top: 5,    right: 7, width: 7, height: 7, borderRadius: "50%", background: "radial-gradient(circle at 35% 35%, #555, #1a1a1a)", border: "1px solid #444" }} />
                  <div style={{ position: "absolute", bottom: 5, right: 7, width: 7, height: 7, borderRadius: "50%", background: "radial-gradient(circle at 35% 35%, #555, #1a1a1a)", border: "1px solid #444" }} />
                  {/* Station number */}
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, minWidth: 40, marginLeft: 6 }}>
                    <EditableText
                      as="span"
                      style={{ color: pal.accentColor, fontSize: 9, fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", lineHeight: 1, marginBottom: 2 }}
                      path={["stations", "stationWord"]}
                      value={(generatedContent as any)?.stations?.stationWord}
                      placeholder="STATION"
                    />
                    <span
                      style={{ color: pal.accentColor, fontSize: 28, fontWeight: 900, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}
                      data-testid={`text-problem-loop-num-${i}`}
                    >
                      {station.num}
                    </span>
                  </div>
                  {/* Copy */}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <EditableText
                      as="p"
                      style={{ color: "#fff", fontWeight: 900, fontSize: 15, lineHeight: 1.3 }}
                      testId={`text-problem-loop-label-${i}`}
                      path={["stations", "stations", i, "label"]}
                      value={station.label}
                    />
                    <EditableText
                      as="p"
                      multiline
                      style={{ color: "rgba(255,255,255,0.55)", fontSize: 13, marginTop: 4, lineHeight: 1.4 }}
                      testId={`text-problem-loop-sub-${i}`}
                      path={["stations", "stations", i, "sub"]}
                      value={station.sub}
                    />
                  </div>
                </motion.div>
                </EditableItem>

                {/* Sludge pipe between stations */}
                {i < stations.length - 1 && (
                  <SludgePipe
                    fromColor={pal.accentColor}
                    toColor={nextPal.accentColor}
                    testId={`connector-problem-loop-${i}`}
                  />
                )}
              </div>
            );
          })}

          {/* ↻ Repeat row */}
          <div className="flex items-center gap-3 mt-2 w-full">
            <div className="flex-1 h-px bg-[var(--color-brand-coral)]/30" />
            <motion.span
              className="text-[var(--color-brand-coral)] text-xs font-black uppercase tracking-widest inline-flex items-center gap-1.5"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              data-testid="text-problem-loop-repeat"
            >
              <RotateCw className="h-3.5 w-3.5" />
              <EditableText
                as="span"
                path={["stations", "repeatLabel"]}
                value={(generatedContent as any)?.stations?.repeatLabel}
                placeholder="repeat"
              />
            </motion.span>
            <div className="flex-1 h-px bg-[var(--color-brand-coral)]/30" />
          </div>
        </div>

        {/* "That's a loop." warning sign */}
        {warningQuote ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            style={{ display: "flex", justifyContent: "center" }}
            data-testid="text-problem-loop-quote"
          >
            <div style={{
              backgroundColor: "#FFCC00",
              border: "4px solid #111",
              outline: "2px solid #111",
              outlineOffset: "4px",
              padding: "14px 24px",
              textAlign: "center",
              transform: "rotate(-0.8deg)",
              boxShadow: "4px 4px 0px #111",
              maxWidth: "100%",
            }}>
              <div style={{ fontSize: 22, marginBottom: 4, lineHeight: 1 }}>⚠</div>
              <p style={{ color: "#111", fontWeight: 900, fontSize: 17, fontStyle: "italic", lineHeight: 1.3, letterSpacing: "-0.01em" }}>
                "
                <EditableText
                  as="span"
                  multiline
                  regenAnchor={!!verdictQuote}
                  path={verdictQuote ? ["quotes", "verdict", "quote"] : ["stations", "loop_quote"]}
                  value={warningQuote}
                />
                "
              </p>
            </div>
          </motion.div>
        ) : quotesLoading ? (
          <div style={{ display: "flex", justifyContent: "center" }} data-testid="loading-problem-loop-quote">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: "#FFCC00" }} />
          </div>
        ) : null}
      </motion.div>
      <div className="mt-12">
        <VillainSection generatedContent={generatedContent} typography={typography} />
      </div>
    </section>
  );
}

// =============================================================================
// VILLAIN SECTION — sits inside the dark-navy belief-shift band, directly
// after the ProblemLoopSection/Stations block. Copy is hardcoded for now
// (a Sim workflow will fill it dynamically in a follow-up task). Matches the
// pasted spec ("Replit Agent Prompt — Villain Section") exactly: cream
// checklist card with plain red ✕ marks + "They have one job. And it's not
// your skin." footer, then a slightly rotated "Who's Really Selling You
// Skincare" report card with 5 competitor types graded F and a coral
// "None of them are on your side" verdict.
// =============================================================================
const VILLAIN_CHECKLIST_ITEMS: string[] = [
  "Cheap fillers that never expire",
  "Shelf life over skin health",
  "Results just good enough to keep you buying",
];

const VILLAIN_REPORT_CARD_ROWS: Array<{ name: string; note: string; grade: string }> = [
  { name: "Big Pharma Brands",       note: "Sells the problem and the solution",     grade: "F" },
  { name: "Private Equity Owned",    note: "Acquired, gutted, reformulated cheap",   grade: "F" },
  { name: "Foreign-Made Formulas",   note: "No traceability. No accountability.",    grade: "F" },
  { name: "Influencer-Backed Lines", note: "Marketing budget disguised as skincare", grade: "D" },
  { name: "Dept. Store Prestige",    note: "90% margin. 10% product.",               grade: "D" },
];

// Neutral, brand-agnostic fallbacks used only by Long Form v2 — same shape and
// count as the v1 spec so the layout stays pixel-identical; only the
// skincare-specific wording is generalized.
const VILLAIN_CHECKLIST_ITEMS_V2: string[] = [
  "Cheap fillers that cut costs",
  "Shelf life over real quality",
  "Results just good enough to keep you buying",
];

const VILLAIN_REPORT_CARD_ROWS_V2: Array<{ name: string; note: string; grade: string }> = [
  { name: "Big Corporate Brands",     note: "Sells the problem and the solution",     grade: "F" },
  { name: "Private Equity Owned",     note: "Acquired, gutted, reformulated cheap",   grade: "F" },
  { name: "Overseas Factories",       note: "No traceability. No accountability.",    grade: "F" },
  { name: "Influencer-Backed Lines",  note: "Marketing budget disguised as product",  grade: "D" },
  { name: "Department Store Prestige",note: "90% margin. 10% product.",               grade: "D" },
];

function VillainSection({
  generatedContent,
}: {
  generatedContent?: GeneratedContent;
  typography?: ReturnType<typeof brandTypography>;
}) {
  // Headers/labels/verdict copy stays static; the checklist items and
  // report-card rows are populated from the Sim workflow when available
  // and fall back to the static spec defaults until the workflow returns.
  const v2 = useLpV2();
  const v = generatedContent?.villain;
  const trim = (s: unknown) => (typeof s === "string" ? s.trim() : "");
  const dynamicChecklist = Array.isArray(v?.checklist_items)
    ? v!.checklist_items!.map(trim).filter(Boolean)
    : [];
  const dynamicRows = Array.isArray(v?.report_rows)
    ? v!.report_rows!
        .map((r) => ({ name: trim(r?.name), note: trim(r?.note), grade: trim(r?.grade) }))
        .filter((r) => r.name)
    : [];
  // Headlines/labels/footer come from the workflow when present, otherwise
  // fall back to the static spec copy. Verdict label + verdict text stay
  // hardcoded by design.
  const checklistHeadline = trim(v?.checklist_headline) || "What they're actually building for:";
  const checklistItems = dynamicChecklist.length > 0 ? dynamicChecklist : (v2 ? VILLAIN_CHECKLIST_ITEMS_V2 : VILLAIN_CHECKLIST_ITEMS);
  const checklistFooter = trim(v?.checklist_footer) || "They have one job. And it's not your skin.";
  const reportEyebrow = trim(v?.report_eyebrow) || "Skincare Industry";
  const reportHeadline = trim(v?.report_headline) || "Who's Really Selling You Skincare";
  const reportRows = dynamicRows.length > 0 ? dynamicRows : (v2 ? VILLAIN_REPORT_CARD_ROWS_V2 : VILLAIN_REPORT_CARD_ROWS);
  const checklistLive = dynamicChecklist.length > 0 || !!trim(v?.checklist_headline);
  const rowsLive = dynamicRows.length > 0 || !!trim(v?.report_headline);
  const verdictLabel = trim(v?.verdict_label) || "Verdict";
  const verdict = trim(v?.verdict) || "None of them are on your side";

  // Editable entries: when content-backed (live), map each item to its stored
  // array index so inline edits patch the right slot; otherwise render the
  // static fallback copy non-editably (pre-generation placeholder).
  const checklistEntries: Array<{ value: string; storedIndex: number }> =
    dynamicChecklist.length > 0 && Array.isArray(v?.checklist_items)
      ? v!.checklist_items!.map((raw, idx) => ({ value: trim(raw), storedIndex: idx })).filter((e) => e.value)
      : checklistItems.map((value) => ({ value, storedIndex: -1 }));
  const reportEntries: Array<{ name: string; note: string; grade: string; storedIndex: number }> =
    dynamicRows.length > 0 && Array.isArray(v?.report_rows)
      ? v!.report_rows!
          .map((r, idx) => ({ name: trim(r?.name), note: trim(r?.note), grade: trim(r?.grade), storedIndex: idx }))
          .filter((e) => e.name)
      : reportRows.map((r) => ({ ...r, storedIndex: -1 }));

  // While the villain workflow is still generating and nothing live has
  // arrived yet, show a spinner instead of the static spec checklist/report.
  if (generatedContent?.loading?.villain && !checklistLive && !rowsLive) {
    return (
      <div className="max-w-[520px] mx-auto" data-testid="section-villain">
        <SectionLoading tone="dark" label="Generating exposé…" />
      </div>
    );
  }

  return (
    <div className="max-w-[520px] mx-auto" data-testid="section-villain">
      <LpReorderGroup
        groupKey="villain:blocks"
        items={[
          /* ── 1. Checklist card — whole card is one hide/delete unit ── */
          { key: "villain:checklist-card", node: (
      <EditableElement elementKey="villain:checklist-card" testId="element-villain-checklist">
      <motion.div
        className="mb-12"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.55 }}
        data-testid="card-villain-checklist"
      >
        <div
          style={{
            backgroundColor: "#F7F3EC",
            borderRadius: 10,
            padding: "20px 18px 18px 18px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
          }}
        >
          <p
            style={{ color: "var(--color-brand-deepnavy)", fontSize: 17, fontWeight: 900, lineHeight: 1.3, marginBottom: 14 }}
            data-testid="text-villain-checklist-headline"
          >
            <EditableText
              as="span"
              multiline
              elementKey="villain:checklist_headline"
              path={["villain", "checklist_headline"]}
              value={trim(v?.checklist_headline)}
              placeholder="What they're actually building for:"
            />
            {checklistLive && <LiveBadge floating />}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {checklistEntries.map((item, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 0",
                  borderBottom: i < checklistEntries.length - 1 ? "1px solid #DDD5C5" : "none",
                }}
                data-testid={`item-villain-checklist-${i}`}
              >
                <span style={{ color: "var(--color-brand-coral)", fontWeight: 900, fontSize: 15, flexShrink: 0 }}>✕</span>
                {item.storedIndex >= 0 ? (
                  <EditableText
                    as="p"
                    multiline
                    style={{ color: "var(--color-brand-deepnavy)", fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}
                    path={["villain", "checklist_items", item.storedIndex]}
                    value={item.value}
                    placeholder=""
                  />
                ) : (
                  <p style={{ color: "var(--color-brand-deepnavy)", fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>{item.value}</p>
                )}
              </div>
            ))}
          </div>
          <p
            style={{
              color: "var(--color-brand-deepnavy)",
              fontSize: 14,
              fontWeight: 800,
              textAlign: "center",
              marginTop: 14,
              paddingTop: 12,
              borderTop: "1px solid #DDD5C5",
            }}
            data-testid="text-villain-checklist-footer"
          >
            <EditableText
              as="span"
              multiline
              path={["villain", "checklist_footer"]}
              value={trim(v?.checklist_footer)}
              placeholder="They have one job. And it's not your skin."
            />
          </p>
        </div>
      </motion.div>
      </EditableElement>
          ) },
          /* ── 2. Villain report card — parent hide/delete/reorder unit ── */
          { key: "villain:report-card", node: (
      <EditableElement elementKey="villain:report-card" testId="element-villain-report-card">
      <motion.div
        className="mb-12"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.55 }}
        data-testid="card-villain-report-card"
      >
        <div
          className="w-full"
          style={{
            boxShadow: "0 8px 32px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)",
            transform: "rotate(0.5deg)",
          }}
        >
          <div className="px-4 py-3 text-center" style={{ backgroundColor: "var(--color-brand-deepnavy)" }}>
            <p className="text-white/40 text-[8px] font-black uppercase tracking-[0.3em]">
              <EditableText
                as="span"
                elementKey="villain:report_eyebrow"
                path={["villain", "report_eyebrow"]}
                value={trim(v?.report_eyebrow)}
                placeholder={v2 ? "The Industry" : "Skincare Industry"}
              />
            </p>
            <p
              className="relative text-white text-[13px] font-black uppercase tracking-[0.15em] mt-0.5"
              data-testid="text-villain-report-headline"
            >
              <EditableText
                as="span"
                multiline
                elementKey="villain:report_headline"
                path={["villain", "report_headline"]}
                value={trim(v?.report_headline)}
                placeholder={v2 ? "Who's Really Selling to You" : "Who's Really Selling You Skincare"}
              />
              {rowsLive && <LiveBadge floating />}
            </p>
          </div>
          <div
            className="flex items-center justify-between px-4 py-1.5"
            style={{ backgroundColor: "var(--color-brand-cream)", borderBottom: "1px solid #D8CFC3" }}
          >
            <EditableText
              as="span"
              className="text-[8px] font-black uppercase tracking-[0.2em]"
              style={{ color: "#6B6560" }}
              path={["villain", "company_type_label"]}
              value={trim(v?.company_type_label)}
              placeholder="Company Type"
            />
            <EditableText
              as="span"
              className="text-[8px] font-black uppercase tracking-[0.2em]"
              style={{ color: "#6B6560" }}
              path={["villain", "grade_label"]}
              value={trim(v?.grade_label)}
              placeholder="Grade"
            />
          </div>
          {reportEntries.map(({ name, note, grade, storedIndex }, i) => (
            <div
              key={`${name}-${i}`}
              className="flex items-center justify-between px-4 py-2.5"
              style={{
                borderBottom: i < reportEntries.length - 1 ? "1px solid #E8DDD0" : "none",
                backgroundColor: i % 2 === 0 ? "#FFFCF4" : "#FAF6EE",
              }}
              data-testid={`row-villain-report-${i}`}
            >
              <div>
                {storedIndex >= 0 ? (
                  <>
                    <EditableText
                      as="p"
                      className="text-[13px] font-semibold"
                      style={{ color: "var(--color-brand-deepnavy)" }}
                      path={["villain", "report_rows", storedIndex, "name"]}
                      value={name}
                      placeholder=""
                    />
                    <EditableText
                      as="p"
                      className="text-[9px]"
                      style={{ color: "#9C9189" }}
                      path={["villain", "report_rows", storedIndex, "note"]}
                      value={note}
                      placeholder=""
                    />
                  </>
                ) : (
                  <>
                    <p className="text-[13px] font-semibold" style={{ color: "var(--color-brand-deepnavy)" }}>{name}</p>
                    <p className="text-[9px]" style={{ color: "#9C9189" }}>{note}</p>
                  </>
                )}
              </div>
              {storedIndex >= 0 ? (
                <EditableText
                  as="span"
                  className="text-[20px] font-black ml-3 shrink-0"
                  style={{ color: "#D64040", fontFamily: "Georgia, serif" }}
                  path={["villain", "report_rows", storedIndex, "grade"]}
                  value={grade}
                  placeholder="F"
                />
              ) : (
                <span
                  className="text-[20px] font-black ml-3 shrink-0"
                  style={{ color: "#D64040", fontFamily: "Georgia, serif" }}
                >
                  {grade?.trim() || "F"}
                </span>
              )}
            </div>
          ))}
          <div
            className="px-4 py-2.5 flex items-center justify-between"
            style={{ borderTop: "2px solid var(--color-brand-deepnavy)", backgroundColor: "var(--color-brand-cream)" }}
          >
            <EditableText
              as="span"
              className="text-[8px] font-black uppercase tracking-[0.2em]"
              style={{ color: "#6B6560" }}
              path={["villain", "verdict_label"]}
              value={trim(v?.verdict_label)}
              placeholder="Verdict"
            />
            <EditableText
              as="span"
              className="text-[10px] font-black uppercase tracking-[0.1em]"
              style={{ color: "var(--color-brand-coral)" }}
              path={["villain", "verdict"]}
              value={trim(v?.verdict)}
              placeholder="None of them are on your side"
              testId="text-villain-verdict"
            />
          </div>
        </div>
      </motion.div>
      </EditableElement>
          ) },
        ]}
      />
    </div>
  );
}

function PsaWarningSection({
  generatedContent,
  typography,
}: {
  generatedContent?: GeneratedContent;
  typography: ReturnType<typeof brandTypography>;
}) {
  const v2 = useLpV2();
  const label = "PUBLIC ADVISORY";
  const adv = generatedContent?.advisory;
  const liveMuted = adv?.advisory_headline_muted?.trim() || "";
  const liveBoldRaw = adv?.advisory_headline_bold?.trim() || "";
  const liveAccent = adv?.advisory_headline_accent?.trim() || "";
  const liveLegacyHeadline = adv?.advisory_headline?.trim() || "";
  // Fallback: if the workflow didn't return the split bold field but did
  // return the legacy single headline, surface that as the bold line so
  // the headline area is never blank.
  const liveBold = liveBoldRaw || (!liveMuted ? liveLegacyHeadline : "");
  const liveSubheadline = adv?.advisory_subheadline?.trim() || "";
  const isLive = !!(liveMuted || liveBold || liveSubheadline || liveLegacyHeadline);
  const showStatusDots = useLpStatusDots();
  // Template literal fallbacks — match the Papabear AboveTheFold spec so
  // the PSA never renders effectively blank when the advisory workflow
  // payload is missing.
  const displayMuted = liveMuted || (v2 ? "You're not the problem." : "Your skin isn't broken.");
  const displayBold = liveBold || (v2 ? "The products are." : "Your products are.");
  const displayAccent = liveAccent || (liveBold ? "" : "are.");
  const displaySubheadline =
    liveSubheadline ||
    (v2
      ? "Most mainstream products are engineered around shelf life, profit margin, and marketing claims — not real results. Honest, well-made ingredients are the only way out of the cycle."
      : "Mainstream skincare is engineered around shelf life, profit margin, and marketing claims — not your skin. Whole-food, ranch-crafted ingredients are the only way out of the cycle.");

  const renderBoldWithAccent = (bold: string, accent: string) => {
    if (!accent) return <>{bold}</>;
    const idx = bold.toLowerCase().indexOf(accent.toLowerCase());
    if (idx === -1) return <>{bold}</>;
    const before = bold.slice(0, idx);
    const match = bold.slice(idx, idx + accent.length);
    const after = bold.slice(idx + accent.length);
    return (
      <>
        {before}
        <span className="text-[#DC2626]">{match}</span>
        {after}
      </>
    );
  };
  const tape: React.CSSProperties = {
    backgroundImage:
      "repeating-linear-gradient(45deg, #F5C84B 0 16px, #0B0B0B 16px 32px)",
  };
  // While the advisory workflow is still generating and no live copy has
  // arrived, show a spinner instead of the static template advisory copy.
  if (generatedContent?.loading?.advisory && !isLive) {
    return (
      <section
        className={`relative ${v2 ? "bg-[var(--color-brand-deepnavy)]" : "bg-brand-deepnavy"} pt-24 pb-12 px-4 sm:px-6`}
        data-testid="section-psa-warning"
      >
        <SectionLoading tone="dark" label="Generating advisory…" />
      </section>
    );
  }
  return (
    <section
      className={`relative ${v2 ? "bg-[var(--color-brand-deepnavy)]" : "bg-brand-deepnavy"} pt-24 pb-0 px-4 sm:px-6`}
      data-testid="section-psa-warning"
    >
      {showStatusDots && (
        <span
          className="absolute top-4 right-4 z-10"
          data-testid={isLive ? "badge-psa-live" : "badge-psa-static"}
        >
          {isLive ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 border border-green-700 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-white" />
              LIVE — advisory copy
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-[#F5C84B] text-[var(--color-brand-deepnavy)] text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 border border-[var(--color-brand-deepnavy)] shadow-sm">
              STATIC — advisory copy
            </span>
          )}
        </span>
      )}
      <EditableElement elementKey="advisory:card" testId="element-advisory-card">
      <motion.div
        className="max-w-xl mx-auto"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        data-testid="motion-psa-reveal"
      >
        <div className="relative p-3 sm:p-4" style={tape}>
          <div className="bg-white">
            <div
              className="relative bg-[#F5C84B] text-[#0B0B0B] py-3 border-b-2 border-[#0B0B0B]"
              data-testid="text-psa-label"
            >
              <div
                className={`flex items-center justify-center gap-3 ${typography.eyebrow}`}
                style={{ fontVariant: "small-caps", letterSpacing: "0.25em" }}
              >
                <AlertTriangle className="h-4 w-4 fill-current" />
                <EditableText
                  as="span"
                  regenAnchor
                  elementKey="advisory:label"
                  className="text-sm sm:text-base font-black"
                  path={["advisory", "advisory_label"]}
                  value={adv?.advisory_label}
                  placeholder={label}
                  testId="text-psa-label-text"
                />
                <AlertTriangle className="h-4 w-4 fill-current" />
              </div>
            </div>
            <div className="px-6 sm:px-10 md:px-16 py-6 md:py-8 text-center">
              <EditableElement elementKey="advisory:headline">
              <h2
                className={`${typography.headline} text-[28px] sm:text-[38px] md:text-[44px] leading-[1.05] uppercase max-w-3xl mx-auto`}
                data-testid="text-psa-headline"
              >
                <span className="block leading-[1.05]">
                  <EditableText
                    as="span"
                    noHideDelete
                    className="block uppercase"
                    style={{ color: "#9CA3AF", fontWeight: 800 }}
                    testId="text-psa-headline-muted"
                    path={["advisory", "advisory_headline_muted"]}
                    value={liveMuted}
                    placeholder={displayMuted}
                  />
                  <EditableText
                    as="span"
                    noHideDelete
                    className="block uppercase"
                    style={{ color: "#111", fontWeight: 900 }}
                    testId="text-psa-headline-bold"
                    path={["advisory", "advisory_headline_bold"]}
                    value={liveBold}
                    placeholder={displayBold}
                    render={(v) => renderBoldWithAccent(v || displayBold, displayAccent)}
                  />
                </span>
              </h2>
              </EditableElement>
              <hr className="mt-8 mb-6 border-0 border-t-2 border-[#0B0B0B] max-w-3xl mx-auto" aria-hidden />
              <EditableText
                as="p"
                multiline
                elementKey="advisory:subheadline"
                className={`${typography.body} text-xs sm:text-sm text-[#3a3a3a] max-w-lg mx-auto leading-relaxed`}
                testId="text-psa-subheadline"
                path={["advisory", "advisory_subheadline"]}
                value={liveSubheadline}
                placeholder={displaySubheadline}
              />
            </div>
          </div>
        </div>
      </motion.div>
      </EditableElement>
    </section>
  );
}

// =============================================================================
// 13. COMPETITOR COMPARISON — "Now meet the competition" (static placeholder)
// =============================================================================
function CompetitorComparisonSection({ typography }: { typography: ReturnType<typeof brandTypography> }) {
  const them = [
    "Synthetic fillers nobody can pronounce",
    "Designed in a lab, optimized for shelf life",
    "Mass-produced by the truckload",
    "Marketing budget bigger than R&D",
  ];
  const us = [
    "Recognizable ingredients sourced with intention",
    "Built around what your skin actually needs",
    "Small batches you can trace back to the source",
    "Sold by the customers who keep coming back",
  ];
  const ribbed: React.CSSProperties = {
    backgroundImage:
      "repeating-linear-gradient(0deg, transparent, transparent 31px, rgba(180,170,150,0.08) 31px, rgba(180,170,150,0.08) 32px)",
  };
  return (
    <section className="relative" style={ribbed}>
      <div className="text-center pt-16 pb-10 px-4 sm:px-6 bg-white">
        <div className={`${typography.eyebrow} text-[var(--color-brand-coral)] mb-3`}>The alternative</div>
        <h2 className={`${typography.headline} text-[26px] md:text-[32px] text-[var(--color-brand-text)]`}>
          Now meet the competition
          <span className="ml-2 align-middle inline-block"><StaticBadge label="competitor-comparison — Sim wiring later" /></span>
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        {/* THEM */}
        <div className="bg-[var(--color-brand-deepnavy)] text-white p-8 md:p-12">
          <div className="mb-6">
            <span className="inline-flex items-center gap-2 bg-[var(--color-brand-coral)] text-white text-xs font-black uppercase tracking-widest rounded-full px-4 py-1.5 font-sans">
              Them
            </span>
          </div>
          <ul className="space-y-4">
            {them.map((item, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`row-competitor-them-${i}`}>
                <XIcon className="h-5 w-5 text-[var(--color-brand-coral)] mt-0.5 shrink-0" />
                <span className={`${typography.body} text-sm md:text-base leading-relaxed`}>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* US */}
        <div className="bg-[var(--color-brand-cream)] text-[var(--color-brand-navy)] p-8 md:p-12">
          <div className="mb-6">
            <span className="inline-flex items-center gap-2 bg-[var(--color-brand-green)] text-white text-xs font-black uppercase tracking-widest rounded-full px-4 py-1.5 font-sans">
              Us
            </span>
          </div>
          <ul className="space-y-4">
            {us.map((item, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`row-competitor-us-${i}`}>
                <Check className="h-5 w-5 text-[var(--color-brand-green)] mt-0.5 shrink-0" />
                <span className={`${typography.body} text-sm md:text-base text-[var(--color-brand-text)] leading-relaxed`}>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// 13b. MID MARQUEE — short coral scrolling strip, used twice mid-page
// =============================================================================
function MidMarquee({ items, variant = "navy" }: { items: string[]; variant?: "navy" | "cream" }) {
  if (items.length === 0) return null;
  const repeated = Array.from({ length: 6 }, () => items).flat();
  const isCream = variant === "cream";
  const bg = isCream ? "bg-[var(--color-brand-cream)]" : "bg-[var(--color-brand-deepnavy)]";
  const text = isCream ? "text-[var(--color-brand-navy)]" : "text-white";
  return (
    <div className={`relative ${bg} ${text} overflow-hidden`}>
      <div className="py-2.5 flex whitespace-nowrap animate-[marqueeMid_30s_linear_infinite]">
        {repeated.map((t, i) => (
          <span key={i} className="mx-6 text-xs font-black uppercase tracking-widest font-sans flex items-center gap-6">
            {t}
            <span className="text-[var(--color-brand-coral)]">·</span>
          </span>
        ))}
      </div>
      <style>{`@keyframes marqueeMid { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </div>
  );
}

// =============================================================================
// 13c. SPRING SALE PITCH — mid-page promo block between marquees
// =============================================================================
function SpringSalePitchSection({ sale, offer, typography }: {
  sale?: SalesDiscount | null;
  offer: Offer;
  typography: ReturnType<typeof brandTypography>;
}) {
  const hasLiveSale = !!sale?.name;
  const eyebrow = hasLiveSale ? (sale?.saleType || "Limited time") : "Limited-time pitch";
  const headline = hasLiveSale
    ? (sale?.customerFacingCopy || sale?.promotionSummary || sale?.name || "Spring sale")
    : "Spring sale: stock up before the season turns";
  const body = hasLiveSale
    ? (sale?.whyThisSaleExists || "Right now, every kit ships at the season's best price. Restock once, ride out the change in weather.")
    : "Right now, every kit ships at the season's best price. Restock once, ride out the change in weather.";
  const ctaText = sale?.ctaLanguage || "Claim the sale price";
  return (
    <section className="bg-[#FDFCF8] py-12 md:py-20 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        <div
          className="rounded-2xl border border-[#D8CFC3] bg-white p-8 md:p-10 text-center"
          style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
          data-testid="card-spring-sale"
        >
          <div className={`${typography.eyebrow} text-[var(--color-brand-coral)] mb-3 flex items-center justify-center gap-2`}>
            {eyebrow}
            {hasLiveSale ? <LiveBadge /> : <StaticBadge label="no active sale — placeholder" />}
          </div>
          <h2 className={`${typography.headline} text-[24px] md:text-[30px] text-[var(--color-brand-text)] mb-3`}>
            {headline}
          </h2>
          <p className={`${typography.body} text-sm md:text-base text-[#6B6560] leading-relaxed mb-6 max-w-xl mx-auto`}>
            {body}
          </p>
          <a
            href={offer.checkoutUrl || undefined}
            data-lp-cta
            className="inline-block bg-[var(--color-brand-coral)] text-white rounded-xl px-6 py-3 text-xs font-black uppercase tracking-widest font-sans hover:opacity-90"
            data-testid="link-spring-sale-cta"
          >
            {ctaText}
          </a>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// 16. FOUNDER STORY — brand.founderStory + brand.founderImage
// =============================================================================
function FounderStorySection({ brand, typography }: { brand: Brand | null | undefined; typography: ReturnType<typeof brandTypography> }) {
  const story: string | null = (brand as any)?.founderStory ?? null;
  const image: string | null = (brand as any)?.founderImage ?? (brand as any)?.founderImageUrl ?? null;
  const founderName: string | null = (brand as any)?.founderName ?? null;
  const hasStory = !!story;
  return (
    <section className="bg-white py-12 md:py-20 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 md:gap-14 items-center">
        {/* Image renders SECOND on mobile (copy first) but FIRST on md+ via order classes */}
        <EditableElement elementKey="founder:image" toolbarSide="left" testId="element-founder-image" className="order-2 md:order-1">
        <div className="order-2 md:order-1 aspect-[4/5] bg-[var(--color-brand-cream)] rounded-2xl overflow-hidden border border-[#D8CFC3]"
          style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
        >
          {image ? (
            <img src={image} alt={founderName ?? brand?.name ?? "Founder"} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <StaticBadge label="brand.founderImage missing" />
            </div>
          )}
        </div>
        </EditableElement>
        <div className="order-1 md:order-2">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[var(--color-brand-coral)] font-sans mb-3">
            From the founder
          </div>
          <h2 className={`relative ${typography.headline} text-[32px] text-[var(--color-brand-navy)] leading-tight mb-5`}>
            Why we started
            {hasStory ? <LiveBadge floating />
                      : <span className="ml-2 align-middle inline-block"><StaticBadge /></span>}
          </h2>
          {hasStory ? (
            <p className={`${typography.body} text-[15px] text-[var(--color-brand-text)] leading-relaxed whitespace-pre-line`} data-testid="text-founder-story">
              {story}
            </p>
          ) : (
            <p className={`${typography.body} text-[15px] text-[#6B6560] leading-relaxed italic`}>
              [Founder story not available — populate brand.founderStory to fill this in.]
            </p>
          )}
          {founderName && (
            <div className="mt-5 text-xs font-black uppercase tracking-widest text-[var(--color-brand-navy)] font-sans">
              — {founderName}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// 17. MEMBERSHIP EXPLAINER — refill cadence
// =============================================================================
function MembershipExplainerSection({ products, typography }: { products: Product[]; typography: ReturnType<typeof brandTypography> }) {
  const product = products[0] ?? null;
  const refill = (product as any)?.price?.refill ?? null;
  const refillPrice: string = refill?.price ?? refill?.amount ?? "";
  const refillCadenceDays: number | null = refill?.cadenceDays ?? refill?.intervalDays ?? null;
  const firstPrice: string = (product as any)?.priceText ?? "";
  const isLive = !!(refillPrice && refillCadenceDays);

  const steps = [
    { num: 1, label: "Try the kit today", chip: firstPrice || "Today" },
    { num: 2, label: `Refills auto-ship${refillCadenceDays ? ` every ${refillCadenceDays} days` : ""}`, chip: refillPrice || "Refill" },
    { num: 3, label: "Pause, swap, or cancel any time", chip: "Free" },
  ];

  return (
    <section className="bg-[#FDFCF8] py-12 md:py-20 px-4 sm:px-6 border-y border-[#D8CFC3]">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className={`relative ${typography.headline} text-[26px] md:text-[32px] text-[var(--color-brand-text)]`}>
            How the membership works
            {isLive ? <LiveBadge floating />
                    : <span className="ml-2 align-middle inline-block"><StaticBadge label="product.price.refill missing" /></span>}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div
              key={s.num}
              className="bg-white rounded-2xl border border-[#D8CFC3] p-6 text-center"
              style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}
              data-testid={`card-membership-step-${s.num}`}
            >
              <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-[var(--color-brand-navy)] text-white font-black text-2xl flex items-center justify-center font-sans">
                {s.num}
              </div>
              <p className={`${typography.body} text-sm md:text-base text-[var(--color-brand-text)] leading-relaxed mb-4`}>
                {s.label}
              </p>
              <span className="inline-block border border-[var(--color-brand-navy)] rounded-full px-4 py-2 text-sm font-black uppercase tracking-widest text-[var(--color-brand-navy)] font-sans">
                {s.chip}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// 19. BOTTOM STICKY BAR — appears after hero scrolls out of view
// =============================================================================
function BottomStickyBar({ products, offer, generatedContent }: {
  products: Product[];
  offer: Offer;
  generatedContent?: GeneratedContent;
}) {
  const [visible, setVisible] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Scroll happens within whichever window owns this bar — the main window in
    // the app, or the mobile-preview iframe's window when rendered there.
    const win = rootRef.current?.ownerDocument?.defaultView ?? window;
    const onScroll = () => {
      // Heuristic: show once we're past ~80vh (hero is roughly that tall).
      const threshold = Math.max(400, win.innerHeight * 0.8);
      setVisible(win.scrollY > threshold);
    };
    onScroll();
    win.addEventListener("scroll", onScroll, { passive: true });
    return () => win.removeEventListener("scroll", onScroll);
  }, []);
  const product = products[0] ?? null;
  const stickyPhotos = getProductImages(product);
  const imgDefault = stickyPhotos[0] ?? null;
  const imgOverride = useLpMediaOverride(LP_PRODUCT_SLOT_STICKY_BAR);
  const img = imgOverride ?? imgDefault;
  const price = ensureDollar(offer.priceToday) || "[price]";
  const ctaText = generatedContent?.cta?.cta_text || generatedContent?.hero?.cta_text || "Get yours";
  return (
    <div
      ref={rootRef}
      className={`fixed inset-x-0 bottom-0 z-40 bg-[var(--color-brand-deepnavy)] text-white py-3 px-4 transition-transform duration-300 ${visible ? "translate-y-0" : "translate-y-full"}`}
      data-testid="bar-sticky-bottom"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
        <div className="group/lpimg relative hidden md:block h-10 w-10 shrink-0">
          <div className="h-full w-full rounded-lg overflow-hidden bg-white/10">
            {img ? (
              <img src={img} alt={product?.name ?? ""} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/30 text-[10px]">IMG</div>
            )}
          </div>
          <LpImageActions
            slotKey={LP_PRODUCT_SLOT_STICKY_BAR}
            productPhotos={stickyPhotos}
            revertLabel="Use default"
            revertTitle="Use the default product photo"
            popoverPlacement="top"
          />
        </div>
        <div className="flex-1 text-center">
          <div className="text-sm font-black uppercase tracking-wider font-sans truncate" data-testid="text-sticky-price">
            {price}
          </div>
        </div>
        <a
          href={offer.checkoutUrl || undefined}
          data-lp-cta
          className="bg-[var(--color-brand-coral)] text-white rounded-xl px-5 py-2.5 text-xs font-black uppercase tracking-widest font-sans hover:opacity-90 shrink-0"
          data-testid="link-sticky-cta"
        >
          {ctaText}
        </a>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN
// =============================================================================
// The Quiz Funnel generates its whole content in one background call. While
// that's in flight (or if it failed) there is no `hero` to fall back on, so the
// renderer shows this dedicated state instead of the normalized placeholder
// quiz — matching the "auto-switch to the editor and just show loading" flow.
function QuizPendingState({
  mode,
  message,
  onRetry,
  onRecover,
  recoverPending = false,
  loadingTitle = "Building your quiz funnel…",
  loadingSub = "Writing the questions, scoring, and personalized results. This usually takes a minute or two.",
  errorTitle = "We couldn't generate your quiz",
  testPrefix = "quiz",
}: {
  mode: "loading" | "error";
  message?: string;
  onRetry?: () => void;
  onRecover?: () => void;
  recoverPending?: boolean;
  loadingTitle?: string;
  loadingSub?: string;
  errorTitle?: string;
  testPrefix?: string;
}) {
  return (
    <div
      className="flex min-h-[420px] flex-col items-center justify-center gap-4 px-6 py-20 text-center"
      style={{ backgroundColor: "#FAFAF7" }}
      data-testid={mode === "loading" ? `${testPrefix}-loading` : `${testPrefix}-error`}
    >
      {mode === "loading" ? (
        <>
          <Loader2 className="h-8 w-8 animate-spin text-[var(--color-brand-primary)]" />
          <div className="space-y-1">
            <p className="text-base font-semibold text-[var(--color-brand-text)]">
              {loadingTitle}
            </p>
            <p className="max-w-md text-sm text-[var(--color-brand-muted)]">
              {loadingSub}
            </p>
          </div>
        </>
      ) : (
        <>
          <AlertTriangle className="h-8 w-8 text-red-500" />
          <div className="space-y-1">
            <p className="text-base font-semibold text-[var(--color-brand-text)]">
              {errorTitle}
            </p>
            <p className="max-w-md text-sm text-[var(--color-brand-muted)]">
              {message || "Something went wrong. Please try generating again."}
            </p>
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-1 inline-flex items-center gap-2 rounded-md bg-[var(--color-brand-primary)] px-4 py-2 text-sm font-semibold text-white"
              data-testid="button-quiz-retry"
            >
              <RotateCw className="h-4 w-4" /> Try again
            </button>
          )}
          {onRecover && (
            <button
              type="button"
              onClick={onRecover}
              disabled={recoverPending}
              className="mt-1 inline-flex items-center gap-2 rounded-md border border-[var(--color-brand-primary)] bg-white px-4 py-2 text-sm font-semibold text-[var(--color-brand-primary)] disabled:cursor-wait disabled:opacity-60"
              data-testid="button-recover-existing-studio-design"
            >
              {recoverPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RotateCw className="h-4 w-4" />
              )}
              {recoverPending ? "Recovering existing design…" : "Recover existing design"}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default function LpExpressRenderer({
  brand, rootProduct, products, sale, generatedContent, reviewsByProduct, ctaUrl, videosLoading = false, onRetrySlot,
  publicLeadContext,
  onRetryHeroSlot, onRetryHero, onRecoverStudioDesign, studioDesignRecoveryAvailable,
  studioDesignRecoveryPending, onRetryProductContext,
  showStatusDots = true,
  editor,
}: LpExpressRendererProps) {
  const offer = buildOffer(brand, products, sale ?? null, ctaUrl);
  const receipt = computeReceipt(products, offer, sale ?? null);
  const typography = brandTypography(brand);
  // Global review aggregate (avg rating + total count across all products) and
  // the brand logo / bundle flat-lay image, wired into the static literals.
  const reviewDisplay = computeReviewDisplay(reviewsByProduct);
  const logoUrl: string | null = [
    (brand as any)?.logoUrl,
    (brand as any)?.adgenLogoUrl,
  ].find((candidate) => typeof candidate === "string" && candidate.trim())?.trim() ?? null;
  const studioOfferText: string | null = [
    (sale as any)?.customerFacingCopy,
    (sale as any)?.promotionSummary,
    (sale as any)?.name,
    (rootProduct as any)?.name,
    (products[0] as any)?.name,
  ].find((candidate) => typeof candidate === "string" && candidate.trim())?.trim() ?? null;
  const groundedProductDetails = groundedStudioProductDetails(
    rootProduct ?? products[0] ?? null,
    offer,
  );
  const isPapabearLabel = (generatedContent as any)?.templateType === "papabear-label";
  const bundleProductPhotos: string[] = isPapabearLabel
    ? resolvePapabearLabelProductImages(rootProduct, products)
    : getProductImages(products[0] ?? null);
  const bundleImage: string | null = bundleProductPhotos[0] ?? null;

  // Shared render context. Each section block's renderer reads from this plus
  // the live `generatedContent` (the section's `fields` are an editor-facing
  // view of the same content, re-derived at read time — see
  // shared/lp-express-sections.ts).
  const ctx = {
    brand, rootProduct, products, sale, offer, generatedContent, typography, reviewDisplay,
    logoUrl, bundleImage, bundleProductPhotos, reviewsByProduct, receipt, videosLoading,
    onRetrySlot, onRetryHeroSlot, onRetryHero, onRetryProductContext,
  };

  // Discrete, ordered, typed sections (preferring a persisted order, falling
  // back to the canonical derivation). The renderer dispatches each block to
  // its existing rich per-section component — visual output is unchanged from
  // the previous hardcoded sequence, but order/membership now live in data.
  const sections = getLpSections(generatedContent);
  // Template version flag. "long-form-v2" is the globally-available,
  // brand-adaptable variant; every section branches on this (in v2 only) to
  // swap hardcoded Papabear fallbacks for neutral, brand/data-driven ones.
  // Default (missing / "long-form") => v1, byte-identical to the legacy page.
  const isV2 = (generatedContent as any)?.templateType === "long-form-v2";
  // The Quiz Funnel is a self-contained interactive template that bypasses the
  // sections model entirely (its whole content lives at generatedContent.quiz).
  const isQuiz = (generatedContent as any)?.templateType === "quiz";
  // The Listicle is likewise a self-contained template whose whole content
  // lives at generatedContent.listicle (AI imagery at .listicleImages).
  const isListicle = (generatedContent as any)?.templateType === "listicle";
  // The Advertorial is a self-contained editorial-article template whose whole
  // content lives at generatedContent.advertorial (AI imagery at
  // .advertorialImages).
  const isAdvertorial = (generatedContent as any)?.templateType === "advertorial";
  // The Product Hero (PDP) is a self-contained flagship product page whose whole
  // copy lives at generatedContent.productHero. Its imagery is the brand's REAL
  // product photos (no AI image pool).
  const isProductHero = (generatedContent as any)?.templateType === "product-hero";
  const isStudioTemplate = isLpStudioDesignSnapshot(
    (generatedContent as any)?.studioDesign,
  );
  const isSmbHome = isStudioTemplate
    ? (generatedContent as any)?.studioDesign.rendererTemplateKey === "smb-home"
    : (generatedContent as any)?.templateType === "smb-home";
  const smbPending = isSmbHome && (!isStudioTemplate || !isLpSmbHomeContentUsable(assembleLpSmbHomeContent(generatedContent)));
  const smbDesignStatus = isSmbHome ? (generatedContent as any)?.studioBrandDesign?.status : undefined;
  const studioDesignFailed =
    (isProductHero || isSmbHome) && ((generatedContent as any)?.studioBrandDesignStatus === "failed" || smbDesignStatus === "failed");
  const studioDesignRecoveryInProgress =
    (isProductHero || isSmbHome) && (studioDesignRecoveryPending === true || smbDesignStatus === "pending");
  // Product-hero flows through the SAME ordered sections pipeline as long-form
  // (getLpSections returns the ph* sections for this template). Until its
  // per-section copy exists, we short-circuit sections.map and show the shared
  // pending placeholder instead (loader, or error+retry in the builder).
  const productHeroPending =
    isProductHero && !hasUsableLpProductHeroSections(generatedContent);
  // The Problem / Solution (PAS) template also flows through the ordered
  // sections pipeline (ps* sections); until its per-section copy exists we
  // short-circuit sections.map and show the shared pending placeholder.
  const isProblemSolution = (generatedContent as any)?.templateType === "problem-solution";
  const problemSolutionPending =
    isProblemSolution && !hasUsableLpProblemSolutionSections(generatedContent);

  // The Cold Drip Offer (Gaffle) is an imported reference template whose whole
  // content lives at generatedContent.coldDrip. Generation is synchronous (the
  // default blob persists before the run completes), so no loader path exists —
  // but keep a pending fallback for the brief hydration window.
  const isColdDrip = (generatedContent as any)?.templateType === "cold-drip";
  // Coffee Shop Lead Capture (Gaffle): imported reference template whose whole
  // content lives at generatedContent.coffeeShop. Same sync-default generation
  // model as cold-drip (loader only for the hydration/async-Sim window).
  const isCoffeeShop = (generatedContent as any)?.templateType === "coffee-shop";

  // The Offer Stack (value-stack close) template also flows through the
  // ordered sections pipeline (os* sections); until its per-section copy
  // exists we short-circuit sections.map and show the shared pending
  // placeholder.
  const isOfferStack = (generatedContent as any)?.templateType === "offer-stack";
  const offerStackPending =
    isOfferStack && !hasUsableLpOfferStackSections(generatedContent);

  // Temecula Passport (Mystery Drop, Gaffle): imported reference template whose
  // whole content lives at generatedContent.temeculaPassport. Same sync-default
  // generation model (loader only for the hydration/async-Sim window).
  const isTemeculaPassport = (generatedContent as any)?.templateType === "temecula-passport";
  const theme = ((generatedContent as any)?.theme ?? null) as LpTheme | null;
  const stylesMap = ((generatedContent as any)?.styles ?? null) as Record<string, any> | null;
  // The brand's saved fonts — offered in the per-block Font picker AND used to
  // resolve "brand:<id>" fontFamily overrides to a concrete CSS stack.
  const brandFonts = (((brand as any)?.brandDesignSystem?.fonts) ?? []) as BrandFont[];
  const studioCssVariables = Object.fromEntries(
    Object.entries({
      ...brandCssVarsV2(brand, theme),
      ...brandFontCssVars(brand),
      ...lpThemeCssVars(theme, brandFonts),
    }).filter(
      ([name, value]) => name.startsWith("--") && typeof value === "string",
    ),
  ) as Record<string, string>;
  const studioFontCss = brandFontFaceCss(
    brand,
    stylesMap as Record<string, LpElementStyle> | null,
    brandFonts,
    theme,
  );

  // Visible, linkable sections offered by the link-URL popover's "jump to a
  // section" picker. The announcement bar and sticky nav aren't meaningful
  // scroll targets, and hidden sections won't exist on the published page.
  const linkTargets: LpLinkTarget[] = sections
    .filter(
      (s) =>
        s.type !== "announcement" &&
        s.type !== "stickyNav" &&
        !s.hidden,
    )
    .map((s) => ({
      anchorId: lpSectionAnchorId(s.type),
      label: LP_SECTION_REGISTRY[s.type]?.label ?? s.type,
    }));

  // Same-page #anchor links smooth-scroll (with a sticky-nav offset via each
  // section's scroll-margin-top). Delegated on the page root so every anchor —
  // footer, proof, hero "reviews", etc. — is covered without per-link wiring.
  // Only active on the read-only/published surface; in the editor, clicking a
  // link opens its edit popover instead. Uses the anchor's ownerDocument so it
  // works inside the mobile-preview iframe too.
  const handleAnchorNav = (e: React.MouseEvent) => {
    const a = (e.target as HTMLElement | null)?.closest?.(
      'a[href^="#"]',
    ) as HTMLAnchorElement | null;
    if (!a) return;
    const id = (a.getAttribute("href") || "").slice(1);
    if (!id) return;
    const el = a.ownerDocument.getElementById(id);
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <LpStatusDotsContext.Provider value={showStatusDots}>
    <LpTemplateVersionProvider value={isV2}>
    <LpEditorProvider value={editor}>
    <LpPageSectionsProvider value={linkTargets}>
    <LpThemeProvider value={theme}>
    <LpComponentOrderProvider value={(generatedContent as any)?.componentOrder ?? null}>
    <LpElementStyleProvider value={stylesMap}>
    <LpBrandFontsProvider value={brandFonts}>
    <LpMediaOverrideProvider value={(generatedContent as any)?.mediaOverrides ?? null}>
    <LpReviewOverrideProvider value={(generatedContent as any)?.reviewOverrides ?? null}>
    <LpElementStateProvider value={(generatedContent as any)?.elements ?? null}>
    <div
      className={`${lpThemeRootClass(theme)} ${typography.body} text-[var(--color-brand-text)]`}
      style={{ ...((isV2 || isListicle || isAdvertorial || isProductHero || isProblemSolution || isColdDrip || isCoffeeShop || isOfferStack || isTemeculaPassport || isPapabearLabel) ? brandCssVarsV2(brand, theme) : brandCssVars(brand)), ...brandFontCssVars(brand), ...lpThemeCssVars(theme, brandFonts), backgroundColor: "#FAFAF7" }}
      onClick={editor ? undefined : handleAnchorNav}
    >
      <style dangerouslySetInnerHTML={{ __html: LP_WYSIWYG_STYLE }} />
      {(() => {
        const fontCss = brandFontFaceCss(brand, stylesMap as Record<string, LpElementStyle> | null, brandFonts, theme);
        return fontCss ? <style dangerouslySetInnerHTML={{ __html: fontCss }} /> : null;
      })()}
      {studioDesignFailed || studioDesignRecoveryInProgress ? (
        <QuizPendingState
          mode={studioDesignFailed ? "error" : "loading"}
          message={
            studioDesignFailed
              ? "Your page design could not be completed. You can recover the existing design without starting a new generation."
              : undefined
          }
          onRetry={studioDesignFailed && !studioDesignRecoveryAvailable ? onRetryHero : undefined}
          onRecover={
            studioDesignFailed && studioDesignRecoveryAvailable
              ? onRecoverStudioDesign
              : undefined
          }
          recoverPending={studioDesignRecoveryPending}
          loadingTitle="Recovering your page design…"
          loadingSub="Checking the existing design result. No new generation will be started."
          errorTitle="We couldn't finish your page design"
          testPrefix="product-hero"
        />
      ) : smbPending ? (
        <QuizPendingState mode={(generatedContent as any)?.heroError ? "error" : "loading"} testPrefix="smb-home" />
      ) : isStudioTemplate && !productHeroPending ? (
        <LpExpressStudioTemplate
          generatedContent={(generatedContent ?? {}) as Record<string, any>}
          productPhotos={bundleProductPhotos}
          logoUrl={logoUrl}
          brandName={brand?.name ?? null}
          offerText={studioOfferText}
          groundedProductDetails={groundedProductDetails}
          ctaUrl={isSmbHome ? (generatedContent as any)?.ctaUrl : offer.checkoutUrl}
          editor={editor}
          sections={sections}
          onRetryImage={
            onRetrySlot
              ? (slotKey) => onRetrySlot("productHeroImages", slotKey)
              : undefined
          }
          reviews={reviewDisplay.allReviews}
          presentation={{
            cssVariables: studioCssVariables,
            rootClassName: lpThemeRootClass(theme),
            fontCss: studioFontCss,
            bridgeCss: LP_WYSIWYG_STYLE,
          }}
        />
      ) : (
      <>
      {isQuiz && (() => {
        const quizUsable = isLpQuizContentUsable((generatedContent as any)?.quiz);
        // Usable quiz content → render the interactive / editable funnel.
        if (quizUsable) {
          // Aggregate ingredient rows across the (bundle-expanded) products,
          // deduped by name, so the result page's "Real ingredients" grid draws
          // from the same product data the long-form template uses.
          const quizIngredientRows = (() => {
            const seen = new Set<string>();
            const out: { name: string; benefit?: string }[] = [];
            for (const p of products ?? []) {
              for (const row of getIngredientRows(p)) {
                const k = row.name.toLowerCase();
                if (seen.has(k)) continue;
                seen.add(k);
                out.push(row);
              }
            }
            return out;
          })();
          return (
            <LpExpressQuiz
              ctx={{
                brand, offer, generatedContent, logoUrl, bundleProductPhotos, typography,
                bundleImage, products, receipt, reviewDisplay,
                ingredientRows: quizIngredientRows,
              }}
            />
          );
        }
        // No usable quiz yet. If generation explicitly FAILED, surface the error
        // in-canvas with a retry (builder only; the public page has no retry).
        if ((generatedContent as any)?.heroError) {
          return (
            <QuizPendingState
              mode="error"
              message={(generatedContent as any)?.heroError}
              onRetry={onRetryHero}
            />
          );
        }
        // Otherwise we're still generating (or content hasn't hydrated yet):
        // ALWAYS show the loader — never a blank screen or the normalized
        // placeholder quiz. Defaulting to the loader (rather than keying off a
        // client `loading.quiz` flag) keeps it robust across the brief POST
        // window and any state reset during the ~1–3 min generation.
        return <QuizPendingState mode="loading" />;
      })()}
      {isListicle && (() => {
        const listicleUsable = isLpListicleContentUsable((generatedContent as any)?.listicle);
        // Usable listicle content → render the long-scroll persuasion page.
        if (listicleUsable) {
          // Real product cards (photo + name + price) for the derived
          // productGrid visual. Prices come from the receipt's line items
          // (matched by name) so copy never carries dollar amounts.
          const productCards: ListicleProductCard[] = (products ?? []).map((p) => {
            const line = receipt.lineItems.find(
              (li) => li.name.toLowerCase() === (p.name ?? "").toLowerCase(),
            );
            return {
              name: p.name ?? "",
              priceLabel: line?.priceLabel ?? null,
              imageUrl: getProductImages(p)[0] ?? null,
            };
          });
          return (
            <LpExpressListicle
              ctx={{
                brand, offer, generatedContent, logoUrl, bundleProductPhotos, typography,
                bundleImage, products, productCards, receipt, reviewDisplay, onRetrySlot,
              }}
            />
          );
        }
        // Generation explicitly FAILED → in-canvas error with retry (builder
        // only; the public page has no retry affordance).
        if ((generatedContent as any)?.heroError) {
          return (
            <QuizPendingState
              mode="error"
              message={(generatedContent as any)?.heroError}
              onRetry={onRetryHero}
              errorTitle="We couldn't generate your listicle"
              testPrefix="listicle"
            />
          );
        }
        // Still generating (or not hydrated yet) → always the loader, never a
        // blank screen or the normalized placeholder listicle.
        return (
          <QuizPendingState
            mode="loading"
            loadingTitle="Building your listicle page…"
            loadingSub="Writing the reasons, offer framing, and supporting sections. This usually takes a minute or two."
            testPrefix="listicle"
          />
        );
      })()}
      {isAdvertorial && (() => {
        const advertorialUsable = isLpAdvertorialContentUsable((generatedContent as any)?.advertorial);
        // Usable advertorial content → render the long-form editorial article.
        if (advertorialUsable) {
          return (
            <LpExpressAdvertorial
              ctx={{
                brand, offer, generatedContent, logoUrl, bundleProductPhotos, typography,
                bundleImage, products, receipt, reviewDisplay, onRetrySlot,
              }}
            />
          );
        }
        // Generation explicitly FAILED → in-canvas error with retry (builder
        // only; the public page has no retry affordance).
        if ((generatedContent as any)?.heroError) {
          return (
            <QuizPendingState
              mode="error"
              message={(generatedContent as any)?.heroError}
              onRetry={onRetryHero}
              errorTitle="We couldn't generate your advertorial"
              testPrefix="advertorial"
            />
          );
        }
        // Still generating (or not hydrated yet) → always the loader, never a
        // blank screen or the normalized placeholder advertorial.
        return (
          <QuizPendingState
            mode="loading"
            loadingTitle="Building your advertorial…"
            loadingSub="Writing the editorial story, offer framing, and supporting sections. This usually takes a minute or two."
            testPrefix="advertorial"
          />
        );
      })()}
      {isColdDrip && (() => {
        const coldDripUsable = isLpColdDripContentUsable(
          normalizeLpColdDripContent((generatedContent as any)?.coldDrip),
        ) && !!(generatedContent as any)?.coldDrip;
        // Usable cold-drip content → render the imported Gaffle offer page.
        if (coldDripUsable) {
          return (
            <LpExpressColdDrip
              ctx={{
                brand, offer, generatedContent, logoUrl, bundleProductPhotos, typography,
                bundleImage, products, receipt, reviewDisplay, onRetrySlot,
                // First photo of EACH page product (bundle-expanded), for the
                // per-product duo cards; falls back to bundleProductPhotos.
                perProductPhotos: products.map((p) => getProductImages(p)[0] ?? null),
              }}
            />
          );
        }
        // With SIM_LP_COLD_DRIP_URL set the copy is written by an AI workflow
        // in the background (like the quiz/listicle/advertorial), so this
        // loading state can persist for a minute or two; without the workflow
        // the route persists the default blob synchronously and this only
        // flashes during the POST/hydration window.
        if ((generatedContent as any)?.heroError) {
          return (
            <QuizPendingState
              mode="error"
              message={(generatedContent as any)?.heroError}
              onRetry={onRetryHero}
              errorTitle="We couldn't create your offer page"
              testPrefix="cold-drip"
            />
          );
        }
        return (
          <QuizPendingState
            mode="loading"
            loadingTitle="Writing your offer page…"
            loadingSub="Copy is being tailored to your brand, persona, and product. This usually takes a minute or two."
            testPrefix="cold-drip"
          />
        );
      })()}
      {isCoffeeShop && (() => {
        const coffeeShopUsable = isLpCoffeeShopContentUsable(
          normalizeLpCoffeeShopContent((generatedContent as any)?.coffeeShop),
        ) && !!(generatedContent as any)?.coffeeShop;
        // Usable coffee-shop content → render the imported Gaffle lead-capture page.
        if (coffeeShopUsable) {
          return (
            <LpExpressCoffeeShop
              ctx={{
                brand, generatedContent, logoUrl, typography, onRetrySlot,
                publicLeadContext,
              }}
            />
          );
        }
        // With SIM_LP_COFFEE_SHOP_URL set the copy is written by an AI workflow
        // in the background; without it the route persists the default blob
        // synchronously and this only flashes during the POST/hydration window.
        if ((generatedContent as any)?.heroError) {
          return (
            <QuizPendingState
              mode="error"
              message={(generatedContent as any)?.heroError}
              onRetry={onRetryHero}
              errorTitle="We couldn't create your lead-capture page"
              testPrefix="coffee-shop"
            />
          );
        }
        return (
          <QuizPendingState
            mode="loading"
            loadingTitle="Writing your lead-capture page…"
            loadingSub="Copy is being tailored to your brand, persona, and product. This usually takes a minute or two."
            testPrefix="coffee-shop"
          />
        );
      })()}
      {isTemeculaPassport && (() => {
        const tpUsable = isLpTemeculaPassportContentUsable(
          normalizeLpTemeculaPassportContent((generatedContent as any)?.temeculaPassport),
        ) && !!(generatedContent as any)?.temeculaPassport;
        // Usable content → render the imported Gaffle mystery-drop page.
        if (tpUsable) {
          return (
            <LpExpressTemeculaPassport
              ctx={{
                brand, generatedContent, logoUrl, typography, onRetrySlot,
                publicLeadContext,
              }}
            />
          );
        }
        // Generation persists the verbatim default blob synchronously (no Sim
        // workflow yet), so this loader only flashes during the POST/hydration
        // window (or shows a real error from a future async workflow).
        if ((generatedContent as any)?.heroError) {
          return (
            <QuizPendingState
              mode="error"
              message={(generatedContent as any)?.heroError}
              onRetry={onRetryHero}
              errorTitle="We couldn't create your mystery-drop page"
              testPrefix="temecula-passport"
            />
          );
        }
        return (
          <QuizPendingState
            mode="loading"
            loadingTitle="Writing your mystery-drop page…"
            loadingSub="Copy is being tailored to your brand, persona, and product. This usually takes a minute or two."
            testPrefix="temecula-passport"
          />
        );
      })()}
      {isPapabearLabel && (() => {
        const papabearLabelUsable =
          !!(generatedContent as any)?.papabearLabel &&
          isLpPapabearLabelContentUsable(
            normalizeLpPapabearLabelContent(
              (generatedContent as any)?.papabearLabel,
            ),
          );
        if (papabearLabelUsable) {
          return (
            <LpExpressPapabearLabel
              ctx={{
                brand,
                offer,
                generatedContent,
                logoUrl,
                bundleProductPhotos,
                typography,
                bundleImage,
                rootProduct,
                products,
                receipt,
                reviewDisplay,
                onRetrySlot,
                onRetryHeroSlot,
              }}
            />
          );
        }
        if ((generatedContent as any)?.heroError) {
          return (
            <QuizPendingState
              mode="error"
              message={(generatedContent as any)?.heroError}
              onRetry={onRetryHero}
              errorTitle="We couldn't create your Papabear page"
              testPrefix="papabear-label"
            />
          );
        }
        return (
          <QuizPendingState
            mode="loading"
            loadingTitle="Preparing your Papabear page…"
            loadingSub="Loading the imported page copy and your live product details."
            testPrefix="papabear-label"
          />
        );
      })()}
      {productHeroPending && (() => {
        // No usable per-section product-hero copy yet. Generation explicitly
        // FAILED → in-canvas error with retry (builder only; the public page has
        // no retry affordance).
        if ((generatedContent as any)?.heroError) {
          return (
            <QuizPendingState
              mode="error"
              message={(generatedContent as any)?.heroError}
              onRetry={onRetryHero}
              errorTitle="We couldn't generate your product page"
              testPrefix="product-hero"
            />
          );
        }
        // Still generating (or not hydrated yet) → always the loader.
        if (isStudioTemplate) {
          return (
            <QuizPendingState
              mode="loading"
              loadingTitle="Writing your page copy…"
              loadingSub={`Copy: generating · Hero/product photo: ${bundleImage ? "ready" : "unavailable"} · Section images: waiting for copy`}
              testPrefix="studio"
            />
          );
        }
        return (
          <QuizPendingState
            mode="loading"
            loadingTitle="Building your product page…"
            loadingSub="Writing the buy-box copy, benefits, how-it-works, reviews and FAQ. This usually takes a minute or two."
            testPrefix="product-hero"
          />
        );
      })()}
      {problemSolutionPending && (() => {
        // No usable per-section problem-solution copy yet. Generation
        // explicitly FAILED → in-canvas error with retry (builder only; the
        // public page has no retry affordance).
        if ((generatedContent as any)?.heroError) {
          return (
            <QuizPendingState
              mode="error"
              message={(generatedContent as any)?.heroError}
              onRetry={onRetryHero}
              errorTitle="We couldn't generate your sales page"
              testPrefix="problem-solution"
            />
          );
        }
        // Still generating (or not hydrated yet) → always the loader.
        return (
          <QuizPendingState
            mode="loading"
            loadingTitle="Building your sales page…"
            loadingSub="Writing the problem story, mechanism, solution reveal, proof and FAQ. This usually takes a minute or two."
            testPrefix="problem-solution"
          />
        );
      })()}
      {offerStackPending && (() => {
        // No usable per-section offer-stack copy yet. Generation explicitly
        // FAILED → in-canvas error with retry (builder only; the public page
        // has no retry affordance).
        if ((generatedContent as any)?.heroError) {
          return (
            <QuizPendingState
              mode="error"
              message={(generatedContent as any)?.heroError}
              onRetry={onRetryHero}
              errorTitle="We couldn't generate your offer page"
              testPrefix="offer-stack"
            />
          );
        }
        // Still generating (or not hydrated yet) → always the loader.
        return (
          <QuizPendingState
            mode="loading"
            loadingTitle="Building your offer page…"
            loadingSub="Writing the hero, value stack, bonuses, reviews and FAQ. This usually takes a minute or two."
            testPrefix="offer-stack"
          />
        );
      })()}
      {!isQuiz && !isListicle && !isAdvertorial && !isColdDrip && !isCoffeeShop && !isTemeculaPassport && !isPapabearLabel && !productHeroPending && !problemSolutionPending && !offerStackPending && sections.map((section, idx) => {
        const render = LP_SECTION_RENDERERS[section.type];
        if (!render) return null;
        const secStyle = lpSectionStyle(stylesMap?.[`sec:${section.type}`]);
        // Read-only / published surface: hidden sections simply don't render.
        if (!editor) {
          if (section.hidden) return null;
          // The sticky nav must NOT be wrapped — its `sticky top-0` behavior
          // depends on being a direct child of the tall page root, and it's
          // never an in-page anchor target anyway. Render it bare (key on its
          // own root element, not a Fragment, to avoid the cartographer plugin's
          // data-replit-metadata-on-Fragment console warning).
          if (section.type === "stickyNav") {
            const rendered = render(ctx);
            return React.isValidElement(rendered)
              ? React.cloneElement(rendered as React.ReactElement, { key: section.id })
              : <React.Fragment key={section.id}>{rendered}</React.Fragment>;
          }
          // Every other visible section is wrapped in a div carrying its stable
          // anchor id + a sticky-nav scroll offset, so in-page #anchor links
          // land below the nav. The wrapper is the reliable place for the id
          // because most section renderers return React components (not host
          // elements), so an id prop wouldn't reach the DOM. Mirrors the
          // editor branch, which already wraps every section.
          return (
            <div
              key={section.id}
              id={lpSectionAnchorId(section.type)}
              style={{ scrollMarginTop: LP_SECTION_SCROLL_MARGIN, ...(secStyle ?? {}) }}
              data-testid={`section-anchor-${section.type}`}
            >
              {render(ctx)}
            </div>
          );
        }
        // In-canvas editor: wrap every section with a hover toolbar. Hidden
        // sections stay visible but dimmed (with a badge) so they can be
        // restored. `idx` is the section's position in the full ordered list, so
        // move-up/down boundary checks are correct.
        const label = LP_SECTION_REGISTRY[section.type]?.label ?? section.type;
        const isFirst = idx === 0;
        const isLast = idx === sections.length - 1;
        return (
          <div
            key={section.id}
            id={lpSectionAnchorId(section.type)}
            style={{ scrollMarginTop: LP_SECTION_SCROLL_MARGIN }}
            className="relative group/lpsec"
            data-testid={`section-wrap-${section.type}`}
          >
            <div className="pointer-events-none absolute inset-0 z-20 border-2 border-transparent transition-colors group-hover/lpsec:border-primary/40" />
            {/* Sticky chrome overlay: keeps the section toolbar (and hidden
                badge) visible while scrolling, so the topmost/thin sections
                (e.g. the announcement bar) don't have their toolbar clipped at
                the top of the scroll viewport. */}
            <div className="pointer-events-none absolute inset-0 z-50">
              <div className="sticky top-2 flex items-start gap-2 px-3 pt-1">
                {section.hidden && (
                  <div className="flex items-center gap-1 rounded-md bg-foreground/80 px-2 py-1 text-xs font-medium text-white shadow-sm">
                    <EyeOff className="h-3 w-3" />
                    Hidden — won't appear on the published page
                  </div>
                )}
                <div className="ml-auto flex items-center gap-0.5 rounded-lg border border-border bg-white/95 p-1 shadow-md opacity-0 transition-opacity pointer-events-none group-hover/lpsec:opacity-100 group-hover/lpsec:pointer-events-auto backdrop-blur-sm">
                  <span className="px-2 text-xs font-semibold text-foreground/80 max-w-[140px] truncate" data-testid={`section-label-${section.type}`}>{label}</span>
                  <LpSectionRegenerate type={section.type} />
                  <LpStylePopover elementKey={`sec:${section.type}`} variant="section" />
                  <button
                    type="button"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
                    onClick={() => editor.onMove(section.type, "up")}
                    disabled={isFirst || editor.busy}
                    title="Move up"
                    data-testid={`button-move-up-${section.type}`}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
                    onClick={() => editor.onMove(section.type, "down")}
                    disabled={isLast || editor.busy}
                    title="Move down"
                    data-testid={`button-move-down-${section.type}`}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:pointer-events-none"
                    onClick={() => editor.onToggleHidden(section.type)}
                    disabled={editor.busy}
                    title={section.hidden ? "Show on published page" : "Hide from published page"}
                    data-testid={`button-toggle-hidden-${section.type}`}
                  >
                    {section.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-30 disabled:pointer-events-none"
                    onClick={() => editor.onDelete(section.type)}
                    disabled={editor.busy}
                    title="Delete section"
                    data-testid={`button-delete-${section.type}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
            <div className={section.hidden ? "opacity-40 grayscale" : ""} style={secStyle}>
              {render(ctx)}
            </div>
          </div>
        );
      })}
      </>
      )}
    </div>
    </LpElementStateProvider>
    </LpReviewOverrideProvider>
    </LpMediaOverrideProvider>
    </LpBrandFontsProvider>
    </LpElementStyleProvider>
    </LpComponentOrderProvider>
    </LpThemeProvider>
    </LpPageSectionsProvider>
    </LpEditorProvider>
    </LpTemplateVersionProvider>
    </LpStatusDotsContext.Provider>
  );
}

// Shared context handed to every section renderer. Built once per render in
// LpExpressRenderer.
type LpRenderContext = {
  brand: Brand | null | undefined;
  products: Product[];
  sale: SalesDiscount | null | undefined;
  offer: ReturnType<typeof buildOffer>;
  generatedContent?: GeneratedContent;
  typography: ReturnType<typeof brandTypography>;
  reviewDisplay: LpReviewDisplay;
  logoUrl: string | null;
  bundleImage: string | null;
  bundleProductPhotos: string[];
  reviewsByProduct?: Record<string, ProductReview[]>;
  receipt: ReturnType<typeof computeReceipt>;
  videosLoading: boolean;
  onRetrySlot?: LpExpressRendererProps["onRetrySlot"];
  onRetryHeroSlot?: LpExpressRendererProps["onRetryHeroSlot"];
  onRetryHero?: LpExpressRendererProps["onRetryHero"];
  onRetryProductContext?: LpExpressRendererProps["onRetryProductContext"];
};

// Per-block renderers, keyed by section type. The dispatch order is driven by
// the ordered sections array, NOT by this map — these are the exact JSX blocks
// that were previously inlined in LpExpressRenderer's return, moved here 1:1 so
// the rendered output is byte-identical.
const LP_SECTION_RENDERERS: Record<
  LpSectionType,
  (ctx: LpRenderContext) => React.ReactNode
> = {
  // SMB only renders its frozen Studio document. Never dispatch into LP1.
  ...Object.fromEntries(LP_SMB_HOME_BLOCK_KEYS.map(key => [key, () => null])) as unknown as Record<typeof LP_SMB_HOME_BLOCK_KEYS[number], (ctx: LpRenderContext) => React.ReactNode>,
  // 1. Announcement bar (above-the-fold zone — SocialProofMarquee and
  //    BrandTaglineStrip intentionally excluded per template).
  announcement: (ctx) => <AnnouncementBar brand={ctx.brand} sale={ctx.sale} />,
  // 2. Sticky nav.
  stickyNav: (ctx) => <StickyNav brand={ctx.brand} typography={ctx.typography} />,
  // 3. Hero (buy box lives inside hero on the right).
  hero: (ctx) => (
    <HeroSection
      brand={ctx.brand}
      products={ctx.products}
      sale={ctx.sale}
      offer={ctx.offer}
      generatedContent={ctx.generatedContent}
      typography={ctx.typography}
      reviewDisplay={ctx.reviewDisplay}
      onRetryHeroSlot={ctx.onRetryHeroSlot}
      onRetryHero={ctx.onRetryHero}
    />
  ),
  // 3b. Product showcase — "THE RECIPE" header + per-product cards. The legacy
  //     IngredientsSection grid is intentionally omitted (the Papabear template
  //     folds the recipe header + ingredient facts into ProductShowcaseSection).
  productShowcase: (ctx) => (
    <ProductShowcaseSection
      brand={ctx.brand}
      products={ctx.products}
      offer={ctx.offer}
      generatedContent={ctx.generatedContent}
      typography={ctx.typography}
      reviewsByProduct={ctx.reviewsByProduct}
      sale={ctx.sale}
    />
  ),
  // 5. PSA warning — wired to generatedContent.advisory.
  advisory: (ctx) => (
    <PsaWarningSection generatedContent={ctx.generatedContent} typography={ctx.typography} />
  ),
  // 5b. Problem loop — how the category traps customers in a cycle (renders the
  //     Villain checklist + report card inside its dark-navy container).
  problemLoop: (ctx) => (
    <ProblemLoopSection generatedContent={ctx.generatedContent} typography={ctx.typography} />
  ),
  // 6. Belief section — static design continuation inside the same dark-navy
  //    band as the stations + villain block above.
  belief: (ctx) => (
    <section className="bg-[var(--color-brand-deepnavy)] py-12 md:py-16 px-4 sm:px-6">
      <div className="max-w-[520px] mx-auto">
        <BeliefSection
          receipt={ctx.receipt}
          bundleImage={ctx.bundleImage}
          bundleProductPhotos={ctx.bundleProductPhotos}
          brandName={ctx.brand?.name ?? null}
          declarationLoading={!!ctx.generatedContent?.loading?.quotes}
          productContext={ctx.generatedContent?.productContext}
          eyebrow={ctx.generatedContent?.villain?.belief_eyebrow}
          headline={ctx.generatedContent?.villain?.belief_headline}
          headingLoading={!!ctx.generatedContent?.loading?.villain}
          cardsLoading={!!ctx.generatedContent?.loading?.productContext}
          declarationQuote={ctx.generatedContent?.quotes?.declaration?.quote}
          declarationAttribution={ctx.generatedContent?.quotes?.declaration?.attribution}
          checkoutUrl={ctx.offer.checkoutUrl}
          onRetrySlot={ctx.onRetryProductContext}
        />
      </div>
    </section>
  ),
  // 7. Farmstand — cream-bg design section, wired to AI origin copy + the
  //    reframe/craft_statement pull-quotes (falls back to hardcoded).
  farmstand: (ctx) => (
    <FarmstandSection
      receipt={ctx.receipt}
      bundleImage={ctx.bundleImage}
      bundleProductPhotos={ctx.bundleProductPhotos}
      brandName={ctx.brand?.name ?? null}
      videoMakingUrl={(ctx.brand as any)?.videoMakingUrl}
      videoLifestyleUrl={(ctx.brand as any)?.videoLifestyleUrl}
      videoMakingOverride={(ctx.generatedContent as any)?.videoOverrides?.making}
      videoLifestyleOverride={(ctx.generatedContent as any)?.videoOverrides?.lifestyle}
      videosLoading={ctx.videosLoading}
      originCopy={ctx.generatedContent?.originCopy}
      originImages={ctx.generatedContent?.originImages}
      originImagesLoading={!!ctx.generatedContent?.loading?.originImages}
      quotes={ctx.generatedContent?.quotes}
      originLoading={!!ctx.generatedContent?.loading?.originCopy}
      quotesLoading={!!ctx.generatedContent?.loading?.quotes}
      checkoutUrl={ctx.offer.checkoutUrl}
      onRetrySlot={ctx.onRetrySlot}
    />
  ),
  // 8..17. Long-form page sub-sections (timeline → footer). Each is now an
  //   individually dispatchable/reorderable block. They share one wired prop
  //   set (buildRemainingProps) and select their single block via `section`, so
  //   the rendered output stays byte-identical to the legacy composite. The
  //   interstitial dividers (SpringSaleBanner / Marquee) travel with their
  //   anchor block inside RemainingPageSections.
  timeline: (ctx) => <RemainingPageSections section="timeline" {...buildRemainingProps(ctx)} />,
  membership: (ctx) => <RemainingPageSections section="membership" {...buildRemainingProps(ctx)} />,
  proof: (ctx) => <RemainingPageSections section="proof" {...buildRemainingProps(ctx)} />,
  alternatives: (ctx) => <RemainingPageSections section="alternatives" {...buildRemainingProps(ctx)} />,
  philosophy: (ctx) => <RemainingPageSections section="philosophy" {...buildRemainingProps(ctx)} />,
  differentiator: (ctx) => <RemainingPageSections section="differentiator" {...buildRemainingProps(ctx)} />,
  faq: (ctx) => <RemainingPageSections section="faq" {...buildRemainingProps(ctx)} />,
  reviews: (ctx) => <RemainingPageSections section="reviews" {...buildRemainingProps(ctx)} />,
  finalCta: (ctx) => <RemainingPageSections section="finalCta" {...buildRemainingProps(ctx)} />,
  footer: (ctx) => <RemainingPageSections section="footer" {...buildRemainingProps(ctx)} />,
  // Product Hero (PDP) sub-sections. Each renders ONE product-hero block from
  // the per-section generatedContent keys (assembled inside the component), so
  // the flagship PDP flows through the same ordered/reorderable pipeline. Its
  // imagery is the brand's REAL product photos (no AI image pool).
  phBuyBox: (ctx) => <LpExpressProductHero section="phBuyBox" ctx={buildProductHeroCtx(ctx)} />,
  phTrustBar: (ctx) => <LpExpressProductHero section="phTrustBar" ctx={buildProductHeroCtx(ctx)} />,
  phBenefits: (ctx) => <LpExpressProductHero section="phBenefits" ctx={buildProductHeroCtx(ctx)} />,
  phHowItWorks: (ctx) => <LpExpressProductHero section="phHowItWorks" ctx={buildProductHeroCtx(ctx)} />,
  phReviews: (ctx) => <LpExpressProductHero section="phReviews" ctx={buildProductHeroCtx(ctx)} />,
  phGuarantee: (ctx) => <LpExpressProductHero section="phGuarantee" ctx={buildProductHeroCtx(ctx)} />,
  phFaq: (ctx) => <LpExpressProductHero section="phFaq" ctx={buildProductHeroCtx(ctx)} />,
  phFinalCta: (ctx) => <LpExpressProductHero section="phFinalCta" ctx={buildProductHeroCtx(ctx)} />,
  phDisclaimer: (ctx) => <LpExpressProductHero section="phDisclaimer" ctx={buildProductHeroCtx(ctx)} />,
  phStickyCta: (ctx) => <LpExpressProductHero section="phStickyCta" ctx={buildProductHeroCtx(ctx)} />,
  psHero: (ctx) => <LpExpressProblemSolution section="psHero" ctx={buildProblemSolutionCtx(ctx)} />,
  psProblem: (ctx) => <LpExpressProblemSolution section="psProblem" ctx={buildProblemSolutionCtx(ctx)} />,
  psWhyFail: (ctx) => <LpExpressProblemSolution section="psWhyFail" ctx={buildProblemSolutionCtx(ctx)} />,
  psMechanism: (ctx) => <LpExpressProblemSolution section="psMechanism" ctx={buildProblemSolutionCtx(ctx)} />,
  psSolution: (ctx) => <LpExpressProblemSolution section="psSolution" ctx={buildProblemSolutionCtx(ctx)} />,
  psProof: (ctx) => <LpExpressProblemSolution section="psProof" ctx={buildProblemSolutionCtx(ctx)} />,
  psOffer: (ctx) => <LpExpressProblemSolution section="psOffer" ctx={buildProblemSolutionCtx(ctx)} />,
  psGuarantee: (ctx) => <LpExpressProblemSolution section="psGuarantee" ctx={buildProblemSolutionCtx(ctx)} />,
  psFaq: (ctx) => <LpExpressProblemSolution section="psFaq" ctx={buildProblemSolutionCtx(ctx)} />,
  psDisclaimer: (ctx) => <LpExpressProblemSolution section="psDisclaimer" ctx={buildProblemSolutionCtx(ctx)} />,
  psStickyCta: (ctx) => <LpExpressProblemSolution section="psStickyCta" ctx={buildProblemSolutionCtx(ctx)} />,
  // Offer Stack (value-stack close) template sections — one dispatch entry per
  // os* block, all through the LpExpressOfferStack component.
  osHero: (ctx) => <LpExpressOfferStack section="osHero" ctx={buildOfferStackCtx(ctx)} />,
  osIntro: (ctx) => <LpExpressOfferStack section="osIntro" ctx={buildOfferStackCtx(ctx)} />,
  osStack: (ctx) => <LpExpressOfferStack section="osStack" ctx={buildOfferStackCtx(ctx)} />,
  osBonuses: (ctx) => <LpExpressOfferStack section="osBonuses" ctx={buildOfferStackCtx(ctx)} />,
  osScarcity: (ctx) => <LpExpressOfferStack section="osScarcity" ctx={buildOfferStackCtx(ctx)} />,
  osReviews: (ctx) => <LpExpressOfferStack section="osReviews" ctx={buildOfferStackCtx(ctx)} />,
  osGuarantee: (ctx) => <LpExpressOfferStack section="osGuarantee" ctx={buildOfferStackCtx(ctx)} />,
  osFaq: (ctx) => <LpExpressOfferStack section="osFaq" ctx={buildOfferStackCtx(ctx)} />,
  osDisclaimer: (ctx) => <LpExpressOfferStack section="osDisclaimer" ctx={buildOfferStackCtx(ctx)} />,
  osStickyCta: (ctx) => <LpExpressOfferStack section="osStickyCta" ctx={buildOfferStackCtx(ctx)} />,
};

// Builds the ProductHeroCtx handed to each product-hero section block from the
// shared render context.
function buildProductHeroCtx(ctx: LpRenderContext) {
  return {
    brand: ctx.brand,
    offer: ctx.offer,
    generatedContent: ctx.generatedContent,
    logoUrl: ctx.logoUrl,
    bundleProductPhotos: ctx.bundleProductPhotos,
    bundleImage: ctx.bundleImage,
    products: ctx.products,
    receipt: ctx.receipt,
    reviewDisplay: ctx.reviewDisplay,
    typography: ctx.typography,
    // In-app builder only — retries a failed AI lifestyle gallery slot
    // (generatedContent.productHeroImages). Undefined on the public page.
    onRetrySlot: ctx.onRetrySlot,
  };
}

// Builds the ProblemSolutionCtx handed to each problem-solution section block
// from the shared render context.
function buildProblemSolutionCtx(ctx: LpRenderContext) {
  return {
    brand: ctx.brand,
    offer: ctx.offer,
    generatedContent: ctx.generatedContent,
    logoUrl: ctx.logoUrl,
    bundleProductPhotos: ctx.bundleProductPhotos,
    bundleImage: ctx.bundleImage,
    products: ctx.products,
    receipt: ctx.receipt,
    reviewDisplay: ctx.reviewDisplay,
    typography: ctx.typography,
    // In-app builder only — retries the AI mechanism-diagram slot
    // (generatedContent.problemSolutionImages). Undefined on the public page.
    onRetrySlot: ctx.onRetrySlot,
  };
}

// Builds the OfferStackCtx handed to each offer-stack section block from the
// shared render context.
function buildOfferStackCtx(ctx: LpRenderContext) {
  return {
    brand: ctx.brand,
    offer: ctx.offer,
    generatedContent: ctx.generatedContent,
    logoUrl: ctx.logoUrl,
    bundleProductPhotos: ctx.bundleProductPhotos,
    bundleImage: ctx.bundleImage,
    products: ctx.products,
    receipt: ctx.receipt,
    reviewDisplay: ctx.reviewDisplay,
    typography: ctx.typography,
    // In-app builder only — retries the AI intro-visual slot
    // (generatedContent.offerStackImages). Undefined on the public page.
    onRetrySlot: ctx.onRetrySlot,
  };
}

// The shared, fully-wired prop set for every long-form sub-section block. Built
// from the render context once per dispatched block; the block selects which
// single sub-section to render via its `section` prop. Mirrors exactly the
// props the legacy single "remaining" renderer passed.
function buildRemainingProps(ctx: LpRenderContext) {
  return {
    receipt: ctx.receipt,
    logoUrl: ctx.logoUrl,
    bundleImage: ctx.bundleImage,
    bundleProductPhotos: ctx.bundleProductPhotos,
    reviewDisplay: ctx.reviewDisplay,
    quotes: ctx.generatedContent?.quotes,
    quotesLoading: !!ctx.generatedContent?.loading?.quotes,
    timeline: ctx.generatedContent?.timeline,
    timelineLoading: !!ctx.generatedContent?.loading?.timeline,
    membership: ctx.generatedContent?.membership,
    membershipLoading: !!ctx.generatedContent?.loading?.membership,
    brandName: ctx.brand?.name ?? null,
    brandUrl: (ctx.brand as any)?.url ?? null,
    brandSummary: (ctx.brand as any)?.summary ?? null,
    socialProfiles: (ctx.brand as any)?.socialProfiles,
    alternatives: ctx.generatedContent?.alternatives,
    alternativesLoading: !!ctx.generatedContent?.loading?.alternatives,
    alternativesImages: ctx.generatedContent?.alternativesImages,
    alternativesImagesLoading: !!ctx.generatedContent?.loading?.alternativesImages,
    philosophy: ctx.generatedContent?.philosophy,
    philosophyLoading: !!ctx.generatedContent?.loading?.philosophy,
    philosophyImages: ctx.generatedContent?.philosophyImages,
    philosophyImagesLoading: !!ctx.generatedContent?.loading?.philosophyImages,
    differentiator: ctx.generatedContent?.differentiator,
    differentiatorLoading: !!ctx.generatedContent?.loading?.differentiator,
    proofImages: ctx.generatedContent?.proofImages,
    proofImagesLoading: !!ctx.generatedContent?.loading?.proofImages,
    proof: ctx.generatedContent?.proof,
    proofLoading: !!ctx.generatedContent?.loading?.proof,
    checkoutUrl: ctx.offer.checkoutUrl,
    faq: ctx.generatedContent?.faq,
    faqLoading: !!ctx.generatedContent?.loading?.faq,
    reviews: ctx.generatedContent?.reviews,
    reviewsLoading: !!ctx.generatedContent?.loading?.reviews,
    finalCta: ctx.generatedContent?.finalCta,
    finalCtaLoading: !!ctx.generatedContent?.loading?.finalCta,
    footer: (ctx.generatedContent as any)?.footer,
    proofPoints: (ctx.brand as any)?.proofHierarchy,
    onRetrySlot: ctx.onRetrySlot,
  };
}
